/**
 * AuraGene — Rare Disease & Precision Medicine Explorer
 * Core Application Engine v3.0
 *
 * v3.0 additions on top of v2.0:
 *   - Expert ↔ Patient mode toggle
 *   - Plain-language translator (privacy-first, rule-based, DE)
 *   - Body-system metaphor (SVG anatomy with highlighted organs)
 *   - Orphanet / NORD / GARD / ACHSE / HPO support resources
 *   - HPO symptom search via EBI OLS (+ offline fallback)
 *   - PDF dossier "for the doctor visit" (jsPDF, client-side)
 *   - Conversational assistant w/ intent detection
 *   - Voice in/out (Web Speech API, fully local)
 *   - Doctor-question template
 */

// ==========================================================================
// Static Knowledge: Featured Diseases
// (extended with patientStory, bodySystems, supportOrgs, plainVariants)
// ==========================================================================
const featuredDatabase = {
    "CFTR": {
        diseaseName: "Cystic Fibrosis",
        geneSymbol: "CFTR",
        proteinName: "Cystic fibrosis transmembrane conductance regulator",
        pdbId: "1cll",
        function: "Epithelial anion channel that plays an essential role in regulating systemic fluid flow and water balance across epithelia. Involved in active transport of chloride and bicarbonate ions.",
        subcellular: "Apical cell membrane, Multi-pass membrane protein",
        cellLocationCode: "membrane",
        orphaCode: "586",
        omim: "219700",
        compounds: [
            { name: "Ivacaftor (VX-770)", role: "CFTR Potentiator (enhances channel gating)", phase: 4 },
            { name: "Lumacaftor (VX-809)", role: "CFTR Corrector (assists protein folding)", phase: 4 },
            { name: "Tezacaftor (VX-661)", role: "CFTR Corrector (improves cellular trafficking)", phase: 4 }
        ],
        variants: [
            { id: "rs113993960", loc: "chr7:117559590", consequence: "In-frame deletion", codon: "ATC > -", clinical: "Pathogenic (F508del)", residue: 508 },
            { id: "rs121908745", loc: "chr7:117615632", consequence: "Missense variant", codon: "GGC > GAC", clinical: "Pathogenic (G551D)", residue: 551 },
            { id: "rs786201005", loc: "chr7:117627402", consequence: "Stop gained", codon: "CAG > TAG", clinical: "Pathogenic (Q493X)", residue: 493 }
        ],
        trials: [
            { id: "NCT05213676", title: "Efficacy and Safety of Lumacaftor/Ivacaftor in Young Patients With Cystic Fibrosis", status: "RECRUITING", phase: "Phase 3" },
            { id: "NCT04786483", title: "Study of CFTR Modulator Therapies in Rare Mutation Cystic Fibrosis Profiles", status: "ACTIVE, NOT RECRUITING", phase: "Phase 2" }
        ],
        patientStory: {
            de: {
                short: "Das CFTR-Gen ist der Bauplan für ein winziges Tor in den Zellwänden von Lunge, Darm und Schweißdrüsen. Dieses Tor reguliert, wie viel Salz und Wasser nach außen geht. Wenn der Bauplan fehlerhaft ist, wird der Schleim auf den Oberflächen zäh — wie Honig statt Wasser. Das macht Atmen, Verdauen und Schwitzen schwer.",
                metaphor: "Stelle dir die Zellwand wie eine Brücke vor. Die CFTR-Proteine sind Schleusentore in dieser Brücke. Bei Mukoviszidose sind viele Schleusen verklemmt — und der Verkehr (Salz + Wasser) staut sich.",
                symptoms: ["Schwer atmen / häufiger Husten", "Salzige Haut", "Verdauungs-Probleme", "Wiederkehrende Lungeninfekte"]
            },
            en: {
                short: "The CFTR gene is the blueprint for a tiny gate in the cell walls of the lungs, gut, and sweat glands. This gate controls how much salt and water passes through. When the blueprint is faulty, mucus on those surfaces becomes thick — like honey instead of water. That makes breathing, digesting and sweating hard.",
                metaphor: "Picture the cell wall as a bridge. CFTR proteins are sluice-gates in that bridge. In cystic fibrosis many of those sluices are stuck — and the traffic (salt + water) backs up.",
                symptoms: ["Difficulty breathing / frequent cough", "Salty-tasting skin", "Digestive problems", "Recurrent lung infections"]
            }
        },
        bodySystems: ["lungs", "intestine", "pancreas", "sweatGlands"],
        supportOrgs: [
            { name: "Mukoviszidose e.V. (DE)", url: "https://www.muko.info/" },
            { name: "Cystic Fibrosis Foundation (US)", url: "https://www.cff.org/" },
            { name: "ACHSE — Allianz Chron. Seltener Erkrank.", url: "https://www.achse-online.de/" }
        ]
    },
    "HTT": {
        diseaseName: "Huntington's Disease",
        geneSymbol: "HTT",
        proteinName: "Huntingtin",
        pdbId: "6ezv",
        function: "Essential protein that plays a key role in intracellular transport, vesicular trafficking, and synaptic transmission. Crucial for neurogenesis and survival of brain cells.",
        subcellular: "Cytoplasm, Nucleus, Associated with microtubule structures",
        cellLocationCode: "cytoplasm",
        orphaCode: "399",
        omim: "143100",
        compounds: [
            { name: "Deutetrabenazine", role: "VMAT2 Inhibitor (improves chorea symptoms)", phase: 4 },
            { name: "Tominersen", role: "Antisense Oligonucleotide (ASO targeting HTT mRNA)", phase: 2 },
            { name: "Pridopidine", role: "Sigma-1 Receptor Agonist (neuroprotective)", phase: 3 }
        ],
        variants: [
            { id: "rs121907998", loc: "chr4:3074876", consequence: "CAG Trinucleotide Repeat", codon: "(CAG)n > Repeat expansion", clinical: "Pathogenic (HD Expansion)", residue: 18 },
            { id: "rs362331", loc: "chr4:3125672", consequence: "Missense variant", codon: "CGC > TGC", clinical: "Likely Benign (SNPs on Mutant Allele)", residue: 50 }
        ],
        trials: [
            { id: "NCT05030792", title: "Safety and Tolerability Study of Tominersen in Huntington's Disease", status: "RECRUITING", phase: "Phase 2" },
            { id: "NCT04828187", title: "A Study to Evaluate the Neuroprotective Qualities of Pridopidine in Early Stage HD", status: "COMPLETED", phase: "Phase 3" }
        ],
        patientStory: {
            de: {
                short: "Das HTT-Gen ist der Bauplan für ein wichtiges Transport-Protein in Nervenzellen. Bei Chorea Huntington wird ein bestimmter DNA-Abschnitt zu oft wiederholt — wie eine Schallplatte, die hängenbleibt. Dadurch wird das Protein klebrig und schädigt nach und nach die Nervenzellen im Gehirn.",
                metaphor: "Stelle dir das Gehirn als Stadt vor mit Bus-Linien (Nervenzellen). HTT ist der Fahrplan-Controller. Bei Huntington werden die Fahrpläne unleserlich wiederholt — die Busse fahren chaotisch und einige Linien werden aufgegeben.",
                symptoms: ["Unwillkürliche Bewegungen (Chorea)", "Stimmungs-Schwankungen", "Gedächtnis- und Konzentrationsprobleme", "Schwierigkeiten beim Schlucken"]
            },
            en: {
                short: "The HTT gene is the blueprint for an essential transport protein in nerve cells. In Huntington's disease, a particular stretch of DNA is repeated too many times — like a record that gets stuck. The resulting protein becomes sticky and slowly damages brain nerve cells.",
                metaphor: "Picture the brain as a city with bus lines (nerve cells). HTT is the schedule controller. In Huntington's the schedules read as garbled repeats — buses run chaotically and some lines are abandoned.",
                symptoms: ["Involuntary movements (chorea)", "Mood swings", "Memory and concentration problems", "Difficulty swallowing"]
            }
        },
        bodySystems: ["brain"],
        supportOrgs: [
            { name: "Deutsche Huntington-Hilfe", url: "https://huntington-hilfe.de/" },
            { name: "Huntington's Disease Society of America", url: "https://hdsa.org/" },
            { name: "ACHSE — Allianz Chron. Seltener Erkrank.", url: "https://www.achse-online.de/" }
        ]
    },
    "GBA": {
        diseaseName: "Gaucher Disease",
        geneSymbol: "GBA",
        proteinName: "Glucosylceramidase / Beta-glucosidase",
        pdbId: "2nty",
        function: "Lysosomal enzyme that catalyzes the breakdown of glucosylceramide into glucose and ceramide. Deficiency leads to accumulation of lipids in spleen, liver, and bones.",
        subcellular: "Lysosome membrane, Single-pass membrane protein",
        cellLocationCode: "lysosome",
        orphaCode: "355",
        omim: "230800",
        compounds: [
            { name: "Imiglucerase", role: "Recombinant Glucocerebrosidase (enzyme replacement)", phase: 4 },
            { name: "Miglustat", role: "Substrate Reduction Therapy (inhibits lipid synthesis)", phase: 4 },
            { name: "Venglustat", role: "Oral Glucosylceramide Synthase Inhibitor", phase: 3 }
        ],
        variants: [
            { id: "rs76763724", loc: "chr1:155239922", consequence: "Missense variant", codon: "AAC > AGC", clinical: "Pathogenic (N370S)", residue: 370 },
            { id: "rs80356768", loc: "chr1:155235843", consequence: "Missense variant", codon: "CTG > CCG", clinical: "Pathogenic (L444P)", residue: 444 }
        ],
        trials: [
            { id: "NCT04147494", title: "Long-term Safety Study of Venglustat in Gaucher Disease Type 3", status: "RECRUITING", phase: "Phase 3" },
            { id: "NCT05284381", title: "Enzyme Replacement Therapy Dosage Optimization Trial in Adult Patients", status: "ACTIVE, NOT RECRUITING", phase: "Phase 4" }
        ],
        patientStory: {
            de: {
                short: "Das GBA-Gen ist der Bauplan für ein Enzym, das in den 'Recycling-Anlagen' der Zellen (Lysosomen) arbeitet. Es zerlegt einen bestimmten Fettstoff. Wenn es nicht richtig funktioniert, sammelt sich dieser Fettstoff in Milz, Leber und Knochen an — wie wenn der Müll nicht abgeholt wird und sich türmt.",
                metaphor: "Stelle dir die Zellen wie eine Stadt vor. Das GBA-Enzym ist die Müllabfuhr für eine bestimmte Sorte Abfall. Wenn die Müllabfuhr ausfällt, türmt sich der Müll in Milz und Leber — diese werden groß und schmerzen.",
                symptoms: ["Vergrößerte Milz / Leber", "Anämie / Blutarmut", "Knochenschmerzen", "Müdigkeit"]
            },
            en: {
                short: "The GBA gene is the blueprint for an enzyme that works in the cell's 'recycling plant' (lysosome). It breaks down a specific fatty molecule. When it doesn't work, that fat accumulates in spleen, liver, and bones — like garbage that doesn't get picked up and piles up.",
                metaphor: "Picture cells as a city. The GBA enzyme is the garbage collector for a specific kind of waste. When pickup fails, the rubbish piles up in spleen and liver — they swell and ache.",
                symptoms: ["Enlarged spleen / liver", "Anemia", "Bone pain", "Fatigue"]
            }
        },
        bodySystems: ["liver", "spleen", "bones"],
        supportOrgs: [
            { name: "Gaucher Gesellschaft Deutschland e.V.", url: "https://www.ggd-ev.de/" },
            { name: "National Gaucher Foundation (US)", url: "https://www.gaucherdisease.org/" },
            { name: "ACHSE — Allianz Chron. Seltener Erkrank.", url: "https://www.achse-online.de/" }
        ]
    },
    "HBB": {
        diseaseName: "Sickle Cell Anemia",
        geneSymbol: "HBB",
        proteinName: "Hemoglobin subunit beta",
        pdbId: "2hhb",
        function: "Involved in oxygen transport from the lungs to peripheral tissues. Pathogenic mutation causing polymerization under low oxygen conditions.",
        subcellular: "Cytoplasm, Erythrocyte cytoplasm",
        cellLocationCode: "cytoplasm",
        orphaCode: "232",
        omim: "603903",
        compounds: [
            { name: "Hydroxyurea", role: "Fetal Hemoglobin Inducer (prevents polymerization)", phase: 4 },
            { name: "Voxelotor", role: "Hemoglobin Oxygen Affinity Modulator", phase: 4 },
            { name: "Crizanlizumab", role: "Anti-P-selectin Monoclonal Antibody", phase: 4 }
        ],
        variants: [
            { id: "rs334", loc: "chr11:5227002", consequence: "Missense variant", codon: "GAG > GTG", clinical: "Pathogenic (HbS / Sickle Cell)", residue: 6 },
            { id: "rs33930165", loc: "chr11:5227018", consequence: "Missense variant", codon: "GAG > AAG", clinical: "Pathogenic (HbC)", residue: 6 }
        ],
        trials: [
            { id: "NCT03811561", title: "Efficacy Study of Voxelotor in Pediatric Patients with Sickle Cell Disease", status: "COMPLETED", phase: "Phase 3" },
            { id: "NCT04845425", title: "Gene Editing (CRISPR-Cas9) Therapeutics for Sickle Cell Disease", status: "RECRUITING", phase: "Phase 1/2" }
        ],
        patientStory: {
            de: {
                short: "Das HBB-Gen ist der Bauplan für einen Teil des Hämoglobins — das Eiweiß, mit dem rote Blutzellen Sauerstoff transportieren. Bei der Sichelzell-Mutation ist ein einziger Baustein vertauscht. Bei Sauerstoffmangel klebt das Hämoglobin zusammen und verformt die Blutzellen zu starren Sicheln, die in feinen Adern stecken bleiben.",
                metaphor: "Rote Blutzellen sind normalerweise wie weiche Donuts, die durch enge Gänge flutschen. Bei Sichelzelle werden sie zu spitzen, harten Sicheln — wie Klebezettel, die in den Türrahmen kleben und Stau verursachen.",
                symptoms: ["Schmerzkrisen (Vaso-Okklusion)", "Müdigkeit, Blässe (Anämie)", "Häufige Infekte", "Gelbsucht der Augen"]
            },
            en: {
                short: "The HBB gene is the blueprint for a piece of hemoglobin — the protein red blood cells use to carry oxygen. In the sickle-cell mutation a single building block is swapped. When oxygen runs low the hemoglobin sticks together and warps the cells into rigid sickles that get stuck in fine vessels.",
                metaphor: "Red blood cells are normally soft donuts that squeeze through narrow corridors. In sickle cell they become hard pointed crescents — like sticky-notes that jam in a doorframe and cause a traffic jam.",
                symptoms: ["Pain crises (vaso-occlusion)", "Fatigue, pallor (anemia)", "Frequent infections", "Yellowing of the eyes (jaundice)"]
            }
        },
        bodySystems: ["blood", "spleen", "bones"],
        supportOrgs: [
            { name: "IST e.V. (Sichelzellerkr. DE)", url: "https://www.sichelzellkrankheit.info/" },
            { name: "Sickle Cell Disease Association (US)", url: "https://www.sicklecelldisease.org/" },
            { name: "ACHSE — Allianz Chron. Seltener Erkrank.", url: "https://www.achse-online.de/" }
        ]
    },
    "APP": {
        diseaseName: "Alzheimer's Disease (FAD)",
        geneSymbol: "APP",
        proteinName: "Amyloid-beta precursor protein",
        pdbId: "1iap",
        function: "Functions as a cell surface receptor. Cleaved by secretases to form amyloid-beta peptides, which accumulate as plaques in Alzheimer's patients.",
        subcellular: "Cell membrane, Single-pass membrane protein",
        cellLocationCode: "membrane",
        orphaCode: "1020",
        omim: "104300",
        compounds: [
            { name: "Lecanemab", role: "Monoclonal Antibody (targets soluble amyloid-beta protofibrils)", phase: 4 },
            { name: "Donanemab", role: "Monoclonal Antibody (clears deposited amyloid plaques)", phase: 4 },
            { name: "Semagacestat", role: "Gamma-Secretase Inhibitor (blocks amyloid cleavage)", phase: 3 }
        ],
        variants: [
            { id: "rs63750577", loc: "chr21:26198642", consequence: "Missense variant", codon: "ATG > GTG", clinical: "Pathogenic (Swedish Mutation)", residue: 670 },
            { id: "rs63750847", loc: "chr21:26198692", consequence: "Missense variant", codon: "ATC > TTC", clinical: "Pathogenic (Indiana Mutation)", residue: 717 }
        ],
        trials: [
            { id: "NCT04693520", title: "A Study of Donanemab in Participants with Early Alzheimer's Disease", status: "RECRUITING", phase: "Phase 3" },
            { id: "NCT03828747", title: "Clinical Trial of Anti-Amyloid Treatment in Asymptomatic Alzheimer's (A4)", status: "COMPLETED", phase: "Phase 3" }
        ],
        patientStory: {
            de: {
                short: "Das APP-Gen ist der Bauplan für ein Eiweiß auf Nervenzell-Oberflächen. Wenn es falsch zerschnitten wird, entstehen klebrige Bruchstücke (Amyloid-Beta), die im Gehirn Klumpen bilden — wie kleine Klebezettel, die sich zwischen den Nervenzellen festsetzen und Signale stören.",
                metaphor: "Stelle dir Nervenzellen als Sender vor, die Signale über kurze Wege schicken. Bei Alzheimer sammeln sich klebrige Klumpen zwischen den Sendern an und blockieren die Signale — wie wenn ein Briefkasten zugeklebt wird.",
                symptoms: ["Gedächtnis-Verlust (besonders kürzlich)", "Orientierungs-Probleme", "Sprach-Schwierigkeiten", "Persönlichkeits-Veränderungen"]
            },
            en: {
                short: "The APP gene is the blueprint for a protein on nerve-cell surfaces. When it's cut incorrectly, sticky fragments (amyloid-beta) are released that form clumps in the brain — like small sticky-notes that wedge between nerve cells and disrupt their signals.",
                metaphor: "Picture nerve cells as small transmitters firing signals over short hops. In Alzheimer's, sticky clumps accumulate between the transmitters and block the messages — like a mailbox that has been glued shut.",
                symptoms: ["Memory loss (especially recent)", "Disorientation", "Language difficulties", "Personality changes"]
            }
        },
        bodySystems: ["brain"],
        supportOrgs: [
            { name: "Deutsche Alzheimer Gesellschaft", url: "https://www.deutsche-alzheimer.de/" },
            { name: "Alzheimer's Association (US)", url: "https://www.alz.org/" },
            { name: "ACHSE — Allianz Chron. Seltener Erkrank.", url: "https://www.achse-online.de/" }
        ]
    }
};

// ==========================================================================
// Plain-Language Medical Dictionary — bilingual DE+EN
// Privacy-first: lives entirely client-side, no API calls.
// ==========================================================================
const medicalDictionary = {
    // Variant types
    "missense variant": { de: "ein einzelner Bau-Buchstaben-Tausch — verändert einen Baustein des Proteins", en: "a single letter swap that changes one building block of the protein" },
    "missense": { de: "ein Baustein wurde getauscht", en: "a building block was swapped" },
    "in-frame deletion": { de: "ein kleiner Baustein fehlt, der Rest des Bauplans bleibt im Takt", en: "a small piece is missing, the rest of the blueprint stays in rhythm" },
    "stop gained": { de: "der Bauplan wird zu früh abgebrochen — das Protein wird zu kurz", en: "the blueprint is cut short too early — the protein ends up truncated" },
    "stop_gained": { de: "der Bauplan wird zu früh abgebrochen — das Protein wird zu kurz", en: "the blueprint is cut short too early — the protein ends up truncated" },
    "frameshift variant": { de: "ab dieser Stelle gerät der Bauplan aus dem Tritt — alles dahinter ist Unsinn", en: "from this point on the reading frame is off — everything downstream becomes nonsense" },
    "frameshift": { de: "ab dieser Stelle gerät der Bauplan aus dem Tritt", en: "the reading frame is shifted from this point on" },
    "splice region variant": { de: "ein Schnittpunkt im Bauplan ist gestört — das Protein wird falsch zusammengesetzt", en: "a splice point is disturbed — the protein is assembled incorrectly" },
    "splice_region_variant": { de: "ein Schnittpunkt im Bauplan ist gestört", en: "a splice point is disturbed" },
    "synonymous variant": { de: "ein 'stiller' Tausch — Protein bleibt unverändert", en: "a 'silent' swap — the protein stays unchanged" },
    "intron variant": { de: "Veränderung in einem Zwischen-Stück, das nicht zum Protein wird", en: "a change in a spacer piece that isn't part of the protein" },
    "5 prime utr variant": { de: "Veränderung vor dem eigentlichen Bauplan", en: "a change in the region just before the blueprint" },
    "3 prime utr variant": { de: "Veränderung nach dem eigentlichen Bauplan", en: "a change in the region just after the blueprint" },
    "cag trinucleotide repeat": { de: "drei Buchstaben (CAG) werden zu oft wiederholt — wie eine hängende Schallplatte", en: "three letters (CAG) are repeated too many times — like a stuck record" },
    "regulatory region variant": { de: "Veränderung in einem Steuer-Bereich (An/Aus-Schalter)", en: "a change in a control region (on/off switch)" },
    "non coding transcript exon variant": { de: "Veränderung in einem Teil, der gelesen aber nicht in Protein übersetzt wird", en: "a change in a region that is read but not translated to protein" },
    "intergenic variant": { de: "Veränderung zwischen Genen — Bedeutung oft unklar", en: "a change between genes — significance often unclear" },

    // Clinical significance
    "pathogenic": { de: "krankheits-verursachend", en: "disease-causing" },
    "likely pathogenic": { de: "sehr wahrscheinlich krankheits-verursachend", en: "very likely disease-causing" },
    "benign": { de: "harmlos", en: "harmless" },
    "likely benign": { de: "wahrscheinlich harmlos", en: "likely harmless" },
    "vus": { de: "Bedeutung noch unklar (Variant of Uncertain Significance)", en: "significance not yet clear (Variant of Uncertain Significance)" },
    "uncertain significance": { de: "Bedeutung noch unklar", en: "significance not yet clear" },

    // Cellular geography
    "transmembrane": { de: "durch die Zellwand verlaufend", en: "running across the cell wall" },
    "apical": { de: "auf der Außenseite der Zelle", en: "on the outward-facing side of the cell" },
    "epithelial": { de: "auf Oberflächen wie Lunge, Darm oder Haut", en: "on surface tissues like lung, gut, or skin" },
    "lysosome": { de: "die 'Recycling-Anlage' der Zelle", en: "the cell's 'recycling plant'" },
    "cytoplasm": { de: "das Innere der Zelle (außerhalb des Zellkerns)", en: "the inside of the cell (outside the nucleus)" },
    "nucleus": { de: "der Zellkern, wo die DNA liegt", en: "the cell's nucleus, where the DNA lives" },
    "membrane": { de: "Zellwand", en: "cell wall" },
    "microtubule": { de: "Stütz-Faser der Zelle", en: "the cell's scaffold fibers" },
    "synaptic": { de: "an den Verbindungs-Stellen zwischen Nervenzellen", en: "at the junctions between nerve cells" },
    "erythrocyte": { de: "rote Blutzelle", en: "red blood cell" },
    "vesicular trafficking": { de: "Transport kleiner Blasen innerhalb der Zelle", en: "transport of tiny bubbles inside the cell" },
    "multi-pass": { de: "mehrfach durch die Zellwand verlaufend", en: "passing through the cell wall multiple times" },
    "single-pass": { de: "einmal durch die Zellwand verlaufend", en: "passing through the cell wall once" },
    "subcellular location": { de: "wo in der Zelle das Protein arbeitet", en: "where in the cell the protein works" },

    // Functional vocabulary
    "ion channel": { de: "ein Tor in der Zellwand für geladene Teilchen", en: "a gate in the cell wall for charged particles" },
    "anion channel": { de: "ein Tor für negativ geladene Teilchen", en: "a gate for negatively charged particles" },
    "receptor": { de: "ein Empfänger / Andock-Stelle", en: "a receiver / docking site" },
    "enzyme": { de: "ein Eiweiß, das chemische Reaktionen ausführt", en: "a protein that carries out a chemical reaction" },
    "monoclonal antibody": { de: "speziell hergestellter Schutz-Eiweiß (wie eine zielsuchende Rakete)", en: "specially manufactured guard protein (like a homing missile)" },
    "antisense oligonucleotide": { de: "kleines genetisches Werkzeug, das einen Bauplan stilllegen kann", en: "a tiny genetic tool that can silence a blueprint" },
    "potentiator": { de: "ein Verstärker (öffnet/aktiviert besser)", en: "a booster (helps something open/activate better)" },
    "corrector": { de: "ein Falt-Helfer (hilft dem Protein, in die richtige Form zu kommen)", en: "a folding helper (helps the protein take its correct shape)" },
    "polymerization": { de: "Verklumpung (mehrere Eiweiße kleben zusammen)", en: "clumping (multiple proteins stick together)" },
    "amyloid": { de: "klebrige Eiweiß-Klümpchen", en: "sticky protein clumps" },
    "plaques": { de: "Klumpen-Ablagerungen", en: "clump-like deposits" },
    "chorea": { de: "unwillkürliche, zappelnde Bewegungen", en: "involuntary, jerky movements" },
    "vmat2": { de: "ein Speicher-System für Botenstoffe im Gehirn", en: "a storage system for brain messengers" },
    "secretase": { de: "eine Eiweiß-Schere", en: "a protein-cutting scissors" },
    "neurogenesis": { de: "Bildung neuer Nervenzellen", en: "growth of new nerve cells" },

    // Genetic toolkit
    "rsid": { de: "eine Datenbank-Nummer für eine bestimmte Genvariante", en: "a database number for a specific gene variant" },
    "rs id": { de: "eine Datenbank-Nummer für eine bestimmte Genvariante", en: "a database number for a specific gene variant" },
    "vcf": { de: "ein Standard-Format für DNA-Listen", en: "a standard format for DNA listings" },
    "chromosome": { de: "eines der 23 großen DNA-Stücke", en: "one of the 23 large DNA packages" },
    "allele": { de: "eine Variante eines Gens", en: "one variant of a gene" },
    "snp": { de: "ein einzelner DNA-Bau-Buchstabe an einer bestimmten Stelle", en: "a single DNA letter at a specific position" },
    "indel": { de: "ein eingefügter oder fehlender DNA-Bau-Buchstabe", en: "an inserted or missing DNA letter" },
    "exon": { de: "Teil des Bauplans, der im Protein landet", en: "a portion of the blueprint that ends up in the protein" },
    "intron": { de: "Zwischen-Stück, das herausgeschnitten wird", en: "a spacer piece that gets cut out" },
    "splicing": { de: "Zusammen-Schneiden der Bauplan-Stücke", en: "stitching the blueprint pieces together" },
    "promoter": { de: "An-Schalter eines Gens", en: "the on-switch of a gene" },
    "phenotype": { de: "wie sich eine Veranlagung im Körper zeigt", en: "how a genetic predisposition shows in the body" },
    "genotype": { de: "die genetische Ausstattung", en: "the genetic make-up" },

    // Trials & care
    "clinical trial": { de: "wissenschaftliche Studie an Patienten", en: "a scientific study in patients" },
    "recruiting": { de: "es werden noch Teilnehmer gesucht", en: "still recruiting participants" },
    "completed": { de: "Studie ist abgeschlossen", en: "the study is complete" },
    "phase 1": { de: "Phase 1 — erste Sicherheits-Tests am Menschen", en: "Phase 1 — first safety tests in humans" },
    "phase 2": { de: "Phase 2 — Test, ob die Therapie wirkt", en: "Phase 2 — testing whether the therapy works" },
    "phase 3": { de: "Phase 3 — großer Wirksamkeits-Test (vor Zulassung)", en: "Phase 3 — large efficacy trial (before approval)" },
    "phase 4": { de: "Phase 4 — Beobachtung nach Zulassung", en: "Phase 4 — monitoring after approval" },
    "approved": { de: "zugelassen (kann verschrieben werden)", en: "approved (may be prescribed)" },
    "intervention study": { de: "Studie, in der eine Therapie gegeben wird", en: "a study in which a therapy is administered" },
    "investigational": { de: "noch in der Forschung — nicht zugelassen", en: "still investigational — not yet approved" },
    "active, not recruiting": { de: "Studie läuft, nimmt aber keine neuen Teilnehmer auf", en: "the study is active but not enrolling new participants" }
};
function dictLookup(term) {
    if (!term) return null;
    const v = medicalDictionary[term.toLowerCase().trim()];
    if (!v) return null;
    if (typeof v === 'string') return v; // legacy
    return v[currentLang] || v.en || v.de;
}

// Symptom → likely gene fallback (used if HPO API blocked) — bilingual keys
const symptomFallback = {
    // German
    "husten": ["CFTR"], "atemnot": ["CFTR"], "salzig": ["CFTR"], "schleim": ["CFTR"],
    "lungeninfekt": ["CFTR"], "verdauung": ["CFTR", "GBA"],
    "milz": ["GBA", "HBB"], "leber": ["GBA"], "anämie": ["HBB", "GBA"],
    "schmerzkrise": ["HBB"], "sichelzelle": ["HBB"],
    "gedächtnis": ["APP", "HTT"], "vergesslich": ["APP"], "demenz": ["APP"], "alzheimer": ["APP"],
    "huntington": ["HTT"], "chorea": ["HTT"], "zappel": ["HTT"], "bewegung": ["HTT", "DMD", "SMN1"],
    "schlucken": ["HTT"], "mukoviszidose": ["CFTR"], "gaucher": ["GBA"], "knochenschmerz": ["GBA", "HBB"],
    "muskelschwäche": ["DMD", "SMN1", "GAA"], "muskeldystrophie": ["DMD"],
    "krampf": ["MECP2", "HEXA"], "blässe": ["HBB"], "blut": ["HBB", "GBA"],
    "kupfer": ["ATP7B"], "wilson": ["ATP7B"], "cholesterin": ["LDLR"],
    "tumor": ["BRCA1", "TP53", "VHL", "NF1"], "krebsrisiko": ["BRCA1", "TP53"],
    "kleinwuchs": ["FGFR3"], "achondroplasie": ["FGFR3"],
    // English
    "cough": ["CFTR"], "breathing": ["CFTR"], "salty skin": ["CFTR"], "mucus": ["CFTR"],
    "lung infection": ["CFTR"], "digestion": ["CFTR", "GBA"],
    "spleen": ["GBA", "HBB"], "liver": ["GBA"], "anemia": ["HBB", "GBA"],
    "pain crisis": ["HBB"], "sickle": ["HBB"],
    "memory": ["APP", "HTT"], "forget": ["APP"], "dementia": ["APP"],
    "movement": ["HTT", "DMD", "SMN1"], "swallowing": ["HTT"],
    "bone pain": ["GBA", "HBB"], "muscle weakness": ["DMD", "SMN1", "GAA"],
    "muscular dystrophy": ["DMD"], "seizure": ["MECP2", "HEXA"],
    "pallor": ["HBB"], "blood": ["HBB", "GBA"],
    "copper": ["ATP7B"], "cholesterol": ["LDLR"],
    "cancer": ["BRCA1", "TP53", "VHL", "NF1"], "tumor risk": ["BRCA1", "TP53"],
    "short stature": ["FGFR3"], "fatigue": ["GBA", "HBB"], "tiredness": ["GBA", "HBB"]
};

// ==========================================================================
// Disease Catalog (browsable database — lite entries beyond featured 5)
// ==========================================================================
const diseaseCatalog = [
    // Featured 5 — mark featured:true so browse view links to them directly
    { gene: "CFTR", name: "Cystic Fibrosis", nameDE: "Mukoviszidose", systems: ["lungs","intestine","pancreas"], prevalence: "1:2.500", onset: "infant", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", featured: true, short: "Zäher Schleim in Lunge & Darm wegen defektem Salz-Wasser-Schleusentor.", shortEN: "Sticky mucus in lungs & gut due to a defective salt-water channel." },
    { gene: "HTT", name: "Huntington's Disease", nameDE: "Chorea Huntington", systems: ["brain"], prevalence: "1:10.000", onset: "adult", inheritance: "autosomal-dominant", difficulty: 4, treatment: "in-research", featured: true, short: "Erbliche, fortschreitende Erkrankung mit Bewegungs-, Stimmungs- und Gedächtnisstörungen.", shortEN: "Inherited progressive disorder with movement, mood, and memory issues." },
    { gene: "GBA", name: "Gaucher Disease", nameDE: "Morbus Gaucher", systems: ["liver","spleen","bones"], prevalence: "1:40.000", onset: "any", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", featured: true, short: "Fett-Stoffwechsel-Störung — Müllabfuhr der Zellen fällt aus.", shortEN: "Lipid storage disorder — the cell's recycling fails." },
    { gene: "HBB", name: "Sickle Cell Anemia", nameDE: "Sichelzellanämie", systems: ["blood","spleen","bones"], prevalence: "1:600", onset: "infant", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", featured: true, short: "Verformte rote Blutzellen verstopfen feine Adern.", shortEN: "Misshapen red blood cells clog small vessels." },
    { gene: "APP", name: "Alzheimer's Disease (familial)", nameDE: "Alzheimer (familiär)", systems: ["brain"], prevalence: "1:100 (over 65)", onset: "elderly", inheritance: "autosomal-dominant", difficulty: 4, treatment: "approved", featured: true, short: "Klebrige Eiweiß-Klumpen stören Nervenzellen im Gehirn.", shortEN: "Sticky protein plaques disrupt brain nerve cells." },

    // Catalog 20+
    { gene: "DMD", name: "Duchenne Muscular Dystrophy", nameDE: "Duchenne-Muskeldystrophie", systems: ["muscle","heart"], prevalence: "1:5.000 boys", onset: "infant", inheritance: "x-linked", difficulty: 4, treatment: "in-research", short: "Fehlende Muskel-Stütze führt zu fortschreitendem Muskelabbau.", shortEN: "Missing muscle-strut protein causes progressive muscle weakness." },
    { gene: "SMN1", name: "Spinal Muscular Atrophy", nameDE: "Spinale Muskelatrophie", systems: ["muscle","brain"], prevalence: "1:10.000", onset: "infant", inheritance: "autosomal-recessive", difficulty: 4, treatment: "approved", short: "Nervenzellen, die Muskeln steuern, gehen verloren.", shortEN: "Loss of motor neurons that drive muscles." },
    { gene: "FBN1", name: "Marfan Syndrome", nameDE: "Marfan-Syndrom", systems: ["heart","bones","eye"], prevalence: "1:5.000", onset: "child", inheritance: "autosomal-dominant", difficulty: 3, treatment: "supportive", short: "Schwaches Bindegewebe — überlange Gliedmaßen, Herz-Risiko.", shortEN: "Weak connective tissue — long limbs and heart risk." },
    { gene: "ATP7B", name: "Wilson Disease", nameDE: "Morbus Wilson", systems: ["liver","brain"], prevalence: "1:30.000", onset: "teen", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", short: "Kupfer sammelt sich in Leber und Gehirn an.", shortEN: "Copper accumulates in liver and brain." },
    { gene: "GAA", name: "Pompe Disease", nameDE: "Morbus Pompe", systems: ["muscle","heart"], prevalence: "1:40.000", onset: "any", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", short: "Zucker-Verwertung im Muskel ist gestört.", shortEN: "Muscle glycogen breakdown is impaired." },
    { gene: "GLA", name: "Fabry Disease", nameDE: "Morbus Fabry", systems: ["heart","blood","kidney"], prevalence: "1:40.000", onset: "child", inheritance: "x-linked", difficulty: 3, treatment: "approved", short: "Fett-Ablagerung in Blutgefäßen schädigt Herz und Nieren.", shortEN: "Fat deposits in vessels damage heart and kidneys." },
    { gene: "HEXA", name: "Tay-Sachs Disease", nameDE: "Tay-Sachs-Krankheit", systems: ["brain"], prevalence: "1:200.000", onset: "infant", inheritance: "autosomal-recessive", difficulty: 5, treatment: "supportive", short: "Fett-Stoff sammelt sich in Nervenzellen — fatal im Kindesalter.", shortEN: "Lipid build-up in nerve cells — fatal in early childhood." },
    { gene: "SOD1", name: "ALS (SOD1-type)", nameDE: "ALS (SOD1-Typ)", systems: ["muscle","brain"], prevalence: "1:50.000", onset: "adult", inheritance: "autosomal-dominant", difficulty: 5, treatment: "in-research", short: "Motorische Nervenzellen sterben fortschreitend ab.", shortEN: "Motor neurons die progressively." },
    { gene: "FXN", name: "Friedreich Ataxia", nameDE: "Friedreich-Ataxie", systems: ["brain","heart","muscle"], prevalence: "1:50.000", onset: "child", inheritance: "autosomal-recessive", difficulty: 3, treatment: "in-research", short: "Eisen-Stoffwechsel in Mitochondrien gestört — Bewegungs-Koordination leidet.", shortEN: "Mitochondrial iron handling fails — coordination is lost." },
    { gene: "MECP2", name: "Rett Syndrome", nameDE: "Rett-Syndrom", systems: ["brain"], prevalence: "1:10.000 girls", onset: "infant", inheritance: "x-linked", difficulty: 4, treatment: "in-research", short: "Genetischer Regler im Gehirn fehlt — Sprache und Motorik gehen verloren.", shortEN: "Brain gene-regulator is missing — speech and motor skills are lost." },
    { gene: "NF1", name: "Neurofibromatosis Type 1", nameDE: "Neurofibromatose Typ 1", systems: ["brain","skin","bones"], prevalence: "1:3.000", onset: "child", inheritance: "autosomal-dominant", difficulty: 3, treatment: "approved", short: "Wachstums-Bremse für Nervengewebe fehlt — gutartige Tumoren entstehen.", shortEN: "Missing nerve-growth brake — benign tumors form." },
    { gene: "PAH", name: "Phenylketonuria (PKU)", nameDE: "Phenylketonurie (PKU)", systems: ["brain"], prevalence: "1:10.000", onset: "infant", inheritance: "autosomal-recessive", difficulty: 2, treatment: "approved", short: "Eiweiß-Baustein Phenylalanin kann nicht abgebaut werden — schadet dem Gehirn.", shortEN: "The amino acid phenylalanine cannot be broken down — brain damage results." },
    { gene: "GALT", name: "Galactosemia", nameDE: "Galaktosämie", systems: ["liver","brain"], prevalence: "1:50.000", onset: "infant", inheritance: "autosomal-recessive", difficulty: 3, treatment: "approved", short: "Milchzucker-Abbau fehlt — Säuglinge brauchen sofortige Diät.", shortEN: "Milk-sugar breakdown fails — infants need an immediate diet." },
    { gene: "FGFR3", name: "Achondroplasia", nameDE: "Achondroplasie", systems: ["bones"], prevalence: "1:25.000", onset: "infant", inheritance: "autosomal-dominant", difficulty: 2, treatment: "approved", short: "Knochen-Wachstum ist gebremst — Kleinwuchs.", shortEN: "Bone growth is slowed — short stature." },
    { gene: "COL3A1", name: "Vascular Ehlers-Danlos", nameDE: "Vaskuläres Ehlers-Danlos", systems: ["heart","skin"], prevalence: "1:50.000", onset: "teen", inheritance: "autosomal-dominant", difficulty: 4, treatment: "supportive", short: "Schwaches Bindegewebe in Gefäßen — Riss-Risiko.", shortEN: "Weak vascular connective tissue — rupture risk." },
    { gene: "VHL", name: "Von Hippel-Lindau Disease", nameDE: "Von-Hippel-Lindau-Erkrankung", systems: ["brain","kidney","eye"], prevalence: "1:36.000", onset: "adult", inheritance: "autosomal-dominant", difficulty: 4, treatment: "approved", short: "Tumor-Bremse fehlt — Zysten und Tumoren in vielen Organen.", shortEN: "Tumor brake fails — cysts and tumors in many organs." },
    { gene: "BRCA1", name: "Hereditary Breast/Ovarian Cancer", nameDE: "Erbliches Brust-/Eierstockkrebs-Risiko", systems: ["other"], prevalence: "1:400", onset: "adult", inheritance: "autosomal-dominant", difficulty: 3, treatment: "approved", short: "DNA-Reparatur ist beeinträchtigt — höheres Krebs-Risiko.", shortEN: "DNA repair is impaired — higher cancer risk." },
    { gene: "PKD1", name: "Polycystic Kidney Disease", nameDE: "Polyzystische Nierenerkrankung", systems: ["kidney"], prevalence: "1:500", onset: "adult", inheritance: "autosomal-dominant", difficulty: 3, treatment: "approved", short: "Zysten lassen die Nieren wachsen — Funktion lässt nach.", shortEN: "Cysts make kidneys grow — function declines." },
    { gene: "LDLR", name: "Familial Hypercholesterolemia", nameDE: "Familiäre Hypercholesterinämie", systems: ["heart","blood"], prevalence: "1:250", onset: "child", inheritance: "autosomal-dominant", difficulty: 2, treatment: "approved", short: "LDL-Cholesterin-Empfänger fehlt — Cholesterin steigt früh.", shortEN: "Missing LDL receptor — cholesterol rises early." },
    { gene: "PRNP", name: "Prion Disease (familial)", nameDE: "Prionen-Erkrankung (familiär)", systems: ["brain"], prevalence: "1:1.000.000", onset: "adult", inheritance: "autosomal-dominant", difficulty: 5, treatment: "supportive", short: "Falsch gefaltetes Eiweiß zerstört Hirngewebe.", shortEN: "Misfolded protein destroys brain tissue." },
    { gene: "TP53", name: "Li-Fraumeni Syndrome", nameDE: "Li-Fraumeni-Syndrom", systems: ["other"], prevalence: "1:5.000", onset: "any", inheritance: "autosomal-dominant", difficulty: 4, treatment: "approved", short: "Tumor-Schutz-Eiweiß fehlt — viele Krebs-Arten möglich.", shortEN: "Tumor-guardian protein is missing — many cancers possible." }
];

// ==========================================================================
// Story scripts — 5-step guided 3D tour per curated disease
// Each step: { titleKey, textKey, action, residue?, autoMs? }
// Actions: 'introduce' | 'reset' | 'zoomResidue' | 'showCompounds' | 'showTrials' | 'wrapup'
// ==========================================================================
const storyScripts = {
    CFTR: [
        { de: { t: "Treffen wir CFTR", x: "Das CFTR-Gen ist der Bauplan für ein winziges Tor in der Zellwand von Lunge, Darm und Schweißdrüsen. Es reguliert den Fluss von Salz und Wasser." }, en: { t: "Meet CFTR", x: "The CFTR gene is the blueprint for a tiny gate in the cell walls of the lungs, gut and sweat glands. It controls the flow of salt and water." }, action: "reset" },
        { de: { t: "So sieht das Tor aus", x: "Hier vor dir steht ein echtes 3D-Modell des CFTR-Proteins, geladen direkt aus der PDB-Strukturdatenbank. Im gesunden Zustand öffnet und schließt es sich rhythmisch." }, en: { t: "Here is the gate", x: "What you see is a real 3D model of the CFTR protein, loaded straight from the PDB structural database. In a healthy cell it opens and closes rhythmically." }, action: "reset" },
        { de: { t: "Der Defekt — Position 508", x: "Die häufigste Variante heißt F508del — an Position 508 fehlt ein winziger Baustein. Schau in 3D: Genau diese Stelle leuchtet in Neon-Rosa. Ohne diesen Baustein verformt sich das Tor und klemmt." }, en: { t: "The defect — position 508", x: "The most common variant is F508del — at position 508 a tiny building block is missing. Watch the 3D model: this exact spot lights up neon-rose. Without that block, the gate warps and jams." }, action: "zoomResidue", residue: 508 },
        { de: { t: "Was das im Körper bedeutet", x: "Wenn das Tor klemmt, bleiben Salz und Wasser im Inneren. Der Schleim auf der Oberfläche wird zäh wie Honig — und macht Atmen, Verdauen und Schwitzen schwer." }, en: { t: "What that means in the body", x: "When the gate jams, salt and water stay trapped inside. The mucus on the surface becomes thick like honey — making breathing, digesting and sweating hard." }, action: "stay" },
        { de: { t: "Was die Forschung tut", x: "Es gibt heute zugelassene Wirkstoffe wie Ivacaftor und Lumacaftor, die das verformte Tor wieder zum Funktionieren bringen. Mehrere Studien laufen — du siehst sie unten." }, en: { t: "What research is doing", x: "Approved drugs like Ivacaftor and Lumacaftor help the warped gate function again today. Several trials are active — you see them below." }, action: "showCompounds" }
    ],
    HTT: [
        { de: { t: "Treffen wir HTT", x: "Das HTT-Gen baut ein wichtiges Transport-Protein in Nervenzellen. Es liefert Nährstoffe entlang langer Nerven-Wege." }, en: { t: "Meet HTT", x: "The HTT gene builds an essential transport protein in nerve cells. It delivers nutrients along long nerve highways." }, action: "reset" },
        { de: { t: "Das Schallplatten-Problem", x: "Bei Chorea Huntington wird ein DNA-Abschnitt zu oft wiederholt — wie eine hängende Schallplatte. Das Protein wird klebrig." }, en: { t: "The stuck-record problem", x: "In Huntington's, a DNA stretch is repeated too many times — like a record that gets stuck. The resulting protein becomes sticky." }, action: "reset" },
        { de: { t: "Erste Schäden", x: "Die klebrigen Proteine schädigen zuerst Nervenzellen tief im Gehirn — dort, wo Bewegung gesteuert wird. Daher die unwillkürlichen, zappelnden Bewegungen." }, en: { t: "First damage", x: "The sticky proteins damage deep brain cells first — the region that controls movement. That's why patients have involuntary, jerky movements." }, action: "zoomResidue", residue: 18 },
        { de: { t: "Stimmung & Gedächtnis", x: "Im weiteren Verlauf werden auch Hirn-Bereiche für Stimmung und Gedächtnis betroffen. Die Krankheit beginnt meist im Erwachsenenalter." }, en: { t: "Mood & memory", x: "Later, brain regions for mood and memory are affected too. The disease typically starts in adulthood." }, action: "stay" },
        { de: { t: "Aktuelle Therapie-Ansätze", x: "Wirkstoffe wie Tominersen versuchen, die Produktion des klebrigen Proteins zu drosseln. Mehrere Studien laufen." }, en: { t: "Current therapy approaches", x: "Drugs like Tominersen try to throttle production of the sticky protein. Several trials are active." }, action: "showCompounds" }
    ],
    GBA: [
        { de: { t: "Treffen wir GBA", x: "Das GBA-Gen baut ein Enzym in der 'Recycling-Anlage' der Zelle (dem Lysosom). Es zerlegt einen bestimmten Fettstoff." }, en: { t: "Meet GBA", x: "The GBA gene builds an enzyme inside the cell's 'recycling plant' (the lysosome). It breaks down a specific fatty molecule." }, action: "reset" },
        { de: { t: "Wenn die Müllabfuhr ausfällt", x: "Wenn dieses Enzym kaputt ist, sammelt sich der Fettstoff in Milz, Leber und Knochen an — wie wenn der Müll nicht abgeholt wird." }, en: { t: "When garbage pickup fails", x: "When the enzyme is broken, the fat piles up in spleen, liver and bones — like rubbish that never gets collected." }, action: "reset" },
        { de: { t: "Die häufigste Mutation", x: "An Position 370 wird ein Baustein vertauscht — N370S. Das Enzym verliert seine Form und arbeitet nicht mehr richtig." }, en: { t: "The most common mutation", x: "At position 370 one building block is swapped — N370S. The enzyme loses its shape and stops working properly." }, action: "zoomResidue", residue: 370 },
        { de: { t: "Symptome", x: "Vergrößerte Milz und Leber, Knochenschmerzen und Müdigkeit — das sind die typischen Folgen der Fett-Anhäufung." }, en: { t: "Symptoms", x: "Enlarged spleen and liver, bone pain and fatigue — these are the typical consequences of the fat build-up." }, action: "stay" },
        { de: { t: "Therapie heute", x: "Es gibt Enzym-Ersatz-Therapien wie Imiglucerase — synthetisches Enzym, das per Infusion gegeben wird. Sehr wirksam." }, en: { t: "Therapy today", x: "There are enzyme replacement therapies like Imiglucerase — synthetic enzyme given by infusion. Very effective." }, action: "showCompounds" }
    ],
    HBB: [
        { de: { t: "Treffen wir HBB", x: "HBB ist der Bauplan für einen Teil des Hämoglobins — das Eiweiß, mit dem rote Blutzellen Sauerstoff durch den Körper tragen." }, en: { t: "Meet HBB", x: "HBB is the blueprint for a piece of hemoglobin — the protein red blood cells use to carry oxygen around the body." }, action: "reset" },
        { de: { t: "Der eine Baustein", x: "Bei der Sichelzell-Mutation ist an Position 6 ein einziger Baustein vertauscht — aus E wird V. Das ist alles." }, en: { t: "The single building block", x: "In the sickle-cell mutation at position 6 a single building block is swapped — E becomes V. That's all it takes." }, action: "zoomResidue", residue: 6 },
        { de: { t: "Was passiert?", x: "Bei Sauerstoffmangel klebt das mutierte Hämoglobin zusammen. Die runden, weichen Blutzellen verformen sich zu spitzen, harten Sicheln." }, en: { t: "What happens?", x: "Under low oxygen the mutated hemoglobin clumps together. Round, soft red blood cells warp into pointed, hard sickles." }, action: "stay" },
        { de: { t: "Die Folge im Körper", x: "Diese Sicheln bleiben in feinen Adern stecken und verursachen schmerzhafte Krisen, Müdigkeit und häufige Infekte." }, en: { t: "The consequence", x: "These sickles get stuck in tiny vessels, causing painful crises, fatigue and frequent infections." }, action: "stay" },
        { de: { t: "Was hilft", x: "Hydroxyurea, Voxelotor und sogar Gen-Editing per CRISPR sind verfügbar oder in Studien — Sichelzell-Therapie hat Riesen-Fortschritte gemacht." }, en: { t: "What helps", x: "Hydroxyurea, Voxelotor, even CRISPR gene-editing are available or in trials — sickle-cell therapy has made huge leaps." }, action: "showCompounds" }
    ],
    APP: [
        { de: { t: "Treffen wir APP", x: "Das APP-Gen ist der Bauplan für ein Eiweiß auf der Oberfläche von Nervenzellen im Gehirn. Es wird ständig zerlegt und neu gebildet." }, en: { t: "Meet APP", x: "The APP gene encodes a protein on brain nerve-cell surfaces. It's constantly cut and rebuilt." }, action: "reset" },
        { de: { t: "Falsch zerschnitten", x: "Bei familiärer Alzheimer-Krankheit wird APP an der falschen Stelle zerschnitten. Es entstehen klebrige Bruchstücke (Amyloid-Beta)." }, en: { t: "Cut at the wrong spot", x: "In familial Alzheimer's, APP is cut at the wrong spot. Sticky fragments emerge (amyloid-beta)." }, action: "reset" },
        { de: { t: "Eine bekannte Mutation", x: "An Position 670 (die 'Swedish Mutation') verändert sich der Schnitt-Punkt — die Schere greift falsch. Mehr klebrige Klümpchen entstehen." }, en: { t: "A famous mutation", x: "At position 670 (the 'Swedish Mutation') the cut-point changes — the scissors snip wrong. More sticky clumps form." }, action: "zoomResidue", residue: 670 },
        { de: { t: "Klumpen im Gehirn", x: "Die Klümpchen sammeln sich zwischen Nervenzellen wie Klebezettel zwischen Mauerritzen — Signale werden blockiert." }, en: { t: "Clumps in the brain", x: "The clumps gather between nerve cells like sticky-notes wedged into wall cracks — signals get blocked." }, action: "stay" },
        { de: { t: "Neue Therapien", x: "Lecanemab und Donanemab — Antikörper, die die Klumpen aus dem Gehirn entfernen — sind seit 2023/2024 zugelassen." }, en: { t: "New therapies", x: "Lecanemab and Donanemab — antibodies that clear the clumps from the brain — are approved since 2023/2024." }, action: "showCompounds" }
    ]
};

// ==========================================================================
// Patient stories for catalog entries (bilingual)
// (featured 5 have their own stories inside featuredDatabase)
// ==========================================================================
const catalogStories = {
    DMD: {
        de: { short: "Das DMD-Gen ist der Bauplan für Dystrophin — eine Stütze, die jede Muskelfaser stabil hält. Ohne diese Stütze brechen die Muskeln bei jeder Bewegung Stück für Stück.", metaphor: "Stelle dir Muskeln wie einen Stoßdämpfer vor. Dystrophin ist die Feder darin. Ohne Feder reibt der Stoßdämpfer sich selbst kaputt.", symptoms: ["Späte Steh-/Lauf-Entwicklung", "Wackelnder Gang, häufiges Hinfallen", "Wadenmuskel-Verhärtung", "Fortschreitende Muskelschwäche"] },
        en: { short: "The DMD gene encodes dystrophin — a strut that keeps every muscle fiber stable. Without it, muscles tear themselves bit by bit with every movement.", metaphor: "Picture muscles as shock absorbers. Dystrophin is the spring inside. Without the spring, the shock absorber grinds itself down.", symptoms: ["Delayed standing/walking", "Wobbly gait, frequent falls", "Calf-muscle stiffening", "Progressive muscle weakness"] }
    },
    SMN1: {
        de: { short: "Das SMN1-Gen versorgt die Nerven, die unsere Muskeln steuern. Wenn es fehlt, sterben diese Nerven nach und nach — die Muskeln werden nicht mehr 'angesteuert'.", metaphor: "Wie ein Notruf-Telefon ohne Strom: die Muskeln warten auf Befehle, die nie kommen.", symptoms: ["Schwache Muskelspannung im Säuglingsalter", "Schwierigkeiten beim Atmen", "Probleme beim Trinken/Schlucken", "Verlust motorischer Fähigkeiten"] },
        en: { short: "The SMN1 gene supports the nerves that drive our muscles. When it's missing, those nerves slowly die — muscles no longer receive commands.", metaphor: "Like an emergency phone without power: the muscles wait for orders that never arrive.", symptoms: ["Weak muscle tone in infancy", "Breathing difficulties", "Problems with feeding/swallowing", "Loss of motor milestones"] }
    },
    FBN1: {
        de: { short: "Das FBN1-Gen ist der Bauplan für Fibrillin — die elastische Faser im Bindegewebe von Sehnen, Herzklappen und Augen-Linsen. Eine schwache Faser lässt diese Strukturen ausleiern.", metaphor: "Wie ein altes Gummiband im Lieblingspullover — alles zieht sich aus, statt zurückzuschnellen.", symptoms: ["Überlange, dünne Gliedmaßen", "Lange, schmale Finger ('Spinnenfinger')", "Erweiterte Hauptschlagader", "Linsen-Verlagerung im Auge"] },
        en: { short: "The FBN1 gene encodes fibrillin — the elastic fiber in connective tissue of tendons, heart valves and eye lenses. A weak fiber lets those structures stretch out.", metaphor: "Like a tired rubber band in your favorite sweater — everything droops instead of snapping back.", symptoms: ["Overly long, thin limbs", "Long slender fingers ('spider fingers')", "Widened aorta", "Lens dislocation in the eye"] }
    },
    ATP7B: {
        de: { short: "Das ATP7B-Gen ist der Bauplan für eine kleine Kupfer-Pumpe in der Leber. Wenn sie defekt ist, sammelt sich Kupfer wie Gift in Leber und Gehirn an.", metaphor: "Wie ein Wasserwerk ohne Abfluss: was reinkommt, bleibt drin — und vergiftet das System.", symptoms: ["Leberentzündung im Jugendalter", "Zitter-Bewegungen", "Persönlichkeits-Veränderungen", "Kupferring um die Hornhaut"] },
        en: { short: "The ATP7B gene encodes a tiny copper pump in the liver. When broken, copper accumulates like a poison in liver and brain.", metaphor: "Like a water plant without drainage: whatever comes in stays in — and poisons the system.", symptoms: ["Hepatitis-like liver issues in teens", "Tremors", "Personality changes", "Copper ring around the cornea"] }
    },
    GAA: {
        de: { short: "Das GAA-Gen ist der Bauplan für ein Enzym, das den Energie-Speicher (Glykogen) im Muskel abbaut. Ohne Abbau verklumpt der Speicher und blockiert die Muskelfasern.", metaphor: "Wie eine Vorratskammer, deren Türen festklemmen — die Lebensmittel sind da, aber nicht mehr erreichbar.", symptoms: ["Muskelschwäche im Säuglingsalter", "Herz-Vergrößerung", "Atemprobleme", "Schlucken erschwert"] },
        en: { short: "The GAA gene encodes an enzyme that breaks down the energy store (glycogen) inside muscle cells. Without breakdown, the store clumps and jams the fibers.", metaphor: "Like a pantry whose doors are jammed — the food is there but no longer reachable.", symptoms: ["Muscle weakness in infancy", "Enlarged heart", "Breathing problems", "Difficulty swallowing"] }
    },
    GLA: {
        de: { short: "Das GLA-Gen ist der Bauplan für ein Fett-spaltendes Enzym. Wenn es fehlt, sammelt sich Fett in den Wänden kleiner Blutgefäße — Herz, Nieren und Nerven leiden.", metaphor: "Wie verstopfte Wasserleitungen voller Schlamm — der Druck steigt, das Material nutzt sich ab.", symptoms: ["Brennende Schmerzen in Händen/Füßen", "Punktförmige rote Haut-Flecken", "Herzrhythmus-Probleme", "Nieren-Funktionsverlust"] },
        en: { short: "The GLA gene encodes a fat-cleaving enzyme. When missing, fat builds up in the walls of small blood vessels — heart, kidneys and nerves all suffer.", metaphor: "Like clogged water pipes full of sludge — pressure rises, the system wears down.", symptoms: ["Burning pain in hands/feet", "Dotty red skin spots", "Heart-rhythm problems", "Kidney failure"] }
    },
    HEXA: {
        de: { short: "Das HEXA-Gen ist der Bauplan für ein Enzym in Nervenzellen. Ohne dieses Enzym sammelt sich Fett in den Zellen, bis sie absterben — meistens im Säuglingsalter tödlich.", metaphor: "Wie eine Spülmaschine ohne Programm: alles Gebrauchte stapelt sich, bis die Küche unbenutzbar ist.", symptoms: ["Verlust motorischer Fähigkeiten", "Krampfanfälle", "Kirschroter Fleck im Auge", "Übersteigerter Schreck-Reflex"] },
        en: { short: "The HEXA gene encodes an enzyme inside nerve cells. Without it, fat piles up inside cells until they die — usually fatal in infancy.", metaphor: "Like a dishwasher with no cycle: every used dish stacks up until the kitchen is unusable.", symptoms: ["Loss of motor milestones", "Seizures", "Cherry-red spot in the eye", "Exaggerated startle reflex"] }
    },
    SOD1: {
        de: { short: "Das SOD1-Gen ist der Bauplan für ein Schutz-Enzym, das schädliche Sauerstoff-Radikale unschädlich macht. Eine defekte Variante wird selbst toxisch und tötet Motor-Nerven.", metaphor: "Wie ein Wachhund, der durchdreht und am Ende selbst die Bewohner beißt.", symptoms: ["Muskelschwäche in einer Gliedmaße", "Muskelzucken", "Versteifte Muskeln", "Sprech- und Schluck-Probleme"] },
        en: { short: "The SOD1 gene encodes a guard enzyme that neutralizes harmful oxygen radicals. A faulty variant becomes toxic itself and kills motor neurons.", metaphor: "Like a guard dog that snaps and starts biting the residents it was meant to protect.", symptoms: ["Weakness in one limb", "Muscle twitching", "Stiff muscles", "Speech and swallowing problems"] }
    },
    FXN: {
        de: { short: "Das FXN-Gen liefert ein Eisen-Transport-Eiweiß für die Energie-Werke der Zelle (Mitochondrien). Ohne es kommen Nervenzellen und Herzmuskel an Energie-Mangel.", metaphor: "Wie Kraftwerke ohne Kohle-Nachschub — alles fährt langsam herunter.", symptoms: ["Unsicherer Gang (Ataxie)", "Verschlechterte Bewegungs-Koordination", "Herz-Schwäche", "Sprach-Verändungen"] },
        en: { short: "The FXN gene delivers an iron-transport protein to the cell's power plants (mitochondria). Without it, nerve cells and heart muscle starve for energy.", metaphor: "Like power plants without coal deliveries — everything slowly winds down.", symptoms: ["Unsteady walking (ataxia)", "Worsening coordination", "Heart weakness", "Slurred speech"] }
    },
    MECP2: {
        de: { short: "Das MECP2-Gen ist ein Master-Schalter, der viele andere Gene in Nervenzellen regelt. Fehlt er, läuft die Nerven-Reifung aus dem Ruder.", metaphor: "Wie ein Dirigent, der nicht mehr da ist — das Orchester aus Nervenzellen spielt durcheinander.", symptoms: ["Verlust gelernter Worte/Bewegungen", "Wiederholende Handbewegungen", "Krampfanfälle", "Atemmuster-Störungen"] },
        en: { short: "The MECP2 gene is a master switch that regulates many other genes in nerve cells. Without it, nerve maturation goes off the rails.", metaphor: "Like a conductor who's gone — the nerve-cell orchestra plays out of sync.", symptoms: ["Loss of learned words/movements", "Repetitive hand movements", "Seizures", "Irregular breathing patterns"] }
    },
    NF1: {
        de: { short: "Das NF1-Gen ist eine Wachstums-Bremse für Nervengewebe. Wenn die Bremse versagt, wachsen kleine gutartige Tumoren entlang der Nerven.", metaphor: "Wie ein Auto ohne Bremspedal — meist langsam, aber an manchen Stellen rollt es einfach los.", symptoms: ["Café-au-lait-Flecken auf der Haut", "Knubbel unter der Haut (Neurofibrome)", "Lern-Schwierigkeiten", "Knochen-Verformungen"] },
        en: { short: "The NF1 gene is a growth brake on nerve tissue. When the brake fails, small benign tumors grow along the nerves.", metaphor: "Like a car without a brake pedal — usually slow, but in some spots it just rolls away.", symptoms: ["Café-au-lait skin spots", "Lumps under the skin (neurofibromas)", "Learning difficulties", "Bone deformities"] }
    },
    PAH: {
        de: { short: "Das PAH-Gen baut die Aminosäure Phenylalanin ab. Wenn das nicht klappt, sammelt sich Phenylalanin im Blut und schädigt das wachsende Gehirn.", metaphor: "Wie ein Spülbecken ohne Abfluss — das, was rein muss, läuft über und beschädigt die Umgebung.", symptoms: ["Entwicklungs-Verzögerung (unbehandelt)", "Helle Hautfarbe", "Modriger Geruch im Urin", "Krampfanfälle (unbehandelt)"] },
        en: { short: "The PAH gene breaks down the amino acid phenylalanine. When it fails, phenylalanine accumulates in the blood and damages the growing brain.", metaphor: "Like a sink with no drain — what's meant to flow out backs up and damages everything around it.", symptoms: ["Developmental delay (if untreated)", "Lighter skin tone", "Musty urine odor", "Seizures (if untreated)"] }
    },
    GALT: {
        de: { short: "Das GALT-Gen baut Milchzucker (Galaktose) ab. Ohne diesen Abbau wird Galaktose giftig — vor allem für Leber, Augen und Gehirn des Säuglings.", metaphor: "Wie ein Spielzimmer, in dem ein Spielzeug nie weggeräumt wird — bald liegt überall dasselbe rum und blockiert alles.", symptoms: ["Gelbsucht beim Säugling", "Trinkschwäche", "Vergrößerte Leber", "Linsentrübung (Katarakt)"] },
        en: { short: "The GALT gene breaks down milk sugar (galactose). Without it, galactose becomes toxic — especially for the infant's liver, eyes and brain.", metaphor: "Like a playroom where one toy is never put away — soon there's only that toy everywhere, blocking everything.", symptoms: ["Jaundice in newborns", "Poor feeding", "Enlarged liver", "Cataract formation"] }
    },
    FGFR3: {
        de: { short: "Das FGFR3-Gen reguliert Knochen-Wachstum. Eine Über-Aktiv-Mutation drosselt das Längs-Wachstum von Armen, Beinen und Schädel.", metaphor: "Wie ein Bauleiter, der allen sagt 'Stopp!' — die Mauer wird einfach nicht höher.", symptoms: ["Kleinwuchs (Erwachsene ca. 1,20 m)", "Kurze Arme und Beine", "Großer Kopf", "Eingedrückte Nasenwurzel"] },
        en: { short: "The FGFR3 gene controls bone growth. An over-active mutation throttles length growth of arms, legs and skull.", metaphor: "Like a foreman shouting 'Stop!' at everyone — the wall just doesn't go any higher.", symptoms: ["Short stature (adult ~1.20 m / 4 ft)", "Short arms and legs", "Larger head", "Depressed nasal bridge"] }
    },
    COL3A1: {
        de: { short: "Das COL3A1-Gen ist der Bauplan für Typ-III-Kollagen, die elastische Faser in Blutgefäßen und Hohl-Organen. Bei schwachem Kollagen reißen die Gefäße leicht.", metaphor: "Wie ein Gartenschlauch aus zu dünnem Material — unter Druck platzt er, wo niemand damit rechnet.", symptoms: ["Dünne, durchscheinende Haut", "Leichte Blutergüsse", "Gefäß-Rupturen (Notfall)", "Spontane Darm-Risse"] },
        en: { short: "The COL3A1 gene encodes type-III collagen, the elastic fiber in vessels and hollow organs. When it's weak, vessels rupture easily.", metaphor: "Like a garden hose made of too-thin material — under pressure it bursts where nobody expected.", symptoms: ["Thin, translucent skin", "Easy bruising", "Vessel ruptures (emergencies)", "Spontaneous bowel tears"] }
    },
    VHL: {
        de: { short: "Das VHL-Gen ist eine universelle Tumor-Bremse. Versagt sie, wachsen gut- und bösartige Tumoren in vielen Organen — meist Nieren, Gehirn und Augen.", metaphor: "Wie eine Stadt-Polizei im Streik — die kleinen Banden, die normalerweise schnell vertrieben werden, breiten sich aus.", symptoms: ["Augen-Hämangiome (Sehverlust-Risiko)", "Kleinhirn-Tumoren", "Nierenkrebs", "Nebennieren-Tumoren"] },
        en: { short: "The VHL gene is a universal tumor brake. When it fails, benign and malignant tumors grow in many organs — usually kidneys, brain and eyes.", metaphor: "Like a city police force on strike — small gangs that would normally be cleared out spread everywhere.", symptoms: ["Eye hemangiomas (vision loss risk)", "Cerebellar tumors", "Kidney cancer", "Adrenal tumors"] }
    },
    BRCA1: {
        de: { short: "Das BRCA1-Gen repariert beschädigte DNA. Wenn es defekt ist, häufen sich genetische Schäden an — vor allem in Brust- und Eierstock-Gewebe.", metaphor: "Wie ein Reparatur-Team mit kaputter Werkstatt — kleine Schäden bleiben liegen, bis daraus ein großes Problem wird.", symptoms: ["Familiäre Häufung von Brustkrebs (jung)", "Häufung von Eierstockkrebs", "Erhöhtes Risiko für andere Krebse", "Symptomfrei bis zum Krebs"] },
        en: { short: "The BRCA1 gene repairs damaged DNA. When it's faulty, genetic damage accumulates — particularly in breast and ovarian tissue.", metaphor: "Like a repair team with a broken workshop — small damages get left until they become a big problem.", symptoms: ["Family history of early breast cancer", "Family history of ovarian cancer", "Higher risk of other cancers", "Asymptomatic until cancer develops"] }
    },
    PKD1: {
        de: { short: "Das PKD1-Gen reguliert das Wachstum von Nieren-Zellen. Bei Defekt entstehen mit den Jahren immer mehr Flüssigkeits-Zysten in den Nieren.", metaphor: "Wie ein Schwamm, der sich mit Wasserbläschen vollsaugt — er wird größer, aber weniger funktional.", symptoms: ["Bluthochdruck im Erwachsenenalter", "Schmerzen im Flanken-Bereich", "Blut im Urin", "Nierenversagen (spät)"] },
        en: { short: "The PKD1 gene regulates kidney-cell growth. When broken, fluid-filled cysts keep forming in the kidneys over the years.", metaphor: "Like a sponge that fills with water bubbles — it gets bigger but less useful.", symptoms: ["High blood pressure in adulthood", "Flank pain", "Blood in urine", "Kidney failure (late)"] }
    },
    LDLR: {
        de: { short: "Das LDLR-Gen ist die Andock-Stelle für 'schlechtes Cholesterin' (LDL). Fehlt sie, kann das Blut LDL nicht abräumen — die Werte steigen schon im Kindes-/Jugendalter.", metaphor: "Wie ein Aufzug ohne Türen — niemand kann mehr aussteigen, das Foyer füllt sich.", symptoms: ["Hohes LDL-Cholesterin schon jung", "Cholesterin-Ablagerungen an Sehnen", "Früher Herzinfarkt-Risiko", "Manchmal Augen-Ring"] },
        en: { short: "The LDLR gene is the docking site for 'bad cholesterol' (LDL). Without it the blood can't clear LDL — levels rise from childhood/teens.", metaphor: "Like an elevator with no doors — nobody can step out, and the lobby fills up.", symptoms: ["High LDL cholesterol from early age", "Cholesterol deposits on tendons", "Early heart-attack risk", "Sometimes an eye ring (arcus)"] }
    },
    PRNP: {
        de: { short: "Das PRNP-Gen ist der Bauplan für ein normales Hirn-Eiweiß. Eine Mutation lässt es sich falsch falten — und nun bringt es andere normale Eiweiße dazu, ebenfalls falsch zu falten. Eine Lawine.", metaphor: "Wie ein einzelnes falsch gefaltetes Hemd, das alle anderen im Schrank dazu bringt, sich falsch zusammenzulegen — bis kein Hemd mehr passt.", symptoms: ["Schnell fortschreitende Demenz", "Bewegungs-Koordinations-Verlust", "Persönlichkeits-Veränderung", "Krampf-Zuckungen (Myoklonus)"] },
        en: { short: "The PRNP gene encodes a normal brain protein. A mutation makes it fold incorrectly — and that misfolded copy then forces other normal copies to misfold too. An avalanche.", metaphor: "Like one badly folded shirt that makes every other shirt in the wardrobe fold itself the same wrong way — until nothing fits anymore.", symptoms: ["Rapidly progressive dementia", "Loss of coordination", "Personality change", "Sudden muscle jerks (myoclonus)"] }
    },
    TP53: {
        de: { short: "Das TP53-Gen wird oft 'Wächter des Genoms' genannt. Es entscheidet, ob eine geschädigte Zelle repariert oder entsorgt wird. Ohne diesen Wächter werden viele Krebs-Arten möglich.", metaphor: "Wie ein Qualitätsprüfer am Fließband, der ausfällt — defekte Produkte gehen ungeprüft raus.", symptoms: ["Familiäre Krebs-Häufung (jung)", "Verschiedene Krebs-Arten möglich", "Sarkome, Brust- und Hirntumoren", "Frühe Diagnose-Notwendigkeit"] },
        en: { short: "The TP53 gene is often called the 'guardian of the genome'. It decides whether a damaged cell gets repaired or removed. Without that guardian, many cancer types become possible.", metaphor: "Like a quality inspector at an assembly line who's gone — defective products go out unchecked.", symptoms: ["Family history of early cancer", "Many different cancer types possible", "Sarcomas, breast and brain tumors", "Early detection needed"] }
    }
};

// ==========================================================================
// i18n — UI strings in EN (default) + DE. Other languages handled via
// Google Translate widget overlay.
// ==========================================================================
const i18n = {
    en: {
        "header.tagline": "Rare Disease & Precision Medicine Explorer",
        "header.searchPlaceholder": "Gene (CFTR, HTT, GBA) or variant (rs113993960) or coord (11:5227002:A:T)...",
        "header.analyze": "Analyze",
        "header.connected": "Bio-Nodes Connected",
        "mode.expert": "Expert Mode",
        "mode.patient": "Patient Mode",
        "quick.featured": "Featured Diseases:",
        "quick.browse": "Browse Library",
        "quick.dossier": "Dossier PDF",
        "panel.bio": "Biological Summary",
        "panel.bioFunction": "Biological Function",
        "panel.subloc": "Subcellular Location",
        "panel.3d": "3D Molecular Structure",
        "panel.therap": "Therapeutics & Trials",
        "panel.activeTrials": "Active Clinical Trials",
        "panel.compounds": "Associated Compounds",
        "panel.variantsExp": "Pathogenic Mutations & ClinVar Evidence",
        "panel.variantsPlain": "Genetic Variants — In Plain Language",
        "panel.symptomSearch": "Symptom Search (HPO)",
        "panel.symptomPlaceholder": "e.g. cough, anemia, forgetfulness, fatigue...",
        "panel.support": "Support & Self-Help",
        "panel.doctorQs": "Questions for the Doctor",
        "panel.stats": "At-a-Glance",
        "stats.totalDiseases": "Diseases in catalog",
        "stats.activeTrials": "Active trials",
        "stats.bodySystems": "Body systems",
        "stats.treatableShare": "With approved therapy",
        "patient.what": "What does this mean?",
        "patient.body": "Where in the body does it act?",
        "patient.symptoms": "Common symptoms",
        "browse.title": "Disease Library",
        "browse.subtitle": "Browse the rare disease catalog — filter, search, dive in.",
        "browse.search": "Search by disease, gene, or symptom...",
        "browse.filterSystem": "Body system",
        "browse.filterTreatment": "Treatment level",
        "browse.filterOnset": "Age of onset",
        "browse.filterInherit": "Inheritance",
        "browse.sortBy": "Sort by",
        "browse.sort.name": "Name (A–Z)",
        "browse.sort.prevalence": "Prevalence (common → rare)",
        "browse.sort.difficulty": "Severity",
        "browse.sort.featured": "Featured first",
        "browse.empty": "No diseases match your filters.",
        "browse.openDisease": "Open in dashboard",
        "browse.featuredBadge": "Featured",
        "browse.allSystems": "All systems",
        "browse.allTreatment": "All treatment levels",
        "browse.allOnset": "Any onset",
        "browse.allInherit": "Any inheritance",
        "treatment.approved": "Approved therapy",
        "treatment.in-research": "In research",
        "treatment.supportive": "Supportive only",
        "onset.infant": "Infant",
        "onset.child": "Child",
        "onset.teen": "Teen",
        "onset.adult": "Adult",
        "onset.elderly": "Elderly",
        "onset.any": "Any age",
        "inherit.autosomal-recessive": "Recessive",
        "inherit.autosomal-dominant": "Dominant",
        "inherit.x-linked": "X-linked",
        "inherit.mitochondrial": "Mitochondrial",
        "inherit.sporadic": "Sporadic",
        "system.brain": "Brain",
        "system.lungs": "Lungs",
        "system.heart": "Heart",
        "system.liver": "Liver",
        "system.spleen": "Spleen",
        "system.kidney": "Kidney",
        "system.blood": "Blood",
        "system.bones": "Bones",
        "system.muscle": "Muscle",
        "system.intestine": "Intestine",
        "system.pancreas": "Pancreas",
        "system.skin": "Skin",
        "system.eye": "Eye",
        "system.sweatGlands": "Sweat glands",
        "system.other": "Multi-system / Other",
        "difficulty": "Complexity (1=simple → 5=very complex)",
        "lang.label": "Language",
        "lang.de": "Deutsch",
        "lang.en": "English",
        "lang.more": "More languages",
        "lang.moreHint": "Powered by Google Translate (UI only, not your data)",
        "footer.privacy": "Privacy-first: all inputs stay in the browser. AuraGene does not replace medical diagnosis.",
        "assist.welcome": "Hi! I am <strong>Aura</strong>, your private companion.<br>Nothing leaves your device — I send no input to external servers.<br><br>You can type freely or tap one of the suggestions below.",
        "assist.unknown": "I can help with:<br>• a gene name (e.g. <em>CFTR</em>)<br>• a variant ID (e.g. <em>rs113993960</em>)<br>• symptoms (e.g. \"<em>fatigue and enlarged spleen</em>\")<br>• jargon (\"<em>What does missense mean?</em>\")<br>• a doctor PDF (\"<em>create PDF</em>\")",
        "patient.noStory": "There is no curated patient-friendly explanation for the gene {gene} yet. The scientific function reads:",
        "patient.noMetaphor": "(no everyday metaphor on file — please discuss with a specialist)",
        "patient.noSymptoms": "Symptom profile is not curated.",
        "docQs.0": "What confirmatory tests are sensible for {disease}, and who covers the cost?",
        "docQs.1": "Which specialists or centers near me treat {disease}?",
        "docQs.2": "Which current clinical trials would be relevant for me/us?",
        "docQs.3": "Are there approved therapies, and would I be eligible?",
        "docQs.4": "Which symptoms should I monitor — and when must I go to the hospital immediately?",
        "docQs.5": "How does {disease} affect daily life, work, and family planning long term?",
        "docQs.6": "Is there genetic counseling available — also for relatives?",
        "docQs.7": "Which patient self-help groups would you recommend?",
        "pdf.title": "AuraGene Dossier",
        "pdf.subtitle": "Rare Disease & Precision Medicine Explorer",
        "pdf.s1": "1. Patient-friendly explanation",
        "pdf.s2": "2. Genetic variants in this report",
        "pdf.s3": "3. Active clinical trials",
        "pdf.s4": "4. Therapy options / compounds",
        "pdf.s5": "5. Support / Patient organizations",
        "pdf.s6": "6. Suggested questions for the doctor",
        "pdf.metaphorLabel": "Everyday analogy:",
        "pdf.symptomsLabel": "Common symptoms:",
        "pdf.disclaimer": "This summary does NOT replace a medical diagnosis. AuraGene aggregates public data (Ensembl, UniProt, ClinicalTrials.gov, OpenTargets, HPO/EBI). Please discuss treatment decisions with a qualified professional.",
        "glossary.title": "Plain-Language Glossary",
        "glossary.subtitle": "Every medical term we know — explained simply.",
        "glossary.search": "Search the glossary...",
        "glossary.empty": "No terms match your search.",
        "glossary.open": "Open glossary",
        "favorites.add": "Add to favorites",
        "favorites.remove": "Remove from favorites",
        "favorites.title": "Favorites",
        "favorites.empty": "No favorites yet — star a disease in the library.",
        "history.title": "Recently viewed",
        "history.empty": "No history yet.",
        "history.clear": "Clear",
        "toast.copied": "Link copied to clipboard.",
        "toast.deepLink": "Loaded from deep link.",
        "toast.error": "Something went wrong — see console for details.",
        "shortcuts.title": "Keyboard shortcuts",
        "shortcuts.search": "/ — focus search",
        "shortcuts.browse": "B — open disease library",
        "shortcuts.assistant": "A — toggle Aura assistant",
        "shortcuts.mode": "M — toggle Expert / Patient",
        "shortcuts.help": "? — show this list",
        "shortcuts.escape": "Esc — close any open panel",
        "onboarding.title": "Welcome to AuraGene",
        "onboarding.line1": "Search a gene, click a featured disease, or browse the library.",
        "onboarding.line2": "Toggle Patient Mode (top right) for plain-language explanations.",
        "onboarding.line3": "Press ? at any time for keyboard shortcuts.",
        "onboarding.dismiss": "Got it",
        "shareBtn": "Share link",
        "shortcutsBtn": "Shortcuts",
        "sugg.missense": "What does missense mean?",
        "sugg.cftr": "Show CFTR",
        "sugg.rs": "Variant rs113993960",
        "sugg.symptom": "Enter symptoms",
        "sugg.pdf": "Create PDF",
        "sugg.patient": "Patient mode",
        "reading.label": "Reading level",
        "reading.simple": "Simple",
        "reading.detailed": "Detailed",
        "tab.story": "Story",
        "tab.body": "Body",
        "tab.inheritance": "Inheritance",
        "tab.prevalence": "Prevalence",
        "tab.more": "More",
        "compare.title": "Compare diseases",
        "compare.subtitle": "Up to 3 diseases side-by-side.",
        "compare.openBtn": "Compare",
        "compare.empty": "Pick diseases from the library using the + icon.",
        "ollama.title": "Local AI translation",
        "ollama.subtitle": "Translate medical jargon locally via Ollama. 100% private — nothing leaves your device.",
        "ollama.enable": "Enable local AI",
        "ollama.url": "Ollama URL",
        "ollama.test": "Test connection",
        "ollama.statusOff": "Not tested",
        "ollama.help": "Install Ollama from <strong>ollama.com</strong> and run <code>ollama pull llama3</code>. The HUD inspector will then use your local model to translate complex findings into plain language — no data ever leaves your computer.",
        "ai.localTranslation": "Local AI translation",
        "stl.title": "Export structure as 3D printable STL",
        "stl.subtitle": "Build a print-ready model of this protein. Atomic radii and bonds are reinforced so the print does not collapse.",
        "stl.radius": "Atom radius (mm scaled)",
        "stl.bondThickness": "Bond thickness (mm scaled)",
        "stl.includeSticks": "Connect atoms with thick bonds",
        "stl.generate": "Generate & download .stl",
        "stl.progress": "Building geometry…",
        "cmp.healthy": "Healthy",
        "cmp.diseased": "With mutation",
        "cmp.autoflip": "Auto-flip",
        "tools.label": "Tools",
        "contact.title": "Contact & Community",
        "contact.subtitle": "Talk to the maintainer. Report data issues. Suggest features. Or just say hi.",
        "contact.bug": "Report data error",
        "contact.bugSub": "Tell us about a wrong variant, missing trial, broken link.",
        "contact.feature": "Suggest a feature",
        "contact.featureSub": "A disease we should add. A new visualization. Tooling.",
        "contact.general": "General message",
        "contact.generalSub": "Questions, collaboration ideas, just a thank-you.",
        "contact.channels": "Other ways to reach us",
        "contact.email": "Direct email",
        "contact.parent": "The parent platform",
        "contact.tools": "More free tools by the same author",
        "contact.disclaimer": "AuraGene is a free public side-project. Replies happen as time allows — usually within a few days.",
        "cp.lernDigital": "Free tools, Chrome extensions, learning resources",
        "cp.talura": "AI phone assistant for medical practices & SMBs",
        "cp.contact": "Report data errors · suggest features · say hi",
        "story.cta": "Tell me the story",
        "story.ctaSub": "Guided 3D tour through this disease",
        "story.play": "Auto-play"
    },
    de: {
        "header.tagline": "Seltene Erkrankungen & Präzisionsmedizin",
        "header.searchPlaceholder": "Gen (CFTR, HTT, GBA) oder Variante (rs113993960) oder Koord (11:5227002:A:T)...",
        "header.analyze": "Analysieren",
        "header.connected": "Bio-Knoten verbunden",
        "mode.expert": "Experten-Modus",
        "mode.patient": "Patienten-Modus",
        "quick.featured": "Hervorgehobene Erkrankungen:",
        "quick.browse": "Bibliothek durchsuchen",
        "quick.dossier": "PDF-Dossier",
        "panel.bio": "Biologische Übersicht",
        "panel.bioFunction": "Biologische Funktion",
        "panel.subloc": "Ort in der Zelle",
        "panel.3d": "3D-Molekülstruktur",
        "panel.therap": "Therapie & Studien",
        "panel.activeTrials": "Aktive klinische Studien",
        "panel.compounds": "Verknüpfte Wirkstoffe",
        "panel.variantsExp": "Krankheits-Mutationen & ClinVar-Belege",
        "panel.variantsPlain": "Genetische Varianten — verständlich",
        "panel.symptomSearch": "Symptom-Suche (HPO)",
        "panel.symptomPlaceholder": "z.B. Husten, Anämie, Vergesslichkeit, Müdigkeit...",
        "panel.support": "Unterstützung & Selbsthilfe",
        "panel.doctorQs": "Fragen für den Arztbesuch",
        "panel.stats": "Auf einen Blick",
        "stats.totalDiseases": "Erkrankungen im Katalog",
        "stats.activeTrials": "Aktive Studien",
        "stats.bodySystems": "Betroffene Organ-Systeme",
        "stats.treatableShare": "Mit zugelassener Therapie",
        "patient.what": "Was bedeutet das?",
        "patient.body": "Wo im Körper wirkt das?",
        "patient.symptoms": "Typische Symptome",
        "browse.title": "Krankheits-Bibliothek",
        "browse.subtitle": "Durchstöbere den Katalog seltener Erkrankungen — Filter, Suche, Detail-Ansicht.",
        "browse.search": "Suche nach Krankheit, Gen oder Symptom...",
        "browse.filterSystem": "Organ-System",
        "browse.filterTreatment": "Therapie-Stand",
        "browse.filterOnset": "Beginn-Alter",
        "browse.filterInherit": "Vererbung",
        "browse.sortBy": "Sortierung",
        "browse.sort.name": "Name (A–Z)",
        "browse.sort.prevalence": "Häufigkeit (häufig → selten)",
        "browse.sort.difficulty": "Schweregrad",
        "browse.sort.featured": "Hervorgehobene zuerst",
        "browse.empty": "Keine Erkrankungen passen zu deinen Filtern.",
        "browse.openDisease": "Im Dashboard öffnen",
        "browse.featuredBadge": "Hervorgehoben",
        "browse.allSystems": "Alle Systeme",
        "browse.allTreatment": "Alle Therapie-Stände",
        "browse.allOnset": "Jedes Alter",
        "browse.allInherit": "Jede Vererbung",
        "treatment.approved": "Zugelassene Therapie",
        "treatment.in-research": "In Forschung",
        "treatment.supportive": "Nur unterstützend",
        "onset.infant": "Säugling",
        "onset.child": "Kindesalter",
        "onset.teen": "Jugendalter",
        "onset.adult": "Erwachsene",
        "onset.elderly": "Senior:innen",
        "onset.any": "Jedes Alter",
        "inherit.autosomal-recessive": "Rezessiv",
        "inherit.autosomal-dominant": "Dominant",
        "inherit.x-linked": "X-chromosomal",
        "inherit.mitochondrial": "Mitochondrial",
        "inherit.sporadic": "Sporadisch",
        "system.brain": "Gehirn",
        "system.lungs": "Lunge",
        "system.heart": "Herz",
        "system.liver": "Leber",
        "system.spleen": "Milz",
        "system.kidney": "Niere",
        "system.blood": "Blut",
        "system.bones": "Knochen",
        "system.muscle": "Muskel",
        "system.intestine": "Darm",
        "system.pancreas": "Bauchspeicheldrüse",
        "system.skin": "Haut",
        "system.eye": "Auge",
        "system.sweatGlands": "Schweißdrüsen",
        "system.other": "Mehrere Systeme / Sonstige",
        "difficulty": "Komplexität (1=einfach → 5=sehr komplex)",
        "lang.label": "Sprache",
        "lang.de": "Deutsch",
        "lang.en": "English",
        "lang.more": "Weitere Sprachen",
        "lang.moreHint": "Über Google Translate (nur Oberfläche, keine Daten)",
        "footer.privacy": "Privacy-first: alle Eingaben bleiben im Browser. AuraGene ersetzt keine ärztliche Diagnose.",
        "assist.welcome": "Hallo! Ich bin <strong>Aura</strong>, dein privater Begleiter.<br>Alles bleibt auf deinem Gerät — ich sende keine Eingaben an externe Server.<br><br>Du kannst frei tippen oder eines der Stichworte unten anklicken.",
        "assist.unknown": "Ich kann dir helfen mit:<br>• einem Gen-Namen (z.B. <em>CFTR</em>)<br>• einer Varianten-ID (z.B. <em>rs113993960</em>)<br>• Symptomen (z.B. \"<em>Müdigkeit und vergrößerte Milz</em>\")<br>• Fach-Begriffen (\"<em>Was bedeutet missense?</em>\")<br>• einem PDF-Dossier (\"<em>PDF erstellen</em>\")",
        "patient.noStory": "Für das Gen {gene} liegt noch keine kuratierte Patienten-Erklärung vor. Die wissenschaftliche Funktion lautet:",
        "patient.noMetaphor": "(keine Alltags-Metapher hinterlegt — bitte mit einer Fachperson besprechen)",
        "patient.noSymptoms": "Symptom-Profil ist nicht kuratiert.",
        "docQs.0": "Welche Bestätigungs-Tests sind für {disease} sinnvoll, und wer trägt die Kosten?",
        "docQs.1": "Welche Spezialist:innen oder Zentren in meiner Nähe behandeln {disease}?",
        "docQs.2": "Welche der aktuellen klinischen Studien wären für mich/uns relevant?",
        "docQs.3": "Gibt es zugelassene Therapien, und wie ist meine Eignung dafür?",
        "docQs.4": "Welche Symptome sollte ich beobachten — und wann sollte ich umgehend in die Klinik?",
        "docQs.5": "Wie wirkt sich {disease} langfristig auf Alltag, Beruf, Familienplanung aus?",
        "docQs.6": "Gibt es genetische Beratung — auch für Angehörige?",
        "docQs.7": "Welche Selbsthilfegruppen empfehlen Sie?",
        "pdf.title": "AuraGene Dossier",
        "pdf.subtitle": "Rare Disease & Precision Medicine Explorer",
        "pdf.s1": "1. Verständliche Erklärung",
        "pdf.s2": "2. Genetische Varianten in diesem Bericht",
        "pdf.s3": "3. Aktive klinische Studien",
        "pdf.s4": "4. Therapie-Optionen / Wirkstoffe",
        "pdf.s5": "5. Unterstützung / Patient:innen-Organisationen",
        "pdf.s6": "6. Vorschläge: Fragen an die Ärztin / den Arzt",
        "pdf.metaphorLabel": "Bildhafter Vergleich:",
        "pdf.symptomsLabel": "Häufige Symptome:",
        "pdf.disclaimer": "Diese Auswertung ersetzt KEINE ärztliche Diagnose. AuraGene aggregiert öffentlich zugängliche Daten (Ensembl, UniProt, ClinicalTrials.gov, OpenTargets, HPO/EBI). Bitte vor Therapie-Entscheidungen mit einer Fachperson sprechen.",
        "glossary.title": "Glossar in Alltagssprache",
        "glossary.subtitle": "Jeder Fachbegriff, den wir kennen — einfach erklärt.",
        "glossary.search": "Glossar durchsuchen...",
        "glossary.empty": "Keine Begriffe passen zu deiner Suche.",
        "glossary.open": "Glossar öffnen",
        "favorites.add": "Zu Favoriten hinzufügen",
        "favorites.remove": "Aus Favoriten entfernen",
        "favorites.title": "Favoriten",
        "favorites.empty": "Noch keine Favoriten — in der Bibliothek auf den Stern klicken.",
        "history.title": "Zuletzt angesehen",
        "history.empty": "Noch nichts angesehen.",
        "history.clear": "Leeren",
        "toast.copied": "Link in die Zwischenablage kopiert.",
        "toast.deepLink": "Aus Deep-Link geladen.",
        "toast.error": "Etwas ist schief gelaufen — Details in der Konsole.",
        "shortcuts.title": "Tastatur-Kürzel",
        "shortcuts.search": "/ — Suchfeld fokussieren",
        "shortcuts.browse": "B — Krankheits-Bibliothek öffnen",
        "shortcuts.assistant": "A — Aura-Assistent öffnen/schließen",
        "shortcuts.mode": "M — Experten / Patienten-Modus",
        "shortcuts.help": "? — Diese Liste anzeigen",
        "shortcuts.escape": "Esc — Offene Panels schließen",
        "onboarding.title": "Willkommen bei AuraGene",
        "onboarding.line1": "Suche ein Gen, klicke eine vorgestellte Erkrankung an oder durchstöbere die Bibliothek.",
        "onboarding.line2": "Aktiviere den Patienten-Modus (oben rechts) für verständliche Erklärungen.",
        "onboarding.line3": "Drücke jederzeit ? für Tastatur-Kürzel.",
        "onboarding.dismiss": "Verstanden",
        "shareBtn": "Link teilen",
        "shortcutsBtn": "Kürzel",
        "sugg.missense": "Was bedeutet missense?",
        "sugg.cftr": "Zeige CFTR",
        "sugg.rs": "Variante rs113993960",
        "sugg.symptom": "Symptome eingeben",
        "sugg.pdf": "PDF erstellen",
        "sugg.patient": "Patienten-Modus",
        "reading.label": "Lese-Stufe",
        "reading.simple": "Einfach",
        "reading.detailed": "Ausführlich",
        "tab.story": "Geschichte",
        "tab.body": "Körper",
        "tab.inheritance": "Vererbung",
        "tab.prevalence": "Häufigkeit",
        "tab.more": "Mehr",
        "compare.title": "Erkrankungen vergleichen",
        "compare.subtitle": "Bis zu 3 Erkrankungen nebeneinander.",
        "compare.openBtn": "Vergleichen",
        "compare.empty": "Wähle Erkrankungen in der Bibliothek über das +-Icon aus.",
        "ollama.title": "Lokale KI-Übersetzung",
        "ollama.subtitle": "Medizinischen Fachjargon lokal via Ollama übersetzen. 100% privat — nichts verlässt dein Gerät.",
        "ollama.enable": "Lokale KI aktivieren",
        "ollama.url": "Ollama-URL",
        "ollama.test": "Verbindung testen",
        "ollama.statusOff": "Noch nicht getestet",
        "ollama.help": "Installiere Ollama von <strong>ollama.com</strong> und führe <code>ollama pull llama3</code> aus. Der HUD-Inspector nutzt dann dein lokales Modell, um komplexe Befunde in Alltagssprache zu übersetzen — keine Daten verlassen jemals deinen Computer.",
        "ai.localTranslation": "Lokale KI-Übersetzung",
        "stl.title": "Struktur als 3D-druckbares STL exportieren",
        "stl.subtitle": "Druckfertiges Modell dieses Proteins bauen. Atom-Radien und Bindungen werden verstärkt, damit der Druck nicht kollabiert.",
        "stl.radius": "Atom-Radius (mm)",
        "stl.bondThickness": "Bindungs-Dicke (mm)",
        "stl.includeSticks": "Atome mit dicken Bindungen verbinden",
        "stl.generate": "Erzeugen & .stl herunterladen",
        "stl.progress": "Geometrie wird gebaut…",
        "cmp.healthy": "Gesund",
        "cmp.diseased": "Mit Mutation",
        "cmp.autoflip": "Auto-Wechsel",
        "tools.label": "Werkzeuge",
        "contact.title": "Kontakt & Community",
        "contact.subtitle": "Schreib dem Maintainer. Melde Daten-Fehler. Wünsche dir Features. Oder sag einfach Hallo.",
        "contact.bug": "Daten-Fehler melden",
        "contact.bugSub": "Falsche Variante, fehlende Studie, kaputter Link — sag Bescheid.",
        "contact.feature": "Feature wünschen",
        "contact.featureSub": "Eine Erkrankung die fehlt. Eine neue Visualisierung. Tooling.",
        "contact.general": "Allgemeine Nachricht",
        "contact.generalSub": "Fragen, Kollaborations-Ideen, einfach ein Danke.",
        "contact.channels": "Weitere Wege",
        "contact.email": "Direkte E-Mail",
        "contact.parent": "Die Mutter-Plattform",
        "contact.tools": "Mehr kostenlose Tools vom selben Autor",
        "contact.disclaimer": "AuraGene ist ein kostenloses öffentliches Nebenprojekt. Antworten kommen so schnell die Zeit erlaubt — meist binnen weniger Tage.",
        "cp.lernDigital": "Kostenlose Tools, Chrome-Extensions, Lern-Ressourcen",
        "cp.talura": "KI-Telefonassistent für Praxen & KMU",
        "cp.contact": "Datenfehler melden · Features wünschen · Hallo sagen",
        "story.cta": "Erzähl mir die Geschichte",
        "story.ctaSub": "Geführte 3D-Tour durch diese Erkrankung",
        "story.play": "Auto-Wechsel"
    }
};

let currentLang = localStorage.getItem('aura_lang') || (navigator.language && navigator.language.startsWith('de') ? 'de' : 'en');

function t(key) {
    return (i18n[currentLang] && i18n[currentLang][key]) || i18n.en[key] || key;
}

function applyI18n() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.getAttribute('data-i18n');
        const val = t(key);
        // If translation contains HTML (e.g. <strong>), allow it via innerHTML
        if (/<[a-z]/.test(val)) el.innerHTML = val;
        else el.textContent = val;
    });
    document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
        const key = el.getAttribute('data-i18n-placeholder');
        el.setAttribute('placeholder', t(key));
    });
    document.querySelectorAll('[data-i18n-title]').forEach(el => {
        const key = el.getAttribute('data-i18n-title');
        el.setAttribute('title', t(key));
    });
    document.documentElement.setAttribute('lang', currentLang);
    // Mode label refresh
    if (DOM.modeLabel) DOM.modeLabel.textContent = currentMode === 'patient' ? t('mode.patient') : t('mode.expert');
}

function setLang(lang) {
    if (!i18n[lang]) lang = 'en';
    currentLang = lang;
    localStorage.setItem('aura_lang', lang);
    applyI18n();
    // Refresh dynamic content rendered earlier
    if (currentGeneData) {
        // Refresh catalog-aware disease name for non-featured genes
        const cat = diseaseCatalog.find(d => d.gene === currentGeneData.geneSymbol);
        if (cat && !featuredDatabase[currentGeneData.geneSymbol]) {
            currentGeneData.diseaseName = currentLang === 'de' ? cat.nameDE : cat.name;
        }
        renderDiseaseInfo(currentGeneData);
        renderPatientView(currentGeneData);
    }
    renderStatsPanel();
    renderFavoritesPanel();
    renderHistoryPanel();
    // Re-populate browse filter labels (option text uses t())
    if (typeof populateBrowseFilters === 'function') populateBrowseFilters();
    // If browse modal is open or has rendered cards, re-render for new locale
    if (DOM.browseGrid && DOM.browseGrid.children.length > 0) renderBrowse();
    // Refresh active language pickers
    document.querySelectorAll('.lang-option').forEach(o => {
        o.classList.toggle('active', o.getAttribute('data-lang') === lang);
    });
}

// ==========================================================================
// Google Translate widget loader (privacy notice: served by Google)
// ==========================================================================
function loadGoogleTranslate() {
    if (window.__googleTranslateLoaded) return;
    window.__googleTranslateLoaded = true;
    window.googleTranslateElementInit = function () {
        // eslint-disable-next-line no-new, no-undef
        new google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: 'de,en,fr,es,it,pt,nl,pl,tr,ru,ar,zh-CN,ja,ko,hi,uk,ro,sv,no,fi,da,cs,el,hu,bg,hr,sk,sl,vi,th,id,he,ms,fa,ur',
            autoDisplay: false,
            layout: google.translate.TranslateElement.InlineLayout.SIMPLE
        }, 'google_translate_element');
    };
    const s = document.createElement('script');
    s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    s.async = true;
    document.head.appendChild(s);
}

// Body-system metaphor heat-map per gene  (mapping body part → opacity)
const bodySystemPaths = {
    brain: { cx: 105, cy: 50, r: 22 },
    eye: { cx: 92, cy: 40, r: 5 },
    lungs: { cx: 105, cy: 130, r: 26 },
    heart: { cx: 95, cy: 135, r: 12 },
    intestine: { cx: 105, cy: 215, r: 22 },
    pancreas: { cx: 130, cy: 195, r: 12 },
    liver: { cx: 80, cy: 175, r: 16 },
    spleen: { cx: 135, cy: 175, r: 12 },
    kidney: { cx: 75, cy: 205, r: 10 },
    bones: { cx: 50, cy: 230, r: 12 },
    muscle: { cx: 165, cy: 280, r: 12 },
    sweatGlands: { cx: 160, cy: 230, r: 10 },
    skin: { cx: 40, cy: 280, r: 9 },
    blood: { cx: 105, cy: 290, r: 14 }
};
// Translated via t('system.<key>') — see i18n dictionary.

// ==========================================================================
// Application State & DOM Cache
// ==========================================================================
let currentPdbId = "1cll";
let isSpinning = false;
let currentRepresentation = 'cartoon';
let currentColorScheme = 'rainbow';
let autocompleteHighlightIndex = -1;
let debounceTimer = null;
let symptomDebounce = null;
let currentMode = localStorage.getItem('aura_mode') || 'expert';
let currentGeneData = null;
let assistantOpen = false;
let voiceListening = false;
let recognition = null;

const DOM = {};

function cacheDOM() {
    const ids = [
        'search-btn', 'gene-search', 'search-loader',
        'target-disease-name', 'target-gene-symbol', 'target-protein-name',
        'target-bio-function', 'target-subcellular-loc', 'protein-indicator',
        'active-pdb-id', 'viewer-loader', 'pdbe-viewer',
        'btn-spin', 'btn-representation', 'btn-color', 'btn-reset',
        'trials-count-badge', 'trials-container', 'compounds-container',
        'variants-table-body',
        'inspector-overlay', 'hud-residue', 'hud-variant', 'hud-clear-btn',
        'autocomplete-suggestions',
        // v3 elements
        'mode-toggle', 'mode-label',
        'patient-summary-card', 'patient-story-text', 'patient-metaphor-text',
        'patient-symptoms-list', 'patient-speak-btn',
        'body-system-card', 'body-system-svg', 'body-system-legend',
        'support-card', 'support-list',
        'doctor-questions-card', 'doctor-questions-list',
        'plain-variants-card', 'plain-variants-list',
        'symptom-search-input', 'symptom-search-results',
        'pdf-export-btn',
        'assistant-fab', 'assistant-panel', 'assistant-close',
        'assistant-messages', 'assistant-input', 'assistant-send',
        'assistant-mic', 'assistant-suggestions',
        // v3.1
        'lang-picker', 'lang-options', 'lang-more', 'google_translate_element',
        'browse-open-btn', 'browse-modal', 'browse-close',
        'browse-search', 'browse-grid', 'browse-empty',
        'browse-filter-system', 'browse-filter-treatment',
        'browse-filter-onset', 'browse-filter-inherit', 'browse-sort',
        'browse-count', 'browse-clear', 'browse-favorites-toggle',
        'stats-panel', 'stats-total-diseases', 'stats-active-trials',
        'stats-systems', 'stats-treatable', 'stats-system-chart',
        // v3.2
        'glossary-modal', 'glossary-close', 'glossary-search', 'glossary-list', 'glossary-open-btn',
        'shortcuts-modal', 'shortcuts-close', 'shortcuts-open-btn',
        'share-link-btn',
        'favorites-list', 'history-list', 'history-clear-btn',
        'side-panel', 'side-panel-toggle',
        'onboarding', 'onboarding-dismiss',
        'toast-container',
        'search-mic-btn',
        'ollama-settings-toggle', 'ollama-url-input', 'ollama-enabled-toggle', 'ollama-status', 'ollama-test-btn',
        'ai-translation-box', 'ai-translation-content', 'ai-translation-spinner',
        'stl-export-btn', 'stl-options-modal', 'stl-options-close',
        'stl-radius-input', 'stl-bond-thickness-input', 'stl-include-sticks',
        'stl-generate-btn', 'stl-progress',
        'btn-compare-view', 'compare-view-overlay', 'compare-banner', 'compare-autoflip-btn',
        'action-tools-toggle', 'action-tools-popover',
        'story-mode-btn', 'story-overlay', 'story-progress-dots', 'story-step-counter',
        'story-title', 'story-text',
        'story-prev-btn', 'story-next-btn', 'story-playpause-btn', 'story-narrate-btn', 'story-close-btn',
        // v3.3
        'inheritance-card', 'prevalence-card', 'external-links-card',
        'compare-modal', 'compare-close', 'compare-grid', 'compare-open-btn', 'compare-count',
        'reading-toggle-simple', 'reading-toggle-detailed',
        'theme-toggle',
        'btn-screenshot',
        'btn-fullscreen'
    ];
    ids.forEach(id => { DOM[toCamel(id)] = document.getElementById(id); });
    DOM.quickChips = document.querySelectorAll('.quick-chip');
}
function toCamel(id) {
    return id.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

// ==========================================================================
// Utility Functions
// ==========================================================================
function debounce(fn, delay, timer) {
    return function (...args) {
        clearTimeout(timer.t);
        timer.t = setTimeout(() => fn.apply(this, args), delay);
    };
}

function classifyQuery(query) {
    const trimmed = query.trim();
    if (/^rs\d+$/i.test(trimmed)) {
        return { type: 'rsid', value: trimmed.toLowerCase() };
    }
    const coordRegex = /^(?:chr)?(\d+|[XYxy]):(\d+)[:\s]*([ACGT])[>:\s]+([ACGT])$/i;
    const match = trimmed.match(coordRegex);
    if (match) {
        return {
            type: 'coordinate',
            chr: match[1], pos: match[2],
            ref: match[3].toUpperCase(), alt: match[4].toUpperCase()
        };
    }
    return { type: 'gene', value: trimmed.toUpperCase() };
}

function escapeHTML(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
}

// ==========================================================================
// Mode Toggle (Expert ↔ Patient)
// ==========================================================================
function setMode(mode) {
    currentMode = mode;
    localStorage.setItem('aura_mode', mode);
    document.body.setAttribute('data-mode', mode);

    // Update segmented mode buttons
    document.querySelectorAll('.mode-segment').forEach(seg => {
        seg.classList.toggle('active', seg.getAttribute('data-mode') === mode);
        seg.setAttribute('aria-pressed', seg.getAttribute('data-mode') === mode);
    });
    if (DOM.modeLabel) DOM.modeLabel.textContent = mode === 'patient' ? t('mode.patient') : t('mode.expert');

    // Re-render patient-only sections if we have data
    if (currentGeneData) {
        renderPatientView(currentGeneData);
    }
    // When switching to patient mode, ensure the first tab is active
    if (mode === 'patient') activatePatientTab('story');
    refreshStoryCTA();
}

// ==========================================================================
// Patient-Mode Tab Navigation
// ==========================================================================
function activatePatientTab(tabName) {
    document.querySelectorAll('.patient-tab').forEach(btn => {
        const isActive = btn.getAttribute('data-tab') === tabName;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive);
    });
    document.querySelectorAll('.patient-tab-pane').forEach(pane => {
        const isActive = pane.getAttribute('data-pane') === tabName;
        if (isActive) pane.removeAttribute('hidden');
        else pane.setAttribute('hidden', '');
    });
}

// ==========================================================================
// Plain-Language Translator
// ==========================================================================
function simplifyText(text) {
    if (!text) return text;
    let out = text;
    const sorted = Object.keys(medicalDictionary).sort((a, b) => b.length - a.length);
    sorted.forEach(term => {
        const explain = dictLookup(term);
        if (!explain) return;
        const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        out = out.replace(re, m => `<span class="plain-term" title="${escapeHTML(explain)}">${m}<span class="plain-tooltip">${escapeHTML(explain)}</span></span>`);
    });
    return out;
}

function plainExplain(term) {
    return dictLookup(term);
}

// ==========================================================================
// Patient View Rendering (Story, Metaphor, Symptoms, Body, Support, Variants)
// ==========================================================================
function renderPatientView(data) {
    renderPatientSummary(data);
    renderBodySystemMap(data);
    renderInheritancePattern(data);
    renderPrevalence(data);
    renderSupportResources(data);
    renderDoctorQuestions(data);
    renderPlainVariants(data);
    renderExternalLinks(data);
    refreshStoryCTA();
}

// ==========================================================================
// Inheritance pattern mini-SVG (autosomal-dominant / recessive / X-linked / mito)
// ==========================================================================
function renderInheritancePattern(data) {
    if (!DOM.inheritanceCard) return;
    const cat = diseaseCatalog.find(d => d.gene === data.geneSymbol);
    if (!cat) { DOM.inheritanceCard.classList.add('hidden'); return; }
    DOM.inheritanceCard.classList.remove('hidden');
    const pat = cat.inheritance;
    const label = t('inherit.' + pat);
    let explain = '';
    if (pat === 'autosomal-dominant') {
        explain = currentLang === 'de'
            ? 'Ein verändertes Gen-Exemplar von einem Elternteil reicht aus, damit die Veranlagung weitergegeben wird. Statistisch erbt etwa jedes 2. Kind die Veranlagung.'
            : 'One altered gene copy from one parent is enough to pass the trait on. Statistically, about every second child inherits it.';
    } else if (pat === 'autosomal-recessive') {
        explain = currentLang === 'de'
            ? 'Beide Eltern tragen je eine veränderte Gen-Kopie. Etwa 1 von 4 Kindern erbt beide veränderten Kopien und entwickelt die Erkrankung.'
            : 'Both parents carry one altered copy each. About 1 in 4 children inherits both altered copies and develops the disease.';
    } else if (pat === 'x-linked') {
        explain = currentLang === 'de'
            ? 'Die Veränderung liegt auf dem X-Chromosom. Söhne sind meist stärker betroffen als Töchter (die einen zweiten X-Schutz haben).'
            : 'The change is on the X chromosome. Sons are typically more affected than daughters (who carry a second protective X).';
    } else if (pat === 'mitochondrial') {
        explain = currentLang === 'de'
            ? 'Mitochondriale DNA wird nur von der Mutter vererbt — Söhne und Töchter aller betroffenen Mütter sind potenziell betroffen.'
            : 'Mitochondrial DNA is inherited only from the mother — sons and daughters of an affected mother can both be affected.';
    } else {
        explain = currentLang === 'de'
            ? 'Vererbungs-Muster nicht eindeutig dokumentiert.'
            : 'Inheritance pattern not clearly documented.';
    }
    DOM.inheritanceCard.innerHTML = `
        <h4 class="patient-section-title">
            <span class="material-symbols-outlined">family_restroom</span>
            <span>${escapeHTML(label)}</span>
        </h4>
        ${buildInheritanceSVG(pat)}
        <p class="patient-explain">${escapeHTML(explain)}</p>
    `;
}

function buildInheritanceSVG(pattern) {
    // 4-person mini pedigree: 2 parents (top) + 2 children (bottom)
    const w = 230, h = 110;
    // Helpers
    const square = (x, y, affected, carrier) => {
        let fill = '#1b1d25', stroke = 'rgba(255,255,255,0.3)';
        if (affected) { fill = 'var(--color-variants)'; stroke = 'var(--color-variants)'; }
        else if (carrier) { fill = 'rgba(255,91,127,0.35)'; stroke = 'var(--color-variants)'; }
        return `<rect x="${x-10}" y="${y-10}" width="20" height="20" fill="${fill}" stroke="${stroke}" stroke-width="1.5" rx="2"/>`;
    };
    const circle = (x, y, affected, carrier) => {
        let fill = '#1b1d25', stroke = 'rgba(255,255,255,0.3)';
        if (affected) { fill = 'var(--color-variants)'; stroke = 'var(--color-variants)'; }
        else if (carrier) { fill = 'rgba(255,91,127,0.35)'; stroke = 'var(--color-variants)'; }
        return `<circle cx="${x}" cy="${y}" r="10" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
    };
    const line = (x1, y1, x2, y2) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>`;

    let parents = '', children = '', edges = '';
    // Edges: father (40,30) — mother (90,30); midpoint (65,30) down to bus (65,55); bus to kids
    edges += line(50, 30, 80, 30); // parent connect
    edges += line(65, 30, 65, 55);
    edges += line(150, 80, 200, 80); // sibling bus
    edges += line(150, 55, 150, 80);
    edges += line(150, 80, 150, 90);
    edges += line(200, 80, 200, 90);
    // Place 4 kids
    edges = '';
    edges += line(50, 30, 100, 30);
    edges += line(75, 30, 75, 50);
    edges += line(75, 50, 195, 50);
    edges += line(75, 50, 75, 60);
    edges += line(115, 50, 115, 60);
    edges += line(155, 50, 155, 60);
    edges += line(195, 50, 195, 60);

    if (pattern === 'autosomal-dominant') {
        parents = square(40, 30, true) + circle(110, 30, false);
        children = square(75, 80, true) + circle(115, 80, false) + square(155, 80, true) + circle(195, 80, false);
    } else if (pattern === 'autosomal-recessive') {
        parents = square(40, 30, false, true) + circle(110, 30, false, true);
        children = square(75, 80, true) + circle(115, 80, false, true) + square(155, 80, false, true) + circle(195, 80, false);
    } else if (pattern === 'x-linked') {
        parents = square(40, 30, false) + circle(110, 30, false, true);
        children = square(75, 80, true) + circle(115, 80, false, true) + square(155, 80, false) + circle(195, 80, false);
    } else if (pattern === 'mitochondrial') {
        parents = square(40, 30, false) + circle(110, 30, true);
        children = square(75, 80, true) + circle(115, 80, true) + square(155, 80, true) + circle(195, 80, true);
    } else {
        parents = square(40, 30, false) + circle(110, 30, false);
        children = square(75, 80, false) + circle(115, 80, false) + square(155, 80, false) + circle(195, 80, false);
    }

    // Legend
    const legendLabels = currentLang === 'de'
        ? { affected: 'betroffen', carrier: 'Träger', unaffected: 'nicht betroffen', male: 'männlich', female: 'weiblich' }
        : { affected: 'affected', carrier: 'carrier', unaffected: 'unaffected', male: 'male', female: 'female' };

    return `
        <svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" class="inherit-svg">
            ${edges}${parents}${children}
            <text x="40" y="20" class="inherit-legend">♂</text>
            <text x="110" y="20" class="inherit-legend">♀</text>
        </svg>
        <div class="inherit-legend-row">
            <span class="il-item"><span class="il-square affected"></span>${escapeHTML(legendLabels.affected)}</span>
            <span class="il-item"><span class="il-square carrier"></span>${escapeHTML(legendLabels.carrier)}</span>
            <span class="il-item"><span class="il-square unaffected"></span>${escapeHTML(legendLabels.unaffected)}</span>
        </div>
    `;
}

// ==========================================================================
// Prevalence dot-grid visualization
// ==========================================================================
function renderPrevalence(data) {
    if (!DOM.prevalenceCard) return;
    const cat = diseaseCatalog.find(d => d.gene === data.geneSymbol);
    if (!cat) { DOM.prevalenceCard.classList.add('hidden'); return; }
    DOM.prevalenceCard.classList.remove('hidden');
    const p = cat.prevalence;
    // Parse "1:2.500" or "1:600" etc.
    let denom = parseFloat((p || '').replace(/[^\d.,]/g, '').replace(/\./g, '').replace(',', '.')) || 0;
    if (!denom || denom < 50) denom = 100; // for very common (1:600 etc.) cap visually
    // Render ~100 dots, highlight 1 (always)
    const dotCount = 100;
    let dots = '';
    for (let i = 0; i < dotCount; i++) {
        const affected = i === 0;
        dots += `<span class="prev-dot ${affected ? 'affected' : ''}"></span>`;
    }
    const labelTxt = currentLang === 'de'
        ? `Häufigkeit ${escapeHTML(p)} — visuell ein Fall pro Block (gerundet)`
        : `Prevalence ${escapeHTML(p)} — one case per block (rounded)`;
    DOM.prevalenceCard.innerHTML = `
        <h4 class="patient-section-title">
            <span class="material-symbols-outlined">groups</span>
            <span>${currentLang === 'de' ? 'Wie häufig ist das?' : 'How common is this?'}</span>
        </h4>
        <div class="prev-grid">${dots}</div>
        <p class="patient-explain">${labelTxt}</p>
    `;
}

// ==========================================================================
// External links (PubMed, NCBI, MalaCards, etc.)
// ==========================================================================
function renderExternalLinks(data) {
    if (!DOM.externalLinksCard) return;
    const gene = data.geneSymbol;
    const disease = data.diseaseName;
    const links = [
        { label: 'PubMed', icon: 'article', url: `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(gene + ' ' + disease)}` },
        { label: 'NCBI Gene', icon: 'data_object', url: `https://www.ncbi.nlm.nih.gov/gene/?term=${encodeURIComponent(gene)}` },
        { label: 'GeneCards', icon: 'badge', url: `https://www.genecards.org/cgi-bin/carddisp.pl?gene=${encodeURIComponent(gene)}` },
        { label: 'MalaCards', icon: 'medical_information', url: `https://www.malacards.org/search?q=${encodeURIComponent(disease)}` },
        { label: 'ClinVar', icon: 'gavel', url: `https://www.ncbi.nlm.nih.gov/clinvar/?term=${encodeURIComponent(gene)}` }
    ];
    DOM.externalLinksCard.innerHTML = `
        <h4 class="patient-section-title">
            <span class="material-symbols-outlined">travel_explore</span>
            <span>${currentLang === 'de' ? 'Mehr erfahren' : 'Learn more'}</span>
        </h4>
        <div class="external-links-grid">
            ${links.map(l => `<a class="ext-link-chip" href="${l.url}" target="_blank" rel="noopener">
                <span class="material-symbols-outlined">${l.icon}</span>
                <span>${escapeHTML(l.label)}</span>
            </a>`).join('')}
        </div>
    `;
}

function pickStory(data) {
    if (!data) return null;
    // First: explicit patientStory on the data object (featured database)
    if (data.patientStory) {
        const ps = data.patientStory;
        if (ps[currentLang]) return ps[currentLang];
        if (ps.en) return ps.en;
        if (ps.de) return ps.de;
        if (ps.short) return ps;
    }
    // Second: catalogStories fallback for non-featured catalog entries
    if (data.geneSymbol && catalogStories[data.geneSymbol]) {
        const cs = catalogStories[data.geneSymbol];
        return cs[currentLang] || cs.en || cs.de;
    }
    return null;
}

function renderPatientSummary(data) {
    if (!DOM.patientStoryText) return;
    const story = pickStory(data);
    if (story) {
        DOM.patientStoryText.textContent = story.short;
        DOM.patientMetaphorText.textContent = story.metaphor;
        if (DOM.patientSymptomsList) {
            DOM.patientSymptomsList.innerHTML = story.symptoms.map(s =>
                `<li><span class="material-symbols-outlined">favorite</span>${escapeHTML(s)}</li>`
            ).join('');
        }
    } else {
        const simplifiedFunction = simplifyText(data.function || '');
        DOM.patientStoryText.innerHTML = t('patient.noStory').replace('{gene}', `<strong>${escapeHTML(data.geneSymbol)}</strong>`) + ' ' + simplifiedFunction;
        DOM.patientMetaphorText.textContent = t('patient.noMetaphor');
        if (DOM.patientSymptomsList) DOM.patientSymptomsList.innerHTML = `<li><span class="material-symbols-outlined">info</span>${t('patient.noSymptoms')}</li>`;
    }
}

function renderBodySystemMap(data) {
    if (!DOM.bodySystemSvg) return;
    const systems = data.bodySystems || guessBodySystems(data);
    const svg = buildBodySVG(systems);
    DOM.bodySystemSvg.innerHTML = svg;
    if (DOM.bodySystemLegend) {
        DOM.bodySystemLegend.innerHTML = systems.map(s =>
            `<span class="legend-chip"><span class="dot"></span>${escapeHTML(t('system.' + s))}</span>`
        ).join('');
    }
}

function guessBodySystems(data) {
    const sub = (data.subcellular || '').toLowerCase();
    const fn = (data.function || '').toLowerCase();
    const sys = new Set();
    if (sub.includes('neuron') || fn.includes('neuro') || fn.includes('brain') || fn.includes('synaptic')) sys.add('brain');
    if (fn.includes('epitheli') || fn.includes('airway') || fn.includes('lung')) sys.add('lungs');
    if (fn.includes('hemoglobin') || fn.includes('erythroc') || fn.includes('oxygen')) sys.add('blood');
    if (fn.includes('lysosom') || fn.includes('glucocereb')) { sys.add('liver'); sys.add('spleen'); }
    return sys.size ? Array.from(sys) : ['brain'];
}

function buildBodySVG(activeSystems) {
    const w = 210, h = 340;
    const isMultiSystem = activeSystems.includes('other') || activeSystems.length >= 4;
    let organs = '';
    let multiHalo = '';
    if (isMultiSystem) {
        // Multi-system fallback: full-body halo + label
        multiHalo = `<path class="body-halo" d="M 65 80 Q 65 75 75 75 L 135 75 Q 145 75 145 80 L 155 200 L 145 230 L 130 320 L 115 320 L 110 230 L 100 230 L 95 320 L 80 320 L 65 230 L 55 200 Z"/>
            <ellipse class="body-halo" cx="105" cy="40" rx="28" ry="32"/>
            <text class="organ-label" x="40" y="22">${escapeHTML(t('system.other'))}</text>`;
    }
    Object.keys(bodySystemPaths).forEach(key => {
        const p = bodySystemPaths[key];
        const active = activeSystems.includes(key);
        const cls = active ? 'organ active' : 'organ';
        const label = t('system.' + key);
        organs += `<circle class="${cls}" cx="${p.cx}" cy="${p.cy}" r="${p.r}" data-organ="${key}"><title>${label}</title></circle>`;
        if (active) {
            organs += `<text class="organ-label" x="${p.cx + p.r + 6}" y="${p.cy + 4}">${label}</text>`;
        }
    });
    // Append multi-system halo last so it renders behind organs
    organs = multiHalo + organs;
    // Body silhouette (head + torso + arms + legs)
    const silhouette = `
        <ellipse cx="105" cy="40" rx="28" ry="32" class="body-line"/>
        <path d="M 65 80 Q 65 75 75 75 L 135 75 Q 145 75 145 80 L 155 200 L 145 230 L 130 320 L 115 320 L 110 230 L 100 230 L 95 320 L 80 320 L 65 230 L 55 200 Z" class="body-line"/>
        <path d="M 65 95 L 30 180 L 38 200 L 55 130 Z" class="body-line"/>
        <path d="M 145 95 L 180 180 L 172 200 L 155 130 Z" class="body-line"/>
    `;
    return `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" class="body-svg">${silhouette}${organs}</svg>`;
}

function renderSupportResources(data) {
    if (!DOM.supportList) return;
    const orgs = data.supportOrgs || [];
    let extra = [];

    // Always add Orphanet + OMIM + HPO links via deterministic URLs
    if (data.orphaCode) {
        extra.push({ name: `Orphanet — ${data.diseaseName}`, url: `https://www.orpha.net/en/disease/detail/${data.orphaCode}`, type: 'orphanet' });
    } else {
        extra.push({ name: `Orphanet (Suche: ${data.diseaseName})`, url: `https://www.orpha.net/en/disease?diseaseGroup=${encodeURIComponent(data.diseaseName)}`, type: 'orphanet' });
    }
    if (data.omim) {
        extra.push({ name: `OMIM — ${data.geneSymbol}`, url: `https://www.omim.org/entry/${data.omim}`, type: 'omim' });
    }
    extra.push({ name: `NIH GARD (rare disease)`, url: `https://rarediseases.info.nih.gov/diseases?query=${encodeURIComponent(data.diseaseName)}`, type: 'gard' });
    extra.push({ name: `NORD (US patient resources)`, url: `https://rarediseases.org/rare-diseases/?search=${encodeURIComponent(data.diseaseName)}`, type: 'nord' });
    extra.push({ name: `HPO — ${data.geneSymbol}`, url: `https://hpo.jax.org/app/browse/gene/${data.geneSymbol}`, type: 'hpo' });

    const all = [...orgs, ...extra];
    DOM.supportList.innerHTML = all.map(o => `
        <a class="support-chip" href="${o.url}" target="_blank" rel="noopener">
            <span class="material-symbols-outlined">favorite_border</span>
            <div class="support-info">
                <strong>${escapeHTML(o.name)}</strong>
                <span>${escapeHTML(o.type || 'Patient-Organisation')}</span>
            </div>
            <span class="material-symbols-outlined ext-icon">open_in_new</span>
        </a>
    `).join('');
}

function getDoctorQuestions(diseaseName) {
    const out = [];
    for (let i = 0; i < 8; i++) {
        out.push(t('docQs.' + i).replace('{disease}', diseaseName));
    }
    return out;
}

function renderDoctorQuestions(data) {
    if (!DOM.doctorQuestionsList) return;
    const questions = getDoctorQuestions(data.diseaseName);
    DOM.doctorQuestionsList.innerHTML = questions.map(q =>
        `<li><span class="material-symbols-outlined">help</span>${escapeHTML(q)}</li>`
    ).join('');
}

function renderPlainVariants(data) {
    if (!DOM.plainVariantsList) return;
    const variants = data.variants || [];
    if (variants.length === 0) {
        DOM.plainVariantsList.innerHTML = '<p class="bio-text-sub">Keine spezifischen Varianten zu dieser Anfrage.</p>';
        return;
    }
    DOM.plainVariantsList.innerHTML = variants.map(v => {
        const sig = (v.clinical || '').toLowerCase();
        let sigBadge = 'unklar';
        let sigClass = 'sig-vus';
        if (sig.includes('pathogenic')) { sigBadge = 'krankheits-verursachend'; sigClass = 'sig-pathogenic'; }
        else if (sig.includes('benign')) { sigBadge = 'harmlos'; sigClass = 'sig-benign'; }
        const consequencePlain = plainExplain(v.consequence) || v.consequence;
        return `
            <div class="plain-variant">
                <div class="plain-variant-head">
                    <strong>${escapeHTML(v.clinical)}</strong>
                    <span class="sig-badge ${sigClass}">${sigBadge}</span>
                </div>
                <div class="plain-variant-body">
                    <span class="plain-variant-id">${escapeHTML(v.id)}</span>
                    <span>${escapeHTML(consequencePlain)}</span>
                </div>
            </div>
        `;
    }).join('');
}

// ==========================================================================
// HPO Symptom Search (EBI OLS + offline fallback)
// ==========================================================================
async function searchHPO(query) {
    if (!query || query.length < 2) {
        if (DOM.symptomSearchResults) DOM.symptomSearchResults.innerHTML = '';
        return;
    }
    const low = query.toLowerCase();
    let apiResults = [];

    // 1. Try EBI OLS HPO (has good CORS)
    try {
        const url = `https://www.ebi.ac.uk/ols4/api/search?q=${encodeURIComponent(query)}&ontology=hp&rows=8&fieldList=label,obo_id,description`;
        const r = await fetch(url);
        if (r.ok) {
            const data = await r.json();
            apiResults = (data.response?.docs || []).map(d => ({
                label: d.label,
                hpoId: d.obo_id,
                desc: Array.isArray(d.description) ? d.description[0] : d.description
            }));
        }
    } catch (e) {
        console.warn('OLS HPO search failed:', e);
    }

    // 2. Match against offline symptom → gene fallback
    const localGenes = new Set();
    Object.keys(symptomFallback).forEach(k => {
        if (low.includes(k) || k.includes(low)) {
            symptomFallback[k].forEach(g => localGenes.add(g));
        }
    });

    renderSymptomResults(apiResults, Array.from(localGenes), query);
}

function renderSymptomResults(hpoTerms, localGenes, query) {
    if (!DOM.symptomSearchResults) return;
    if (hpoTerms.length === 0 && localGenes.length === 0) {
        DOM.symptomSearchResults.innerHTML = `<div class="empty-state"><span class="material-symbols-outlined">search_off</span><p>Keine HPO-Treffer und keine bekannten Gene zu "${escapeHTML(query)}"</p></div>`;
        return;
    }

    let html = '';
    if (localGenes.length > 0) {
        html += `<div class="symptom-section-title">Verknüpfte Gene (Aura-Bibliothek):</div>`;
        html += `<div class="symptom-gene-row">`;
        localGenes.forEach(g => {
            const meta = featuredDatabase[g];
            html += `<button class="symptom-gene-chip" data-gene="${g}">
                <strong>${g}</strong><span>${meta ? escapeHTML(meta.diseaseName) : 'Bekanntes Gen'}</span>
            </button>`;
        });
        html += `</div>`;
    }
    if (hpoTerms.length > 0) {
        html += `<div class="symptom-section-title">HPO-Begriffe (EBI Ontology):</div>`;
        hpoTerms.forEach(t => {
            html += `<div class="hpo-result">
                <div class="hpo-label">${escapeHTML(t.label)} <span class="hpo-id">${escapeHTML(t.hpoId || '')}</span></div>
                ${t.desc ? `<div class="hpo-desc">${escapeHTML(t.desc)}</div>` : ''}
            </div>`;
        });
    }
    DOM.symptomSearchResults.innerHTML = html;

    DOM.symptomSearchResults.querySelectorAll('.symptom-gene-chip').forEach(btn => {
        btn.addEventListener('click', () => {
            const g = btn.getAttribute('data-gene');
            DOM.geneSearch.value = g;
            performAnalysis(g);
        });
    });
}

// ==========================================================================
// Assistant (privacy-first, intent-based)
// ==========================================================================
function openAssistant() {
    assistantOpen = true;
    DOM.assistantPanel.classList.add('open');
    DOM.assistantPanel.setAttribute('aria-hidden', 'false');
    setTimeout(() => DOM.assistantInput && DOM.assistantInput.focus(), 200);
}
function closeAssistant() {
    assistantOpen = false;
    DOM.assistantPanel.classList.remove('open');
    DOM.assistantPanel.setAttribute('aria-hidden', 'true');
}
function pushAssistantMessage(role, content, opts = {}) {
    if (!DOM.assistantMessages) return;
    const msg = document.createElement('div');
    msg.className = `assist-msg assist-${role}`;
    msg.innerHTML = `<div class="assist-bubble">${content}</div>`;
    DOM.assistantMessages.appendChild(msg);
    DOM.assistantMessages.scrollTop = DOM.assistantMessages.scrollHeight;
    if (role === 'bot' && opts.speak) speakText(stripHTML(content));
}
function stripHTML(s) {
    const d = document.createElement('div'); d.innerHTML = s; return d.textContent || '';
}

function processAssistantMessage(text) {
    if (!text || !text.trim()) return;
    const raw = text.trim();
    pushAssistantMessage('user', escapeHTML(raw));

    const lower = raw.toLowerCase();

    // 1. Direct gene mention
    const geneMatch = raw.match(/\b([A-Z][A-Z0-9]{1,9})\b/);
    if (geneMatch && featuredDatabase[geneMatch[1].toUpperCase()]) {
        const g = geneMatch[1].toUpperCase();
        pushAssistantMessage('bot', `Ich lade das Gen <strong>${g}</strong> in das Dashboard. Schau dir die Patienten-Erklärung links an — ich aktiviere den Patienten-Modus für dich.`);
        setMode('patient');
        performAnalysis(g);
        return;
    }

    // 2. rsID variant
    const rs = raw.match(/\brs\d+\b/i);
    if (rs) {
        pushAssistantMessage('bot', `Ich analysiere die Variante <strong>${rs[0]}</strong> über Ensembl VEP und zoome auf den betroffenen Baustein im 3D-Modell.`);
        DOM.geneSearch.value = rs[0];
        performAnalysis(rs[0]);
        return;
    }

    // 3. Coordinate
    if (/chr?\d+:\d+/.test(lower)) {
        pushAssistantMessage('bot', `Genomische Koordinate erkannt — ich starte die Analyse.`);
        DOM.geneSearch.value = raw;
        performAnalysis(raw);
        return;
    }

    // 4. PDF / Dossier intent
    if (/(pdf|dossier|arzt|export|drucken|print)/i.test(lower)) {
        pushAssistantMessage('bot', `Ich erstelle dir ein strukturiertes PDF-Dossier zum aktuellen Gen — das kannst du zum Arzt-Termin mitnehmen.`);
        setTimeout(() => generateDoctorDossier(), 400);
        return;
    }

    // 5. Mode-switch intent
    if (/expert/i.test(lower)) { setMode('expert'); pushAssistantMessage('bot', 'Experten-Modus ist aktiv.'); return; }
    if (/patient|laien|einfach|verstehen/i.test(lower)) { setMode('patient'); pushAssistantMessage('bot', 'Patienten-Modus ist aktiv — komplexe Tabellen sind ausgeblendet.'); return; }

    // 6. Symptom intent (search the local fallback first)
    const symptomHits = Object.keys(symptomFallback).filter(k => lower.includes(k));
    if (symptomHits.length > 0) {
        const allGenes = new Set();
        symptomHits.forEach(k => symptomFallback[k].forEach(g => allGenes.add(g)));
        const list = Array.from(allGenes).map(g => `<button class="assist-chip" data-gene="${g}">${g} — ${featuredDatabase[g]?.diseaseName || ''}</button>`).join(' ');
        pushAssistantMessage('bot', `Folgende Gene sind in der Aura-Bibliothek mit deinen Stichworten verknüpft: ${list}. Klicke auf eins, um es zu öffnen.`);
        // Wire clicks
        setTimeout(() => {
            DOM.assistantMessages.querySelectorAll('.assist-chip[data-gene]').forEach(btn => {
                btn.addEventListener('click', () => {
                    const g = btn.getAttribute('data-gene');
                    DOM.geneSearch.value = g;
                    performAnalysis(g);
                    setMode('patient');
                });
            });
        }, 50);
        return;
    }

    // 7. "Was bedeutet X" / "What does X mean" — medical-dictionary lookup
    const whatDE = raw.match(/was (bedeutet|heißt|ist) (.+?)\??$/i);
    const whatEN = raw.match(/what (does|is) (.+?) (mean|stand for)\??$/i)
                || raw.match(/what does (.+?) mean\??$/i)
                || raw.match(/^define\s+(.+?)\??$/i);
    let term = null;
    if (whatDE) term = whatDE[2].trim();
    else if (whatEN) term = (whatEN[2] || whatEN[1]).trim();
    if (term) {
        const explain = plainExplain(term);
        if (explain) {
            pushAssistantMessage('bot', `<strong>${escapeHTML(term)}</strong>: ${escapeHTML(explain)}`);
        } else {
            const msg = currentLang === 'de'
                ? `Den Begriff <em>${escapeHTML(term)}</em> habe ich nicht im Glossar. Probiere einen anderen Fach-Begriff — ich kenne &gt;80 davon.`
                : `I don't have the term <em>${escapeHTML(term)}</em> in the glossary. Try a different jargon word — I know &gt;80.`;
            pushAssistantMessage('bot', msg);
        }
        return;
    }

    // 8. Help / fallback
    if (/(hilfe|help|was kannst du|wie geht|anleitung|guide|how to)/i.test(lower)) {
        showAssistantWelcome();
        return;
    }

    pushAssistantMessage('bot', t('assist.unknown'));
}

function showAssistantWelcome() {
    pushAssistantMessage('bot', t('assist.welcome'));
}

// ==========================================================================
// Voice (Web Speech API — completely local in modern browsers)
// ==========================================================================
function initSpeechRecognition() {
    const Sr = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Sr) {
        if (DOM.assistantMic) DOM.assistantMic.style.display = 'none';
        return;
    }
    recognition = new Sr();
    recognition.lang = 'de-DE';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        DOM.assistantInput.value = transcript;
        stopVoiceListening();
        processAssistantMessage(transcript);
        DOM.assistantInput.value = '';
    };
    recognition.onend = () => stopVoiceListening();
    recognition.onerror = (e) => {
        console.warn('Speech recognition error:', e);
        stopVoiceListening();
    };
}
function startVoiceListening() {
    if (!recognition) initSpeechRecognition();
    if (!recognition) return;
    try {
        recognition.start();
        voiceListening = true;
        DOM.assistantMic.classList.add('listening');
    } catch (e) {
        console.warn('Recognition start failed:', e);
    }
}
function stopVoiceListening() {
    voiceListening = false;
    if (DOM.assistantMic) DOM.assistantMic.classList.remove('listening');
}
// ==========================================================================
// Search-bar Voicebot (Modul 1 — directly drives gene-search + 3D zoom)
// ==========================================================================
let searchRecognition = null;
let searchListening = false;

function initSearchRecognition() {
    const Sr = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Sr) {
        if (DOM.searchMicBtn) DOM.searchMicBtn.style.display = 'none';
        return;
    }
    searchRecognition = new Sr();
    searchRecognition.lang = currentLang === 'de' ? 'de-DE' : 'en-US';
    searchRecognition.interimResults = false;
    searchRecognition.maxAlternatives = 1;
    searchRecognition.continuous = false;

    searchRecognition.onresult = (e) => {
        try {
            const transcript = (e.results[0][0].transcript || '').trim();
            handleVoiceIntent(transcript);
        } catch (err) {
            console.warn('Search recognition error:', err);
            toast(t('toast.error'), 'error');
        }
        stopSearchListening();
    };
    searchRecognition.onend = () => stopSearchListening();
    searchRecognition.onerror = (e) => {
        console.warn('Search recognition error event:', e);
        stopSearchListening();
    };
}

function startSearchListening() {
    if (!searchRecognition) initSearchRecognition();
    if (!searchRecognition) return;
    // Refresh language each time
    searchRecognition.lang = currentLang === 'de' ? 'de-DE' : 'en-US';
    try {
        searchRecognition.start();
        searchListening = true;
        if (DOM.searchMicBtn) DOM.searchMicBtn.classList.add('listening');
        toast(currentLang === 'de' ? 'Höre zu… z.B. "Suche CFTR"' : 'Listening… try "search CFTR"', 'info', 2500);
    } catch (e) {
        console.warn('Search recognition start failed:', e);
    }
}
function stopSearchListening() {
    searchListening = false;
    if (DOM.searchMicBtn) DOM.searchMicBtn.classList.remove('listening');
}

function handleVoiceIntent(transcript) {
    if (!transcript) return;
    const raw = transcript.trim();
    const lower = raw.toLowerCase();

    // Intent 1: "show/zeige mutation rsXXXX"
    let m = raw.match(/\b(rs\d{2,})\b/i);
    if (m) {
        const rsid = m[1].toLowerCase();
        DOM.geneSearch.value = rsid;
        toast(currentLang === 'de' ? `Variante ${rsid}` : `Variant ${rsid}`, 'success');
        performAnalysis(rsid);
        return;
    }

    // Intent 2: "search/suche [GENE]" or "show/zeige [GENE]"
    const geneIntent = raw.match(/(?:suche?|zeige?|search|show|find|look up|finde|zeig)\s+(?:nach\s+|for\s+|the\s+|das\s+|den\s+|die\s+|den\s+|mutation\s+|gene?\s+|gen\s+)?([A-Za-z][A-Za-z0-9]{1,9})/i);
    if (geneIntent) {
        const sym = geneIntent[1].toUpperCase();
        DOM.geneSearch.value = sym;
        toast(currentLang === 'de' ? `Suche ${sym}` : `Searching ${sym}`, 'success');
        performAnalysis(sym);
        return;
    }

    // Intent 3: standalone gene symbol (all caps or recognizable)
    const symMatch = raw.match(/\b([A-Z][A-Z0-9]{1,9})\b/);
    if (symMatch && (featuredDatabase[symMatch[1]] || diseaseCatalog.some(d => d.gene === symMatch[1]))) {
        const sym = symMatch[1];
        DOM.geneSearch.value = sym;
        toast(currentLang === 'de' ? `Suche ${sym}` : `Searching ${sym}`, 'success');
        performAnalysis(sym);
        return;
    }

    // Intent 4: fuzzy match against known gene names
    const allGenes = [...Object.keys(featuredDatabase), ...diseaseCatalog.map(d => d.gene)];
    const upper = raw.toUpperCase().replace(/\s+/g, '');
    const hit = allGenes.find(g => upper.includes(g));
    if (hit) {
        DOM.geneSearch.value = hit;
        toast(currentLang === 'de' ? `Erkannt: ${hit}` : `Recognized: ${hit}`, 'success');
        performAnalysis(hit);
        return;
    }

    // Intent 5: disease name → catalog lookup by name
    const lowerStripped = lower.replace(/[^\p{L}\s]/gu, '').trim();
    const catalogHit = diseaseCatalog.find(d => {
        const n1 = d.name.toLowerCase();
        const n2 = (d.nameDE || '').toLowerCase();
        return lowerStripped.includes(n1) || lowerStripped.includes(n2);
    });
    if (catalogHit) {
        DOM.geneSearch.value = catalogHit.gene;
        toast(currentLang === 'de' ? `Erkrankung erkannt: ${catalogHit.nameDE}` : `Disease recognized: ${catalogHit.name}`, 'success');
        performAnalysis(catalogHit.gene);
        return;
    }

    // Fallback: just put the raw text in the search field
    DOM.geneSearch.value = raw;
    toast(currentLang === 'de' ? `Nicht erkannt — versuche es nochmal mit "Suche CFTR"` : `Not recognized — try "search CFTR"`, 'info', 4000);
}

function speakText(text) {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'de-DE';
    u.rate = 1.0;
    u.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const de = voices.find(v => v.lang.startsWith('de'));
    if (de) u.voice = de;
    window.speechSynthesis.speak(u);
}

// ==========================================================================
// PDF Dossier (jsPDF — loaded from CDN, no telemetry)
// ==========================================================================
async function generateDoctorDossier() {
    if (!currentGeneData) {
        toast('Select a gene or variant first.', 'info');
        return;
    }
    if (!window.jspdf) {
        toast('PDF engine not yet loaded. Please retry in a moment.', 'error');
        return;
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 50;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = margin;
    const data = currentGeneData;

    // Header
    doc.setFillColor(13, 14, 18);
    doc.rect(0, 0, pageW, 80, 'F');
    doc.setTextColor(0, 242, 254);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(t('pdf.title'), margin, 40);
    doc.setFontSize(10);
    doc.setTextColor(154, 165, 181);
    doc.text(t('pdf.subtitle'), margin, 58);

    const date = new Date().toLocaleString('de-DE');
    doc.text(date, pageW - margin - 110, 40);

    y = 110;
    doc.setTextColor(13, 14, 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(data.diseaseName, margin, y); y += 22;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(94, 105, 120);
    doc.text(`Gen: ${data.geneSymbol}    •    Protein: ${data.proteinName}`, margin, y); y += 26;

    // Helper: section header
    const section = (title, color = [0, 184, 230]) => {
        if (y > pageH - 80) { doc.addPage(); y = margin; }
        doc.setFillColor(...color);
        doc.rect(margin, y - 12, 4, 18, 'F');
        doc.setTextColor(13, 14, 18);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(title, margin + 12, y);
        y += 20;
    };
    const textBlock = (text, opts = {}) => {
        if (!text) return;
        doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
        doc.setFontSize(opts.size || 10);
        doc.setTextColor(...(opts.color || [50, 55, 65]));
        const lines = doc.splitTextToSize(text, pageW - 2 * margin);
        lines.forEach(l => {
            if (y > pageH - 60) { doc.addPage(); y = margin; }
            doc.text(l, margin, y); y += 13;
        });
        y += 6;
    };

    // 1. Plain-Language Summary
    section(t('pdf.s1'));
    const story = pickStory(data);
    if (story) {
        textBlock(story.short);
        textBlock(t('pdf.metaphorLabel'), { bold: true });
        textBlock(story.metaphor);
        textBlock(t('pdf.symptomsLabel'), { bold: true });
        story.symptoms.forEach(s => textBlock('• ' + s));
    } else {
        textBlock(data.function || '');
    }

    // 2. Variants
    section(t('pdf.s2'));
    if (data.variants && data.variants.length) {
        data.variants.forEach(v => {
            textBlock(`${v.id} — ${v.clinical}`, { bold: true });
            const cons = plainExplain(v.consequence) || v.consequence;
            textBlock(`${v.loc}   |   ${v.codon}   |   ${cons}`);
        });
    }

    // 3. Active Trials
    section(t('pdf.s3'), [155, 81, 224]);
    if (data.trials && data.trials.length) {
        data.trials.forEach(tr => {
            textBlock(`${tr.id} — ${tr.phase} — ${tr.status}`, { bold: true });
            textBlock(tr.title);
            textBlock(`https://clinicaltrials.gov/study/${tr.id}`, { color: [0, 100, 180] });
        });
    }

    // 4. Compounds / Therapies
    section(t('pdf.s4'), [155, 81, 224]);
    if (data.compounds && data.compounds.length) {
        data.compounds.forEach(c => {
            textBlock(`${c.name}`, { bold: true });
            textBlock(`${c.role}   |   ${c.phase >= 4 ? t('treatment.approved') : 'Phase ' + c.phase}`);
        });
    }

    // 5. Patient Resources
    section(t('pdf.s5'), [255, 91, 127]);
    const orgs = data.supportOrgs || [];
    orgs.forEach(o => {
        textBlock(`• ${o.name}`, { bold: true });
        textBlock(`  ${o.url}`, { color: [0, 100, 180] });
    });
    if (data.orphaCode) textBlock(`• Orphanet: https://www.orpha.net/en/disease/detail/${data.orphaCode}`, { color: [0, 100, 180] });
    if (data.omim) textBlock(`• OMIM:     https://www.omim.org/entry/${data.omim}`, { color: [0, 100, 180] });
    textBlock(`• HPO Browse: https://hpo.jax.org/app/browse/gene/${data.geneSymbol}`, { color: [0, 100, 180] });

    // 6. Questions for the doctor
    section(t('pdf.s6'), [255, 91, 127]);
    getDoctorQuestions(data.diseaseName).forEach(q => textBlock('• ' + q));

    // Footer disclaimer
    if (y > pageH - 80) { doc.addPage(); y = margin; }
    y = pageH - 50;
    doc.setDrawColor(200, 200, 210);
    doc.line(margin, y, pageW - margin, y);
    y += 14;
    doc.setFontSize(8);
    doc.setTextColor(120, 130, 140);
    doc.text(t('pdf.disclaimer'), margin, y, { maxWidth: pageW - 2 * margin });

    const filename = `AuraGene-Dossier-${data.geneSymbol}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
}

// ==========================================================================
// Molstar Interaction Functions
// ==========================================================================
function getViewerInstance() {
    return DOM.pdbeViewer && DOM.pdbeViewer.viewerInstance;
}

function updateViewerMolecule(pdbId) {
    if (!pdbId) return;
    currentPdbId = pdbId.toLowerCase();
    DOM.activePdbId.textContent = pdbId.toUpperCase();
    DOM.viewerLoader.classList.remove('hidden');
    if (DOM.inspectorOverlay) DOM.inspectorOverlay.classList.add('hidden');
    DOM.pdbeViewer.setAttribute('molecule-id', currentPdbId);
    isSpinning = false;
    DOM.btnSpin.classList.remove('active');
    setTimeout(() => {
        DOM.viewerLoader.classList.add('hidden');
        const viewer = getViewerInstance();
        if (viewer) viewer.visual.reset({ camera: true, theme: true });
    }, 1200);
}

function initViewerControls() {
    DOM.btnSpin.addEventListener('click', () => {
        const v = getViewerInstance(); if (!v) return;
        isSpinning = !isSpinning;
        v.canvas.toggleSpin(isSpinning);
        DOM.btnSpin.classList.toggle('active', isSpinning);
    });
    DOM.btnRepresentation.addEventListener('click', () => {
        const v = getViewerInstance(); if (!v) return;
        if (currentRepresentation === 'cartoon') {
            currentRepresentation = 'spacefill';
            DOM.btnRepresentation.innerHTML = `<span class="material-symbols-outlined">bubble_chart</span> Spheres`;
            v.visual.update({ representation: 'spacefill' });
        } else if (currentRepresentation === 'spacefill') {
            currentRepresentation = 'line';
            DOM.btnRepresentation.innerHTML = `<span class="material-symbols-outlined">blur_on</span> Wireframe`;
            v.visual.update({ representation: 'line' });
        } else {
            currentRepresentation = 'cartoon';
            DOM.btnRepresentation.innerHTML = `<span class="material-symbols-outlined">shape_line</span> Cartoon`;
            v.visual.update({ representation: 'cartoon' });
        }
    });
    DOM.btnColor.addEventListener('click', () => {
        const v = getViewerInstance(); if (!v) return;
        if (currentColorScheme === 'rainbow') {
            currentColorScheme = 'bfactor';
            DOM.btnColor.innerHTML = `<span class="material-symbols-outlined">thermostat</span> B-Factor`;
            v.visual.selectColor('bfactor');
        } else if (currentColorScheme === 'bfactor') {
            currentColorScheme = 'chain';
            DOM.btnColor.innerHTML = `<span class="material-symbols-outlined">layers</span> Chain`;
            v.visual.selectColor('chain');
        } else {
            currentColorScheme = 'rainbow';
            DOM.btnColor.innerHTML = `<span class="material-symbols-outlined">palette</span> Rainbow`;
            v.visual.selectColor('rainbow');
        }
    });
    DOM.btnReset.addEventListener('click', () => {
        const v = getViewerInstance(); if (!v) return;
        v.visual.reset({ camera: true, theme: true });
        clearHighlight();
    });
}

// ==========================================================================
// Autocomplete Engine (NLM Clinical Tables API)
// ==========================================================================
async function fetchGeneSuggestions(query) {
    if (!query || query.length < 2) { hideAutocomplete(); return; }
    const c = classifyQuery(query);
    if (c.type !== 'gene') { hideAutocomplete(); return; }
    try {
        const url = `https://clinicaltables.nlm.nih.gov/api/genes/v4/search?terms=${encodeURIComponent(query)}&df=symbol,name&maxList=8`;
        const r = await fetch(url);
        if (!r.ok) throw new Error('NLM API error');
        const data = await r.json();
        const total = data[0];
        const fields = data[3];
        if (total === 0 || !fields || fields.length === 0) { hideAutocomplete(); return; }
        renderSuggestions(fields);
    } catch (e) {
        console.warn('Autocomplete fetch failed:', e);
        hideAutocomplete();
    }
}
function renderSuggestions(items) {
    DOM.autocompleteSuggestions.innerHTML = '';
    autocompleteHighlightIndex = -1;
    items.forEach((item, index) => {
        const [symbol, name] = item;
        const el = document.createElement('div');
        el.className = 'suggestion-item';
        el.setAttribute('data-index', index);
        el.innerHTML = `<span class="suggestion-symbol">${escapeHTML(symbol)}</span><span class="suggestion-name">${escapeHTML(name || '')}</span>`;
        el.addEventListener('click', () => {
            DOM.geneSearch.value = symbol;
            hideAutocomplete();
            performAnalysis(symbol);
        });
        DOM.autocompleteSuggestions.appendChild(el);
    });
    DOM.autocompleteSuggestions.classList.remove('hidden');
}
function hideAutocomplete() {
    DOM.autocompleteSuggestions.classList.add('hidden');
    DOM.autocompleteSuggestions.innerHTML = '';
    autocompleteHighlightIndex = -1;
}
function navigateSuggestions(direction) {
    const items = DOM.autocompleteSuggestions.querySelectorAll('.suggestion-item');
    if (items.length === 0) return;
    items.forEach(i => i.classList.remove('highlighted'));
    if (direction === 'down') {
        autocompleteHighlightIndex = (autocompleteHighlightIndex + 1) % items.length;
    } else {
        autocompleteHighlightIndex = autocompleteHighlightIndex <= 0 ? items.length - 1 : autocompleteHighlightIndex - 1;
    }
    items[autocompleteHighlightIndex].classList.add('highlighted');
    items[autocompleteHighlightIndex].scrollIntoView({ block: 'nearest' });
}
function selectHighlightedSuggestion() {
    const items = DOM.autocompleteSuggestions.querySelectorAll('.suggestion-item');
    if (autocompleteHighlightIndex >= 0 && autocompleteHighlightIndex < items.length) {
        const symbol = items[autocompleteHighlightIndex].querySelector('.suggestion-symbol').textContent;
        DOM.geneSearch.value = symbol;
        hideAutocomplete();
        performAnalysis(symbol);
        return true;
    }
    return false;
}

// ==========================================================================
// Scientific API Integrations
// ==========================================================================
async function fetchFromUniProt(geneSymbol) {
    try {
        const r = await fetch(`https://rest.uniprot.org/uniprotkb/search?query=gene:${geneSymbol}+AND+organism_id:9606&format=json`);
        if (!r.ok) throw new Error("UniProt query failed");
        const data = await r.json();
        if (!data.results || data.results.length === 0) return null;
        const res = data.results[0];
        const accession = res.primaryAccession;
        const proteinName = res.proteinDescription?.recommendedName?.fullName?.value || "Uncharacterized Protein";
        const functionComment = res.comments?.find(c => c.commentType === 'FUNCTION');
        const bioFunction = functionComment?.texts?.[0]?.value || "Biological function has not yet been fully characterized for this protein.";
        const subCellComment = res.comments?.find(c => c.commentType === 'SUBCELLULAR_LOCATION');
        const locations = subCellComment?.subcellularLocations?.map(l => l.location.value) || [];
        const subcellularLoc = locations.length > 0 ? locations.join(', ') : "Subcellular distribution not documented";
        const pdbRefs = res.uniProtKBCrossReferences?.filter(ref => ref.database === 'PDB') || [];
        const pdbId = pdbRefs.length > 0 ? pdbRefs[0].id : null;
        return { proteinName, function: bioFunction, subcellular: subcellularLoc, pdbId: pdbId ? pdbId.toLowerCase() : null, accession };
    } catch (e) {
        console.warn("UniProt API fetch failed:", e);
        return null;
    }
}
async function fetchEnsemblGeneId(geneSymbol) {
    try {
        const r = await fetch(`https://rest.ensembl.org/lookup/symbol/homo_sapiens/${geneSymbol}?content-type=application/json`);
        if (!r.ok) throw new Error("Ensembl lookup failed");
        const d = await r.json();
        return d.id || null;
    } catch (e) { console.warn("Ensembl id lookup failed:", e); return null; }
}
async function fetchVepByRsId(rsId) {
    try {
        const r = await fetch(`https://rest.ensembl.org/vep/human/id/${rsId}?content-type=application/json`);
        if (!r.ok) throw new Error("VEP rsID query failed");
        return await r.json();
    } catch (e) { console.warn("VEP rsID fetch failed:", e); return null; }
}
async function fetchVepByCoordinate(chr, pos, alt) {
    try {
        const region = `${chr}:${pos}-${pos}:1`;
        const r = await fetch(`https://rest.ensembl.org/vep/human/region/${region}/${alt}?content-type=application/json`);
        if (!r.ok) throw new Error("VEP coordinate query failed");
        return await r.json();
    } catch (e) { console.warn("VEP coordinate fetch failed:", e); return null; }
}
function parseVepResponse(vepData) {
    if (!vepData || !Array.isArray(vepData) || vepData.length === 0) return null;
    const entry = vepData[0];
    const rsId = entry.id || entry.input || 'unknown';
    const location = entry.seq_region_name && entry.start ? `chr${entry.seq_region_name}:${entry.start}` : 'unknown';
    const alleleString = entry.allele_string || '-';
    const transcripts = entry.transcript_consequences || [];
    let best = transcripts[0] || {};
    const proteinAffecting = transcripts.filter(t => t.protein_start);
    if (proteinAffecting.length > 0) best = proteinAffecting[0];
    const geneSymbol = best.gene_symbol || null;
    const consequence = (best.consequence_terms || []).join(', ').replace(/_/g, ' ');
    const residue = best.protein_start || 0;
    const aminoAcids = best.amino_acids || '';
    const codons = best.codons || alleleString;
    let clinicalLabel = `VEP: ${consequence}`;
    if (aminoAcids) {
        const [refAA, altAA] = aminoAcids.split('/');
        if (refAA && altAA) clinicalLabel = `${refAA}${residue}${altAA} (${consequence})`;
    }
    return { geneSymbol, variant: { id: rsId, loc: location, consequence: consequence || 'Sequence alteration', codon: codons, clinical: clinicalLabel, residue } };
}
async function fetchOpenTargetsDrugs(ensemblId) {
    if (!ensemblId) return [];
    // OpenTargets v4 schema (verified 2026-05-23): knownDrugs was renamed to drugAndClinicalCandidates.
    // ClinicalTargetFromTarget exposes {drug{name, mechanismsOfAction{rows{mechanismOfAction}}}, maxClinicalStage}.
    // maxClinicalStage is an enum: APPROVAL / PHASE_3 / PHASE_2 / PHASE_1 / PRECLINICAL / etc.
    const q = {
        query: `query TargetDrugs($ensemblId: String!) {
            target(ensemblId: $ensemblId) {
                drugAndClinicalCandidates {
                    rows {
                        drug {
                            name
                            mechanismsOfAction { rows { mechanismOfAction } }
                        }
                        maxClinicalStage
                    }
                }
            }
        }`,
        variables: { ensemblId }
    };
    const stageToPhase = { APPROVAL: 4, PHASE_3: 3, PHASE_2: 2, PHASE_1: 1, PRECLINICAL: 0 };
    try {
        const r = await fetch('https://api.platform.opentargets.org/api/v4/graphql', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(q)
        });
        if (!r.ok) return [];
        const data = await r.json();
        if (data.errors) return [];
        const rows = data?.data?.target?.drugAndClinicalCandidates?.rows || [];
        const map = new Map();
        rows.forEach(row => {
            const name = row.drug?.name || 'Unknown';
            const phase = stageToPhase[row.maxClinicalStage] ?? 0;
            const moa = row.drug?.mechanismsOfAction?.rows?.[0]?.mechanismOfAction || 'Mechanism not specified';
            const ex = map.get(name);
            if (!ex || phase > (ex.phase || 0)) {
                map.set(name, { name, role: moa, phase });
            }
        });
        return Array.from(map.values()).sort((a, b) => b.phase - a.phase).slice(0, 6);
    } catch { return []; }
}
// ==========================================================================
// API Cache layer — localStorage with TTL + force-refresh
// ==========================================================================
const apiCache = {
    get(key, ttlMs) {
        try {
            const raw = localStorage.getItem('aura_cache_' + key);
            if (!raw) return null;
            const { ts, data } = JSON.parse(raw);
            if (Date.now() - ts > ttlMs) return null;
            return { data, ageMs: Date.now() - ts };
        } catch { return null; }
    },
    set(key, data) {
        try { localStorage.setItem('aura_cache_' + key, JSON.stringify({ ts: Date.now(), data })); } catch {}
    },
    bust(prefix) {
        try {
            Object.keys(localStorage).forEach(k => {
                if (k.startsWith('aura_cache_' + (prefix || ''))) localStorage.removeItem(k);
            });
        } catch {}
    }
};

// Freshness state per section (variants/trials/compounds)
const freshnessState = {};
function markFreshness(section, ageMs) {
    freshnessState[section] = { ts: Date.now() - (ageMs || 0), age: ageMs };
    renderFreshness();
}
function renderFreshness() {
    document.querySelectorAll('.freshness-label').forEach(el => {
        const section = el.getAttribute('data-section');
        const s = freshnessState[section];
        if (!s) { el.textContent = ''; el.classList.add('stale'); return; }
        const age = Date.now() - s.ts;
        el.textContent = (currentLang === 'de' ? 'Live · ' : 'Live · ') + formatAge(age);
        el.classList.remove('stale');
    });
}
// Re-render freshness every 30s
setInterval(renderFreshness, 30000);

async function refreshSection(section) {
    if (!currentGeneData) return;
    const gene = currentGeneData.geneSymbol;
    if (section === 'variants') {
        toast(currentLang === 'de' ? 'ClinVar wird neu geladen…' : 'Refreshing ClinVar…', 'info', 2000);
        apiCache.bust('clinvar_' + gene);
        const r = await fetchClinVarVariants(gene, true);
        if (r && r.data && r.data.length > 0) {
            currentGeneData.variants = r.data;
            renderVariants(r.data);
            renderPatientView(currentGeneData);
            markFreshness('variants', 0);
        }
    } else if (section === 'trials') {
        toast(currentLang === 'de' ? 'Studien werden neu geladen…' : 'Refreshing trials…', 'info', 2000);
        const trials = await fetchClinicalTrials(currentGeneData.diseaseName || gene);
        if (trials && trials.length) {
            currentGeneData.trials = trials;
            renderClinicalTrials(trials);
            markFreshness('trials', 0);
        }
    } else if (section === 'compounds') {
        toast(currentLang === 'de' ? 'Wirkstoffe werden neu geladen…' : 'Refreshing compounds…', 'info', 2000);
        const eid = await fetchEnsemblGeneId(gene);
        if (eid) {
            const drugs = await fetchOpenTargetsDrugs(eid);
            if (drugs && drugs.length) {
                currentGeneData.compounds = drugs;
                renderCompounds(drugs);
                markFreshness('compounds', 0);
            }
        }
    }
}

function formatAge(ms) {
    if (ms == null) return '';
    const s = Math.round(ms / 1000);
    if (s < 60) return currentLang === 'de' ? `vor ${s} s` : `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return currentLang === 'de' ? `vor ${m} min` : `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return currentLang === 'de' ? `vor ${h} h` : `${h}h ago`;
    return currentLang === 'de' ? `vor ${Math.round(h/24)} Tagen` : `${Math.round(h/24)}d ago`;
}

// ==========================================================================
// ClinVar live-fetch via NCBI E-Utils (real, current, updated by ClinVar daily)
// Returns variant array compatible with renderVariants
// ==========================================================================
const aminoCodes3to1 = {
    Ala:'A', Arg:'R', Asn:'N', Asp:'D', Cys:'C', Gln:'Q', Glu:'E', Gly:'G',
    His:'H', Ile:'I', Leu:'L', Lys:'K', Met:'M', Phe:'F', Pro:'P', Ser:'S',
    Thr:'T', Trp:'W', Tyr:'Y', Val:'V', Ter:'X'
};

function parseProteinChange(hgvsTitle) {
    // Examples: "NM_000492.4(CFTR):c.200C>G (p.Pro67Arg)" → { residue:67, label:"P67R" }
    //           "NM_000492.4(CFTR):c.1521_1523delCTT (p.Phe508del)" → { residue:508, label:"F508del" }
    const m = hgvsTitle.match(/\(p\.([A-Z][a-z]{2})(\d+)([A-Z][a-z]{2}|Ter|del|\*|fs)?\)/);
    if (!m) return { residue: 0, label: '' };
    const ref1 = aminoCodes3to1[m[1]] || '?';
    const pos = parseInt(m[2], 10);
    let alt = m[3] || '';
    if (alt === 'del') return { residue: pos, label: `${ref1}${pos}del` };
    if (alt === 'Ter' || alt === '*') return { residue: pos, label: `${ref1}${pos}X` };
    if (alt === 'fs') return { residue: pos, label: `${ref1}${pos}fs` };
    const alt1 = aminoCodes3to1[alt] || alt;
    return { residue: pos, label: `${ref1}${pos}${alt1}` };
}

async function fetchClinVarVariants(geneSymbol, force = false) {
    const cacheKey = 'clinvar_' + geneSymbol;
    if (!force) {
        const cached = apiCache.get(cacheKey, 24 * 60 * 60 * 1000); // 24h cache
        if (cached) return { data: cached.data, ageMs: cached.ageMs, fromCache: true };
    }
    try {
        // 1. esearch: get up to 12 pathogenic variant IDs
        const searchUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi?db=clinvar&term=${encodeURIComponent(geneSymbol + '[gene] AND pathogenic[clin_sig]')}&retmode=json&retmax=12&sort=relevance`;
        const sr = await fetch(searchUrl);
        if (!sr.ok) throw new Error('esearch failed');
        const sd = await sr.json();
        const ids = sd?.esearchresult?.idlist || [];
        if (ids.length === 0) return { data: [], ageMs: 0, fromCache: false };

        // 2. esummary: details
        const summaryUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=clinvar&id=${ids.join(',')}&retmode=json`;
        const fr = await fetch(summaryUrl);
        if (!fr.ok) throw new Error('esummary failed');
        const fd = await fr.json();
        const results = [];
        ids.forEach(id => {
            const r = fd.result?.[id];
            if (!r) return;
            const title = r.title || '';
            const parsed = parseProteinChange(title);
            const vs = r.variation_set?.[0] || {};
            const locs = vs.variation_loc || [];
            const grch38 = locs.find(l => l.assembly_name === 'GRCh38') || locs[0] || {};
            const cdna = vs.cdna_change || '';
            // Pull clinical significance: germline_classification.description or similar
            const sig = r.germline_classification?.description
                || r.clinical_significance?.description
                || 'Pathogenic';
            // dbSNP rsID if available
            const xrefs = vs.variation_xrefs || [];
            const dbsnp = xrefs.find(x => x.db_source === 'dbSNP');
            const rsId = dbsnp ? 'rs' + dbsnp.db_id : (r.accession || 'VCV' + id);
            results.push({
                id: rsId,
                loc: grch38.chr ? `chr${grch38.chr}:${grch38.display_start || grch38.start}` : '?',
                consequence: title.includes('del') ? 'In-frame deletion'
                    : title.includes('Ter') || title.includes('*') ? 'Stop gained'
                    : 'Missense variant',
                codon: cdna || '?',
                clinical: `${sig} (${parsed.label || 'Variant'})`,
                residue: parsed.residue,
                title
            });
        });
        // Filter out variants without protein-residue info (can't 3D-zoom them anyway)
        const filtered = results.filter(v => v.residue > 0).slice(0, 8);
        apiCache.set(cacheKey, filtered);
        return { data: filtered, ageMs: 0, fromCache: false };
    } catch (e) {
        console.warn('ClinVar fetch failed:', e);
        return { data: null, ageMs: 0, fromCache: false };
    }
}

async function fetchClinicalTrials(diseaseName) {
    try {
        const r = await fetch(`https://clinicaltrials.gov/api/v2/studies?query.cond=${encodeURIComponent(diseaseName)}&pageSize=5`);
        if (!r.ok) throw new Error("ClinicalTrials query failed");
        const data = await r.json();
        if (!data.studies || data.studies.length === 0) return [];
        return data.studies.map(s => {
            const info = s.protocolSection;
            return {
                id: info.identificationModule?.nctId || "NCT unknown",
                title: info.descriptionModule?.briefSummary || info.identificationModule?.officialTitle || "Untitled Study",
                status: info.statusModule?.overallStatus || "Unknown",
                phase: info.designModule?.phases?.[0] || "Phase N/A"
            };
        });
    } catch (e) { console.warn("ClinicalTrials API query failed:", e); return []; }
}

// ==========================================================================
// UI Rendering (existing expert-side)
// ==========================================================================
function renderDiseaseInfo(data) {
    // Prefer catalog-localized name when available
    const cat = diseaseCatalog.find(d => d.gene === data.geneSymbol);
    const localizedName = cat ? (currentLang === 'de' ? cat.nameDE : cat.name) : (data.diseaseName || "Custom Analysis");
    DOM.targetDiseaseName.textContent = localizedName;
    DOM.targetGeneSymbol.textContent = data.geneSymbol.toUpperCase();
    DOM.targetProteinName.textContent = data.proteinName;
    DOM.targetBioFunction.innerHTML = simplifyText(data.function);
    DOM.targetSubcellularLoc.innerHTML = simplifyText(data.subcellular);

    DOM.proteinIndicator.className = "cell-protein";
    const subStr = (data.subcellular || "").toLowerCase();
    setTimeout(() => {
        DOM.proteinIndicator.classList.add('active');
        if (subStr.includes('membrane') || subStr.includes('apical')) DOM.proteinIndicator.style.top = "26px";
        else if (subStr.includes('lysosome') || subStr.includes('vesicle')) DOM.proteinIndicator.style.top = "60px";
        else DOM.proteinIndicator.style.top = "75px";
    }, 100);
}

function renderClinicalTrials(trials) {
    DOM.trialsContainer.innerHTML = '';
    const active = trials.filter(t => t.status.toLowerCase().includes('recruiting') || t.status.toLowerCase().includes('active'));
    DOM.trialsCountBadge.textContent = `${active.length} Active`;
    if (trials.length === 0) {
        DOM.trialsContainer.innerHTML = `<div class="empty-state"><span class="material-symbols-outlined">error</span><p>No trials found or api rate-limited.</p></div>`;
        return;
    }
    trials.forEach(t => {
        const statusClass = t.status.toLowerCase().includes('recruiting') ? 'status-recruiting' : 'status-completed';
        const card = document.createElement('div');
        card.className = 'trial-card animate-fade-in';
        card.innerHTML = `
            <div class="trial-header">
                <a href="https://clinicaltrials.gov/study/${t.id}" target="_blank" class="trial-nct">${t.id}</a>
                <span class="trial-status ${statusClass}">${t.status.replaceAll('_', ' ')}</span>
            </div>
            <div class="trial-title" title="${escapeHTML(t.title)}">${escapeHTML(t.title)}</div>
            <div class="trial-meta"><span>Phase: ${escapeHTML(t.phase)}</span><span>Intervention Study</span></div>`;
        DOM.trialsContainer.appendChild(card);
    });
}
function renderCompounds(compounds) {
    DOM.compoundsContainer.innerHTML = '';
    if (!compounds || compounds.length === 0) {
        DOM.compoundsContainer.innerHTML = `<div class="empty-state"><p>No therapeutic compounds linked.</p></div>`;
        return;
    }
    compounds.forEach(c => {
        const phase = c.phase || 0;
        let cls = 'phase-investigational', label = 'Investigational';
        if (phase >= 4) { cls = 'phase-approved'; label = 'Approved'; }
        else if (phase >= 2) { cls = 'phase-clinical'; label = `Phase ${phase}`; }
        else if (phase === 1) { cls = 'phase-investigational'; label = 'Phase 1'; }
        const chip = document.createElement('div');
        chip.className = 'compound-chip animate-fade-in';
        chip.innerHTML = `
            <span class="material-symbols-outlined">science</span>
            <div class="compound-info">
                <div class="compound-header"><strong>${escapeHTML(c.name)}</strong><span class="phase-badge ${cls}">${label}</span></div>
                <span>${escapeHTML(c.role)}</span>
            </div>`;
        DOM.compoundsContainer.appendChild(chip);
    });
}
function renderVariants(variants) {
    DOM.variantsTableBody.innerHTML = '';
    if (!variants || variants.length === 0) {
        DOM.variantsTableBody.innerHTML = `<tr><td colspan="6" style="text-align:center;">No variant catalog linked.</td></tr>`;
        return;
    }
    variants.forEach(v => {
        const sig = v.clinical.toLowerCase();
        let badgeClass = 'sig-vus';
        if (sig.includes('pathogenic')) badgeClass = 'sig-pathogenic';
        if (sig.includes('benign')) badgeClass = 'sig-benign';
        const residue = v.residue || 0;
        const row = document.createElement('tr');
        row.className = 'animate-fade-in';
        row.setAttribute('data-residue', residue);
        row.setAttribute('data-clinical', v.clinical);
        const isRsId = v.id && v.id.startsWith('rs');
        const idLink = isRsId
            ? `<a href="https://www.ncbi.nlm.nih.gov/snp/${v.id}" target="_blank" class="table-link">${v.id}</a>`
            : `<span class="table-link">${escapeHTML(v.id)}</span>`;
        row.innerHTML = `
            <td>${idLink}</td>
            <td>${escapeHTML(v.loc)} (GRCh38)</td>
            <td>${escapeHTML(v.consequence)}</td>
            <td><code>${escapeHTML(v.codon)}</code></td>
            <td><span class="sig-badge ${badgeClass}">${escapeHTML(v.clinical)}</span></td>
            <td>${residue ? `<button class="inspect-row-btn" data-residue="${residue}" data-variant="${escapeHTML(v.clinical)}"><span class="material-symbols-outlined">biotech</span> Inspect</button>` : `<span style="color:var(--text-muted);">N/A</span>`}</td>`;
        if (residue) {
            row.addEventListener('click', (e) => {
                if (e.target.tagName === 'A') return;
                document.querySelectorAll('#variants-table-body tr').forEach(r => r.classList.remove('active-row'));
                row.classList.add('active-row');
                highlightResidue(residue, v.clinical);
            });
        }
        DOM.variantsTableBody.appendChild(row);
    });
}

// ==========================================================================
// Search & Flow Orchestration
// ==========================================================================
async function performAnalysis(query) {
    if (!query) return;
    DOM.searchLoader.classList.remove('hidden');
    DOM.searchBtn.disabled = true;
    hideAutocomplete();
    // Track history (gene symbols only — not rsIDs) + share-link hash
    const trimmed = query.trim().toUpperCase();
    if (/^[A-Z][A-Z0-9]{1,9}$/.test(trimmed)) {
        pushHistory(trimmed);
    }
    updateHashFor(query.trim());

    const c = classifyQuery(query);

    if (c.type === 'rsid' || c.type === 'coordinate') {
        let vep = null;
        if (c.type === 'rsid') vep = await fetchVepByRsId(c.value);
        else vep = await fetchVepByCoordinate(c.chr, c.pos, c.alt);
        const parsed = parseVepResponse(vep);
        if (!parsed || !parsed.geneSymbol) {
            toast(`VEP could not resolve "${query}".`, 'error', 5000);
            DOM.searchLoader.classList.add('hidden'); DOM.searchBtn.disabled = false; return;
        }
        const geneSymbol = parsed.geneSymbol.toUpperCase();
        if (featuredDatabase[geneSymbol]) {
            const entry = { ...featuredDatabase[geneSymbol] };
            entry.variants = [...featuredDatabase[geneSymbol].variants];
            const existing = entry.variants.find(v => v.id === parsed.variant.id);
            if (!existing) entry.variants.unshift(parsed.variant);
            currentGeneData = entry;
            renderDiseaseInfo(entry);
            renderClinicalTrials(entry.trials);
            renderCompounds(entry.compounds);
            renderVariants(entry.variants);
            renderPatientView(entry);
            updateViewerMolecule(entry.pdbId);
            if (parsed.variant.residue) {
                setTimeout(() => {
                    const rows = document.querySelectorAll('#variants-table-body tr');
                    if (rows.length > 0) rows[0].classList.add('active-row');
                    highlightResidue(parsed.variant.residue, parsed.variant.clinical);
                }, 1800);
            }
            fetchClinicalTrials(entry.diseaseName).then(live => { if (live && live.length > 0) renderClinicalTrials(live); });
            DOM.searchLoader.classList.add('hidden'); DOM.searchBtn.disabled = false; return;
        }
        await performLiveGeneAnalysis(geneSymbol, parsed.variant);
        DOM.searchLoader.classList.add('hidden'); DOM.searchBtn.disabled = false; return;
    }

    const clean = c.value;
    if (featuredDatabase[clean]) {
        const entry = { ...featuredDatabase[clean] };
        currentGeneData = entry;
        renderDiseaseInfo(entry);
        // Initial paint with hardcoded fallback (instant UX), then replace with live data
        renderClinicalTrials(entry.trials);
        renderCompounds(entry.compounds);
        renderVariants(entry.variants);
        renderPatientView(entry);
        updateViewerMolecule(entry.pdbId);
        DOM.searchLoader.classList.add('hidden'); DOM.searchBtn.disabled = false;
        // Live-fetch all 3 in parallel and overwrite if available
        fetchClinicalTrials(entry.diseaseName).then(live => {
            if (live && live.length > 0) { entry.trials = live; renderClinicalTrials(live); markFreshness('trials', 0); }
        });
        fetchEnsemblGeneId(clean).then(eid => {
            if (eid) fetchOpenTargetsDrugs(eid).then(drugs => {
                if (drugs && drugs.length > 0) { entry.compounds = drugs; renderCompounds(drugs); markFreshness('compounds', 0); }
            });
        });
        fetchClinVarVariants(clean).then(r => {
            if (r && r.data && r.data.length > 0) {
                entry.variants = r.data;
                renderVariants(r.data);
                markFreshness('variants', r.ageMs);
            } else {
                markFreshness('variants', null);
            }
        });
        return;
    }

    await performLiveGeneAnalysis(clean, null);
    DOM.searchLoader.classList.add('hidden'); DOM.searchBtn.disabled = false;
}

async function performLiveGeneAnalysis(geneSymbol, userVariant) {
    const [uni, ensemblId] = await Promise.all([fetchFromUniProt(geneSymbol), fetchEnsemblGeneId(geneSymbol)]);
    if (!uni) {
        toast(`Gene "${geneSymbol}" could not be resolved on UniProt.`, 'error', 5000);
        return;
    }
    const variants = [];
    if (userVariant) variants.push(userVariant);
    const pdbId = uni.pdbId || '1cll';
    // Enrich with catalog metadata if available (body systems, prevalence-level disease name, etc.)
    const catalogEntry = diseaseCatalog.find(d => d.gene === geneSymbol);
    const payload = {
        diseaseName: catalogEntry ? (currentLang === 'de' ? catalogEntry.nameDE : catalogEntry.name) : `${geneSymbol}-Associated Syndrome`,
        geneSymbol,
        proteinName: uni.proteinName,
        function: uni.function,
        subcellular: uni.subcellular,
        pdbId,
        variants,
        compounds: [],
        trials: [],
        supportOrgs: [],
        bodySystems: catalogEntry ? catalogEntry.systems : undefined
    };
    currentGeneData = payload;
    renderDiseaseInfo(payload);
    renderVariants(variants);
    renderPatientView(payload);
    updateViewerMolecule(pdbId);
    DOM.compoundsContainer.innerHTML = `<div class="empty-state"><div class="loader"></div><p>Querying OpenTargets...</p></div>`;

    if (userVariant && userVariant.residue) {
        setTimeout(() => {
            const rows = document.querySelectorAll('#variants-table-body tr');
            if (rows.length > 0) rows[0].classList.add('active-row');
            highlightResidue(userVariant.residue, userVariant.clinical);
        }, 1800);
    }

    const [drugs, trials, clinvar] = await Promise.all([
        ensemblId ? fetchOpenTargetsDrugs(ensemblId) : Promise.resolve([]),
        fetchClinicalTrials(geneSymbol + " disease"),
        fetchClinVarVariants(geneSymbol)
    ]);
    payload.compounds = drugs;
    payload.trials = trials;
    if (drugs && drugs.length > 0) renderCompounds(drugs);
    else renderCompounds([{ name: currentLang === 'de' ? 'Keine zugelassenen Wirkstoffe gefunden' : 'No approved compounds found', role: currentLang === 'de' ? `OpenTargets hat aktuell nichts für ${geneSymbol}` : `OpenTargets has no current data for ${geneSymbol}`, phase: 0 }]);
    renderClinicalTrials(trials);
    markFreshness('compounds', 0);
    markFreshness('trials', 0);
    if (clinvar && clinvar.data && clinvar.data.length > 0) {
        payload.variants = [...payload.variants, ...clinvar.data];
        renderVariants(payload.variants);
        renderPatientView(payload);
        markFreshness('variants', clinvar.ageMs);
        // Auto-highlight first pathogenic if no user variant
        if (!userVariant && payload.variants[0]?.residue) {
            setTimeout(() => highlightResidue(payload.variants[0].residue, payload.variants[0].clinical), 1800);
        }
    } else {
        markFreshness('variants', null);
    }
}

// ==========================================================================
// 3D Selection & Zoom
// ==========================================================================
function highlightResidue(residueNum, variantLabel) {
    const v = getViewerInstance();
    if (!v) { console.warn("Molstar not ready"); return; }
    try {
        v.visual.select({ data: [{ residue_number: parseInt(residueNum), color: { r: 255, g: 91, b: 127 } }], focus: true });
        DOM.hudResidue.textContent = `Residue #${residueNum}`;
        DOM.hudVariant.textContent = variantLabel || "Pathogenic Mutation Active";
        DOM.inspectorOverlay.classList.remove('hidden');
        // Trigger local-AI translation if enabled
        if (ollamaConfig.enabled && variantLabel) {
            const ctx = `Gene ${currentGeneData?.geneSymbol || ''}, disease ${currentGeneData?.diseaseName || ''}, variant ${variantLabel} at amino-acid position ${residueNum}.`;
            renderAITranslation(ctx);
        } else if (DOM.aiTranslationBox) {
            DOM.aiTranslationBox.classList.add('hidden');
        }
    } catch (e) { console.error("Molstar select error:", e); }
}
function clearHighlight() {
    document.querySelectorAll('#variants-table-body tr').forEach(r => r.classList.remove('active-row'));
    if (DOM.inspectorOverlay) DOM.inspectorOverlay.classList.add('hidden');
    const v = getViewerInstance();
    if (v) v.visual.reset({ camera: true, theme: true });
}

// ==========================================================================
// jsPDF loader (lazy CDN, no blocking startup)
// ==========================================================================
function loadJsPDF() {
    return new Promise((resolve, reject) => {
        if (window.jspdf) return resolve();
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('jsPDF CDN load failed'));
        document.head.appendChild(s);
    });
}

// ==========================================================================
// Stats Panel (overview)
// ==========================================================================
function renderStatsPanel() {
    if (!DOM.statsPanel) return;
    const total = diseaseCatalog.length;
    let treatable = 0;
    const systemTally = {};
    let totalTrials = 0;

    diseaseCatalog.forEach(d => {
        if (d.treatment === 'approved') treatable++;
        d.systems.forEach(s => { systemTally[s] = (systemTally[s] || 0) + 1; });
    });
    // count trials from featured database (live data would extend this)
    Object.values(featuredDatabase).forEach(g => { totalTrials += (g.trials || []).length; });

    const treatPct = Math.round((treatable / total) * 100);

    if (DOM.statsTotalDiseases) DOM.statsTotalDiseases.textContent = total;
    if (DOM.statsActiveTrials) DOM.statsActiveTrials.textContent = totalTrials;
    if (DOM.statsSystems) DOM.statsSystems.textContent = Object.keys(systemTally).length;
    if (DOM.statsTreatable) DOM.statsTreatable.textContent = `${treatPct}%`;

    if (DOM.statsSystemChart) {
        // simple horizontal bar chart sorted by frequency
        const max = Math.max(...Object.values(systemTally));
        const bars = Object.keys(systemTally)
            .sort((a, b) => systemTally[b] - systemTally[a])
            .slice(0, 8)
            .map(s => {
                const pct = Math.round((systemTally[s] / max) * 100);
                return `<div class="stat-bar-row">
                    <span class="stat-bar-label">${escapeHTML(t('system.' + s))}</span>
                    <div class="stat-bar-track"><div class="stat-bar-fill" style="width:${pct}%"></div></div>
                    <span class="stat-bar-value">${systemTally[s]}</span>
                </div>`;
            }).join('');
        DOM.statsSystemChart.innerHTML = bars;
    }
}

// ==========================================================================
// Disease Browser (modal w/ filter + search + sort)
// ==========================================================================
let browseState = {
    search: '',
    system: '',
    treatment: '',
    onset: '',
    inherit: '',
    sort: 'featured',
    favoritesOnly: false
};

function openBrowse() {
    if (!DOM.browseModal) return;
    DOM.browseModal.classList.add('open');
    DOM.browseModal.setAttribute('aria-hidden', 'false');
    renderBrowse();
    setTimeout(() => DOM.browseSearch && DOM.browseSearch.focus(), 100);
}
function closeBrowse() {
    if (!DOM.browseModal) return;
    DOM.browseModal.classList.remove('open');
    DOM.browseModal.setAttribute('aria-hidden', 'true');
}

function getFilteredCatalog() {
    const s = browseState.search.trim().toLowerCase();
    let list = diseaseCatalog.filter(d => {
        if (browseState.favoritesOnly && !isFavorite(d.gene)) return false;
        if (browseState.system && !d.systems.includes(browseState.system)) return false;
        if (browseState.treatment && d.treatment !== browseState.treatment) return false;
        if (browseState.onset && d.onset !== browseState.onset && d.onset !== 'any') return false;
        if (browseState.inherit && d.inheritance !== browseState.inherit) return false;
        if (s) {
            const hay = `${d.name} ${d.nameDE} ${d.gene} ${d.short} ${d.shortEN || ''} ${d.systems.join(' ')}`.toLowerCase();
            if (!hay.includes(s)) return false;
        }
        return true;
    });
    switch (browseState.sort) {
        case 'name':
            list.sort((a, b) => (currentLang === 'de' ? a.nameDE : a.name).localeCompare(currentLang === 'de' ? b.nameDE : b.name));
            break;
        case 'difficulty':
            list.sort((a, b) => b.difficulty - a.difficulty);
            break;
        case 'prevalence':
            list.sort((a, b) => prevalenceRank(a.prevalence) - prevalenceRank(b.prevalence));
            break;
        case 'featured':
        default:
            list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
}

function prevalenceRank(p) {
    if (!p) return 9e9;
    const m = p.match(/1[:\s]+([\d.,]+)/);
    if (!m) return 9e9;
    return parseFloat(m[1].replace(/[.,]/g, ''));
}

function renderBrowse() {
    if (!DOM.browseGrid) return;
    const list = getFilteredCatalog();
    if (DOM.browseCount) DOM.browseCount.textContent = list.length;
    if (list.length === 0) {
        DOM.browseGrid.innerHTML = '';
        if (DOM.browseEmpty) DOM.browseEmpty.classList.remove('hidden');
        return;
    }
    if (DOM.browseEmpty) DOM.browseEmpty.classList.add('hidden');

    DOM.browseGrid.innerHTML = list.map(d => {
        const dispName = currentLang === 'de' ? d.nameDE : d.name;
        const dispShort = currentLang === 'de' ? d.short : (d.shortEN || d.short);
        const systemChips = d.systems.map(s => `<span class="catalog-system-chip">${escapeHTML(t('system.' + s))}</span>`).join('');
        const stars = '★'.repeat(d.difficulty) + '☆'.repeat(5 - d.difficulty);
        const treatLabel = t('treatment.' + d.treatment);
        const treatClass = `treat-${d.treatment.replace(/[^a-z]/g, '')}`;
        const featured = d.featured ? `<span class="catalog-featured">${t('browse.featuredBadge')}</span>` : '';
        const fav = isFavorite(d.gene);
        const favIcon = fav ? 'star' : 'star_outline';
        const favTitle = fav ? t('favorites.remove') : t('favorites.add');
        const inCompare = compareSet.includes(d.gene);
        const compareTitle = inCompare ? (currentLang === 'de' ? 'Aus Vergleich entfernen' : 'Remove from compare') : (currentLang === 'de' ? 'Zum Vergleich hinzufügen' : 'Add to compare');
        return `
            <div class="catalog-card ${inCompare ? 'in-compare' : ''}" data-gene="${escapeHTML(d.gene)}" data-name="${escapeHTML(d.name)}" role="button" tabindex="0">
                <button class="catalog-fav" data-gene="${escapeHTML(d.gene)}" title="${escapeHTML(favTitle)}" aria-label="${escapeHTML(favTitle)}">
                    <span class="material-symbols-outlined ${fav ? 'starred' : ''}">${favIcon}</span>
                </button>
                <button class="catalog-compare" data-gene="${escapeHTML(d.gene)}" title="${escapeHTML(compareTitle)}" aria-label="${escapeHTML(compareTitle)}">
                    <span class="material-symbols-outlined ${inCompare ? 'in' : ''}">${inCompare ? 'check_box' : 'add_box'}</span>
                </button>
                <div class="catalog-head">
                    <div class="catalog-title">
                        <strong>${escapeHTML(dispName)}</strong>
                        <span class="catalog-gene">${escapeHTML(d.gene)}</span>
                    </div>
                    ${featured}
                </div>
                <p class="catalog-short">${escapeHTML(dispShort)}</p>
                <div class="catalog-systems">${systemChips}</div>
                <div class="catalog-meta">
                    <span class="catalog-meta-item"><span class="material-symbols-outlined">groups</span>${escapeHTML(d.prevalence)}</span>
                    <span class="catalog-meta-item"><span class="material-symbols-outlined">child_care</span>${escapeHTML(t('onset.' + d.onset))}</span>
                    <span class="catalog-meta-item"><span class="material-symbols-outlined">family_restroom</span>${escapeHTML(t('inherit.' + d.inheritance))}</span>
                </div>
                <div class="catalog-foot">
                    <span class="catalog-treat ${treatClass}">${escapeHTML(treatLabel)}</span>
                    <span class="catalog-difficulty" title="${escapeHTML(t('difficulty'))}">${stars}</span>
                </div>
            </div>
        `;
    }).join('');

    // Card opens disease in patient mode (laypeople-first UX)
    DOM.browseGrid.querySelectorAll('.catalog-card').forEach(card => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('.catalog-fav')) return;
            const g = card.getAttribute('data-gene');
            DOM.geneSearch.value = g;
            closeBrowse();
            // Auto-switch to patient mode for layperson-friendly experience
            setMode('patient');
            performAnalysis(g);
        });
        card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
        });
    });
    DOM.browseGrid.querySelectorAll('.catalog-fav').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleFavorite(btn.getAttribute('data-gene'));
        });
    });
    DOM.browseGrid.querySelectorAll('.catalog-compare').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleCompare(btn.getAttribute('data-gene'));
        });
    });
}

function populateBrowseFilters() {
    if (!DOM.browseFilterSystem) return;
    const systems = Array.from(new Set(diseaseCatalog.flatMap(d => d.systems))).sort();
    DOM.browseFilterSystem.innerHTML = `<option value="">${t('browse.allSystems')}</option>` +
        systems.map(s => `<option value="${s}">${escapeHTML(t('system.' + s))}</option>`).join('');
    DOM.browseFilterTreatment.innerHTML = `<option value="">${t('browse.allTreatment')}</option>` +
        ['approved', 'in-research', 'supportive'].map(v => `<option value="${v}">${escapeHTML(t('treatment.' + v))}</option>`).join('');
    DOM.browseFilterOnset.innerHTML = `<option value="">${t('browse.allOnset')}</option>` +
        ['infant', 'child', 'teen', 'adult', 'elderly'].map(v => `<option value="${v}">${escapeHTML(t('onset.' + v))}</option>`).join('');
    DOM.browseFilterInherit.innerHTML = `<option value="">${t('browse.allInherit')}</option>` +
        ['autosomal-dominant', 'autosomal-recessive', 'x-linked', 'mitochondrial'].map(v => `<option value="${v}">${escapeHTML(t('inherit.' + v))}</option>`).join('');
    DOM.browseSort.innerHTML =
        `<option value="featured">${t('browse.sort.featured')}</option>` +
        `<option value="name">${t('browse.sort.name')}</option>` +
        `<option value="prevalence">${t('browse.sort.prevalence')}</option>` +
        `<option value="difficulty">${t('browse.sort.difficulty')}</option>`;
}

// ==========================================================================
// Story-Modus — guided 5-step 3D tour per disease
// ==========================================================================
let storyState = {
    gene: null,
    steps: [],
    idx: 0,
    autoTimer: null,
    autoPlay: false,
    narrate: localStorage.getItem('aura_story_narrate') !== '0'   // default ON
};

function geneHasStory(gene) {
    return !!storyScripts[gene];
}

function refreshStoryCTA() {
    if (!DOM.storyModeBtn) return;
    const gene = currentGeneData?.geneSymbol;
    if (gene && geneHasStory(gene) && currentMode === 'patient') {
        DOM.storyModeBtn.hidden = false;
    } else {
        DOM.storyModeBtn.hidden = true;
    }
}

function openStory() {
    const gene = currentGeneData?.geneSymbol;
    if (!gene || !geneHasStory(gene)) return;
    storyState.gene = gene;
    storyState.steps = storyScripts[gene];
    storyState.idx = 0;
    DOM.storyOverlay?.classList.remove('hidden');
    renderStoryStep();
}
function closeStory() {
    DOM.storyOverlay?.classList.add('hidden');
    stopStoryAutoPlay();
    // Cancel any in-flight narration
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    clearHighlight();
}

function renderStoryStep() {
    if (!storyState.steps.length) return;
    const i = storyState.idx;
    const step = storyState.steps[i];
    const localePack = step[currentLang] || step.en || step.de;
    if (!localePack) return;

    DOM.storyTitle.textContent = localePack.t;
    DOM.storyText.textContent = localePack.x;
    DOM.storyStepCounter.textContent = `${i + 1} / ${storyState.steps.length}`;

    // Progress dots
    if (DOM.storyProgressDots) {
        DOM.storyProgressDots.innerHTML = storyState.steps
            .map((_, j) => `<span class="story-dot ${j === i ? 'active' : j < i ? 'done' : ''}"></span>`)
            .join('');
    }
    // Prev disabled on first, Next disabled on last
    DOM.storyPrevBtn.disabled = i === 0;
    DOM.storyNextBtn.disabled = i === storyState.steps.length - 1;

    // Execute step action on the 3D viewer
    executeStoryAction(step);

    // Narrate
    if (storyState.narrate) {
        speakText(localePack.t + '. ' + localePack.x);
    }
}

function executeStoryAction(step) {
    switch (step.action) {
        case 'reset':
        case 'introduce':
            clearHighlight();
            break;
        case 'zoomResidue':
            if (step.residue) {
                // Find best variant label for this residue
                const v = (currentGeneData?.variants || []).find(x => x.residue === step.residue);
                const label = v ? v.clinical : `Residue ${step.residue}`;
                setTimeout(() => highlightResidue(step.residue, label), 350);
            }
            break;
        case 'showCompounds':
            // Brief flash on Therapeutics panel
            flashElement(document.querySelector('.panel-right'));
            break;
        case 'showTrials':
            flashElement(document.getElementById('trials-container'));
            break;
        case 'stay':
        default:
            break;
    }
}

function flashElement(el) {
    if (!el) return;
    el.classList.add('story-flash');
    setTimeout(() => el.classList.remove('story-flash'), 1600);
}

function storyNext() {
    if (storyState.idx < storyState.steps.length - 1) {
        storyState.idx++;
        renderStoryStep();
    } else {
        stopStoryAutoPlay();
        toast(currentLang === 'de' ? 'Geschichte zu Ende.' : 'Story complete.', 'success', 2500);
    }
}
function storyPrev() {
    if (storyState.idx > 0) {
        storyState.idx--;
        renderStoryStep();
    }
}
function startStoryAutoPlay() {
    stopStoryAutoPlay();
    storyState.autoPlay = true;
    DOM.storyPlaypauseBtn.querySelector('span.material-symbols-outlined').textContent = 'pause';
    storyState.autoTimer = setInterval(() => {
        if (storyState.idx >= storyState.steps.length - 1) {
            stopStoryAutoPlay();
            return;
        }
        storyNext();
    }, 9000); // 9s per step → time to listen + read
}
function stopStoryAutoPlay() {
    if (storyState.autoTimer) {
        clearInterval(storyState.autoTimer);
        storyState.autoTimer = null;
    }
    storyState.autoPlay = false;
    if (DOM.storyPlaypauseBtn) {
        DOM.storyPlaypauseBtn.querySelector('span.material-symbols-outlined').textContent = 'play_arrow';
    }
}
function toggleStoryAutoPlay() {
    if (storyState.autoPlay) stopStoryAutoPlay();
    else startStoryAutoPlay();
}
function toggleStoryNarrate() {
    storyState.narrate = !storyState.narrate;
    localStorage.setItem('aura_story_narrate', storyState.narrate ? '1' : '0');
    DOM.storyNarrateBtn?.classList.toggle('active', storyState.narrate);
    // Re-narrate current step if turned on; cancel if off
    if (storyState.narrate) {
        const step = storyState.steps[storyState.idx];
        const lp = step[currentLang] || step.en || step.de;
        if (lp) speakText(lp.t + '. ' + lp.x);
    } else if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }
}

// ==========================================================================
// Immersive 3D-Viewer Mode (custom, replaces broken browser fullscreen)
// ==========================================================================
function toggleImmersiveMode() {
    const on = document.body.classList.toggle('immersive');
    document.body.setAttribute('data-immersive', on ? '1' : '0');
    if (on) {
        // Inject exit-button if not present
        if (!document.getElementById('immersive-exit')) {
            const exit = document.createElement('button');
            exit.id = 'immersive-exit';
            exit.className = 'immersive-exit';
            exit.title = 'Exit immersive mode (Esc)';
            exit.innerHTML = '<span class="material-symbols-outlined">close</span>';
            exit.addEventListener('click', toggleImmersiveMode);
            document.body.appendChild(exit);
        }
        // Hint-toast
        toast(currentLang === 'de' ? 'Vollbild — Esc zum Schließen' : 'Immersive mode — Esc to exit', 'info', 2500);
    } else {
        const exit = document.getElementById('immersive-exit');
        if (exit) exit.remove();
    }
}

// Hook into Escape: if immersive mode active, exit it (only when no modal is open)
document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (document.body.classList.contains('immersive')) {
        // Defer to next tick so other modals can intercept first
        setTimeout(() => {
            const browseOpen = DOM.browseModal?.classList.contains('open');
            const glossaryOpen = DOM.glossaryModal?.classList.contains('open');
            const shortcutsOpen = DOM.shortcutsModal?.classList.contains('open');
            const compareOpen = DOM.compareModal?.classList.contains('open');
            const ollamaOpen = document.getElementById('ollama-settings-modal')?.classList.contains('open');
            const stlOpen = document.getElementById('stl-options-modal')?.classList.contains('open');
            if (!browseOpen && !glossaryOpen && !shortcutsOpen && !compareOpen && !ollamaOpen && !stlOpen) {
                toggleImmersiveMode();
            }
        }, 0);
    }
});

// ==========================================================================
// Compare View — Healthy ↔ Diseased (Modul 4)
// ==========================================================================
let compareViewState = null; // null = off, 'healthy', 'diseased'
let compareAutoFlipTimer = null;

function showCompareView() {
    if (!currentGeneData) {
        toast(currentLang === 'de' ? 'Bitte erst ein Gen laden.' : 'Load a gene first.', 'info');
        return;
    }
    if (!DOM.compareViewOverlay) return;
    DOM.compareViewOverlay.classList.remove('hidden');
    setCompareView('healthy');
}
function hideCompareView() {
    if (!DOM.compareViewOverlay) return;
    DOM.compareViewOverlay.classList.add('hidden');
    stopAutoFlip();
    compareViewState = null;
}
function toggleCompareView() {
    if (DOM.compareViewOverlay?.classList.contains('hidden')) showCompareView();
    else hideCompareView();
}

function setCompareView(mode) {
    compareViewState = mode;
    document.querySelectorAll('.compare-seg').forEach(b => {
        b.classList.toggle('active', b.getAttribute('data-cmp') === mode);
    });
    const banner = DOM.compareBanner;
    if (!currentGeneData) return;
    const variant = (currentGeneData.variants || []).find(v => v.residue);
    if (mode === 'healthy') {
        // Clear highlight, show wild-type banner
        clearHighlight();
        if (banner) banner.innerHTML = `<span class="material-symbols-outlined">favorite</span>
            <span><strong>${escapeHTML(currentLang === 'de' ? 'Gesund' : 'Healthy')}</strong> — ${escapeHTML(currentLang === 'de' ? 'Wildtyp-Protein wie es funktioniert' : 'Wild-type — how the protein normally works')}</span>`;
        banner.className = 'compare-banner healthy visible';
    } else if (mode === 'diseased') {
        if (variant) {
            highlightResidue(variant.residue, variant.clinical);
        }
        if (banner) banner.innerHTML = `<span class="material-symbols-outlined">warning</span>
            <span><strong>${escapeHTML(currentLang === 'de' ? 'Mit Mutation' : 'With mutation')}</strong> — ${escapeHTML(variant ? variant.clinical : (currentLang === 'de' ? 'keine pathogene Variante hinterlegt' : 'no pathogenic variant on file'))}</span>`;
        banner.className = 'compare-banner diseased visible';
    }
}

function startAutoFlip() {
    stopAutoFlip();
    compareAutoFlipTimer = setInterval(() => {
        if (!compareViewState) return;
        setCompareView(compareViewState === 'healthy' ? 'diseased' : 'healthy');
    }, 3000);
    DOM.compareAutoflipBtn?.classList.add('active');
}
function stopAutoFlip() {
    if (compareAutoFlipTimer) {
        clearInterval(compareAutoFlipTimer);
        compareAutoFlipTimer = null;
    }
    DOM.compareAutoflipBtn?.classList.remove('active');
}
function toggleAutoFlip() {
    if (compareAutoFlipTimer) stopAutoFlip();
    else startAutoFlip();
}

// ==========================================================================
// STL-Export (Modul 3 — print-ready protein model)
// Lazy-loads Three.js + STLExporter from CDN, fetches PDB, builds reinforced
// sphere-and-stick mesh, exports to .stl
// ==========================================================================
let stlOptions = {
    atomRadius: 1.1,
    bondThickness: 0.55,
    includeSticks: true
};

async function ensureThreeJS() {
    if (window.THREE && window.THREE.STLExporter) return true;
    if (window.THREE && window.__stlExporterLoaded) return true;
    return new Promise((resolve, reject) => {
        const loadScript = (src) => new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = () => res();
            s.onerror = () => rej(new Error('Failed to load ' + src));
            document.head.appendChild(s);
        });
        // Pinned to r0.140 — last version with examples/js/ UMD-style STLExporter
        loadScript('https://unpkg.com/three@0.140.0/build/three.min.js')
            .then(() => loadScript('https://unpkg.com/three@0.140.0/examples/js/exporters/STLExporter.js'))
            .then(() => { window.__stlExporterLoaded = true; resolve(true); })
            .catch(e => reject(e));
    });
}

async function fetchPDBFile(pdbId) {
    const url = `https://files.rcsb.org/download/${pdbId.toLowerCase()}.pdb`;
    const r = await fetch(url);
    if (!r.ok) throw new Error('PDB download failed: ' + r.status);
    return await r.text();
}

// Minimal PDB ATOM/HETATM parser — extracts atoms with positions + chain + residue
function parsePDB(text) {
    const atoms = [];
    const lines = text.split('\n');
    for (const line of lines) {
        if (!line.startsWith('ATOM  ') && !line.startsWith('HETATM')) continue;
        try {
            const name = line.slice(12, 16).trim();
            const altLoc = line.slice(16, 17).trim();
            if (altLoc && altLoc !== 'A') continue; // skip alternate locations except A
            const resName = line.slice(17, 20).trim();
            const chain = line.slice(21, 22).trim() || 'A';
            const resSeq = parseInt(line.slice(22, 26).trim(), 10);
            const x = parseFloat(line.slice(30, 38));
            const y = parseFloat(line.slice(38, 46));
            const z = parseFloat(line.slice(46, 54));
            const element = (line.slice(76, 78).trim() || name.replace(/[^A-Z]/g, '').slice(0, 1)).trim();
            if (isNaN(x) || isNaN(y) || isNaN(z)) continue;
            atoms.push({ name, resName, chain, resSeq, x, y, z, element });
        } catch {}
    }
    return atoms;
}

// Build a reinforced sphere-and-stick mesh from atoms
function buildPrintableMesh(atoms, opts) {
    const THREE = window.THREE;
    const group = new THREE.Group();
    const radius = opts.atomRadius;
    const bondR = opts.bondThickness;
    const sphereGeo = new THREE.SphereGeometry(radius, 12, 12);

    // Backbone-only filter for protein: alpha-carbons (CA) form the chain
    const backbone = atoms.filter(a => a.name === 'CA' || a.name === 'P');
    // If no backbone (small molecules), fall back to all atoms
    const drawAtoms = backbone.length >= 3 ? backbone : atoms;

    // Add spheres for each atom
    drawAtoms.forEach(a => {
        const m = new THREE.Mesh(sphereGeo);
        m.position.set(a.x, a.y, a.z);
        group.add(m);
    });

    // Add reinforced bond cylinders between consecutive backbone atoms in same chain
    if (opts.includeSticks) {
        const cylGeo = new THREE.CylinderGeometry(bondR, bondR, 1, 10, 1, false);
        for (let i = 0; i < drawAtoms.length - 1; i++) {
            const a = drawAtoms[i], b = drawAtoms[i + 1];
            if (a.chain !== b.chain) continue;
            // Skip giant jumps (chain breaks)
            const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (dist > 6) continue; // typical CA-CA ~3.8Å, P-P ~6Å → drop disconnected jumps
            const m = new THREE.Mesh(cylGeo);
            m.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
            // Orient cylinder along direction
            const dir = new THREE.Vector3(dx, dy, dz).normalize();
            const up = new THREE.Vector3(0, 1, 0);
            const q = new THREE.Quaternion().setFromUnitVectors(up, dir);
            m.quaternion.copy(q);
            m.scale.set(1, dist, 1);
            group.add(m);
        }
    }

    return group;
}

async function generateSTL() {
    if (!currentGeneData || !currentPdbId) {
        toast(currentLang === 'de' ? 'Bitte erst ein Gen laden.' : 'Load a gene first.', 'info');
        return;
    }
    if (DOM.stlProgress) DOM.stlProgress.classList.remove('hidden');
    if (DOM.stlGenerateBtn) DOM.stlGenerateBtn.disabled = true;

    try {
        await ensureThreeJS();
        const pdbText = await fetchPDBFile(currentPdbId);
        const atoms = parsePDB(pdbText);
        if (atoms.length === 0) throw new Error('No atoms parsed from PDB');
        const group = buildPrintableMesh(atoms, stlOptions);

        const exporter = new window.THREE.STLExporter();
        const stl = exporter.parse(group);

        const blob = new Blob([stl], { type: 'application/sla' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AuraGene-${currentGeneData.geneSymbol}-${currentPdbId}-print.stl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast(currentLang === 'de' ? `STL fertig: ${atoms.length} Atome → druckfertig` : `STL ready: ${atoms.length} atoms → print-ready`, 'success', 5000);
    } catch (e) {
        console.warn('STL export failed:', e);
        toast(currentLang === 'de' ? `STL-Export fehlgeschlagen: ${e.message}` : `STL export failed: ${e.message}`, 'error', 5000);
    } finally {
        if (DOM.stlProgress) DOM.stlProgress.classList.add('hidden');
        if (DOM.stlGenerateBtn) DOM.stlGenerateBtn.disabled = false;
    }
}

function openSTLModal() {
    const modal = document.getElementById('stl-options-modal');
    if (!modal) return;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
}
function closeSTLModal() {
    const modal = document.getElementById('stl-options-modal');
    if (modal) { modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true'); }
}

// ==========================================================================
// Ollama Local-AI Integration (Modul 2 — privacy-first medical translation)
// ==========================================================================
let ollamaConfig = {
    enabled: localStorage.getItem('aura_ollama_enabled') === '1',
    url: localStorage.getItem('aura_ollama_url') || 'http://localhost:11434',
    model: localStorage.getItem('aura_ollama_model') || 'llama3'
};

function saveOllamaConfig() {
    localStorage.setItem('aura_ollama_enabled', ollamaConfig.enabled ? '1' : '0');
    localStorage.setItem('aura_ollama_url', ollamaConfig.url);
    localStorage.setItem('aura_ollama_model', ollamaConfig.model);
}

function setOllamaStatus(state, msg) {
    if (!DOM.ollamaStatus) return;
    const map = {
        ok: '🟢',
        off: '⚫',
        warn: '🟡',
        err: '🔴'
    };
    DOM.ollamaStatus.innerHTML = `${map[state] || '⚫'} ${escapeHTML(msg)}`;
}

async function testOllamaConnection() {
    const url = (DOM.ollamaUrlInput?.value || ollamaConfig.url).replace(/\/+$/, '');
    setOllamaStatus('warn', currentLang === 'de' ? 'Verbinde…' : 'Connecting…');
    try {
        const r = await fetch(url + '/api/tags', { method: 'GET' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const models = (data.models || []).map(m => m.name || m.model);
        if (models.length === 0) {
            setOllamaStatus('warn', currentLang === 'de' ? 'Verbunden, aber kein Modell installiert. Tipp: ollama pull llama3' : 'Connected, but no model installed. Try: ollama pull llama3');
            return false;
        }
        // Persist URL + try to pick a sensible model
        ollamaConfig.url = url;
        const preferred = models.find(m => /llama3|llama-3|mistral|gemma|qwen|med/i.test(m)) || models[0];
        ollamaConfig.model = preferred;
        saveOllamaConfig();
        setOllamaStatus('ok', currentLang === 'de' ? `Verbunden · Modell: ${preferred}` : `Connected · model: ${preferred}`);
        toast(currentLang === 'de' ? `Lokale KI verbunden (${preferred})` : `Local AI connected (${preferred})`, 'success');
        return true;
    } catch (e) {
        console.warn('Ollama test failed:', e);
        setOllamaStatus('err', currentLang === 'de' ? 'Nicht erreichbar — läuft Ollama auf Port 11434?' : 'Not reachable — is Ollama running on port 11434?');
        return false;
    }
}

async function fetchLocalTranslation(clinicalSnippet, opts = {}) {
    if (!ollamaConfig.enabled) {
        return { ok: false, reason: 'disabled' };
    }
    if (!clinicalSnippet) return { ok: false, reason: 'empty' };
    const url = ollamaConfig.url.replace(/\/+$/, '') + '/api/generate';
    const langInstruction = currentLang === 'de'
        ? 'Antworte ausschließlich auf Deutsch. Drei einfache Sätze, Alltagssprache, kein Fachjargon. Keine Disclaimer, keine Aufzählungspunkte.'
        : 'Reply only in English. Three plain everyday sentences, no medical jargon. No disclaimers, no bullet points.';
    const prompt = (currentLang === 'de'
        ? 'Du bist ein freundlicher medizinischer Übersetzer. Übersetze diesen genetischen Befund für einen Laien.'
        : 'You are a friendly medical translator. Translate this genetic finding for a layperson.')
        + '\n' + langInstruction
        + '\n\nBefund: ' + clinicalSnippet;
    try {
        const r = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                model: ollamaConfig.model,
                prompt,
                stream: false,
                options: { temperature: 0.3, num_predict: 220 }
            })
        });
        if (!r.ok) {
            return { ok: false, reason: 'http-' + r.status };
        }
        const data = await r.json();
        const out = (data.response || '').trim();
        if (!out) return { ok: false, reason: 'empty-response' };
        return { ok: true, text: out };
    } catch (e) {
        console.warn('Ollama translate failed:', e);
        return { ok: false, reason: 'network' };
    }
}

async function renderAITranslation(clinicalSnippet) {
    if (!DOM.aiTranslationBox) return;
    if (!ollamaConfig.enabled) {
        DOM.aiTranslationBox.classList.add('hidden');
        return;
    }
    DOM.aiTranslationBox.classList.remove('hidden');
    DOM.aiTranslationSpinner?.classList.remove('hidden');
    DOM.aiTranslationContent.textContent = currentLang === 'de' ? 'Lokale KI denkt nach…' : 'Local AI is thinking…';
    const result = await fetchLocalTranslation(clinicalSnippet);
    DOM.aiTranslationSpinner?.classList.add('hidden');
    if (result.ok) {
        DOM.aiTranslationContent.textContent = result.text;
    } else {
        const msg = result.reason === 'disabled'
            ? (currentLang === 'de' ? 'Lokale KI deaktiviert — klicke das Roboter-Icon oben um sie zu aktivieren.' : 'Local AI disabled — click the robot icon in the header to enable.')
            : (currentLang === 'de' ? 'Lokale KI-Übersetzung nicht aktiv.' : 'Local AI translation not active.');
        DOM.aiTranslationContent.textContent = msg;
    }
}

function openOllamaSettings() {
    const modal = document.getElementById('ollama-settings-modal');
    if (!modal) return;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    if (DOM.ollamaEnabledToggle) DOM.ollamaEnabledToggle.checked = ollamaConfig.enabled;
    if (DOM.ollamaUrlInput) DOM.ollamaUrlInput.value = ollamaConfig.url;
}
function closeOllamaSettings() {
    const modal = document.getElementById('ollama-settings-modal');
    if (modal) { modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true'); }
}

// ==========================================================================
// Compare Mode (up to 3 diseases side-by-side)
// ==========================================================================
let compareSet = [];
const COMPARE_MAX = 3;

function toggleCompare(gene) {
    if (!gene) return;
    if (compareSet.includes(gene)) {
        compareSet = compareSet.filter(g => g !== gene);
    } else {
        if (compareSet.length >= COMPARE_MAX) {
            toast(`Max ${COMPARE_MAX} ${currentLang === 'de' ? 'Erkrankungen vergleichbar' : 'diseases at a time'}`, 'info');
            return;
        }
        compareSet.push(gene);
    }
    if (DOM.compareCount) DOM.compareCount.textContent = compareSet.length;
    if (DOM.compareOpenBtn) DOM.compareOpenBtn.classList.toggle('has-items', compareSet.length > 0);
    // Re-render browse so cards reflect compare-state
    if (DOM.browseGrid && DOM.browseGrid.children.length > 0) renderBrowse();
}

function openCompare() {
    if (!DOM.compareModal) return;
    if (compareSet.length === 0) {
        toast(currentLang === 'de' ? 'Wähle erst Erkrankungen aus der Bibliothek (Vergleich-Button auf jeder Karte)' : 'Select diseases first from the library (compare button on each card)', 'info', 5000);
        return;
    }
    DOM.compareModal.classList.add('open');
    DOM.compareModal.setAttribute('aria-hidden', 'false');
    renderCompare();
}
function closeCompare() {
    if (!DOM.compareModal) return;
    DOM.compareModal.classList.remove('open');
    DOM.compareModal.setAttribute('aria-hidden', 'true');
}

function renderCompare() {
    if (!DOM.compareGrid) return;
    const cols = compareSet.map(gene => {
        const cat = diseaseCatalog.find(d => d.gene === gene);
        const featured = featuredDatabase[gene];
        const name = cat ? (currentLang === 'de' ? cat.nameDE : cat.name) : (featured?.diseaseName || gene);
        const story = pickStory(featured || { geneSymbol: gene, patientStory: null });
        const storyText = story?.short || (cat ? (currentLang === 'de' ? cat.short : (cat.shortEN || cat.short)) : '');
        const systems = cat ? cat.systems.map(s => `<span class="catalog-system-chip">${escapeHTML(t('system.' + s))}</span>`).join('') : '';
        const prevalence = cat?.prevalence || '?';
        const onset = cat ? t('onset.' + cat.onset) : '?';
        const inheritance = cat ? t('inherit.' + cat.inheritance) : '?';
        const treatment = cat ? t('treatment.' + cat.treatment) : '?';
        const difficulty = cat ? '★'.repeat(cat.difficulty) + '☆'.repeat(5 - cat.difficulty) : '';
        return `
        <article class="compare-col">
            <header class="compare-col-head">
                <strong>${escapeHTML(name)}</strong>
                <span class="catalog-gene">${escapeHTML(gene)}</span>
                <button class="compare-remove" data-gene="${escapeHTML(gene)}" title="Remove">
                    <span class="material-symbols-outlined">close</span>
                </button>
            </header>
            <p class="compare-story">${escapeHTML(storyText)}</p>
            <dl class="compare-data">
                <dt>${escapeHTML(currentLang === 'de' ? 'Häufigkeit' : 'Prevalence')}</dt><dd>${escapeHTML(prevalence)}</dd>
                <dt>${escapeHTML(currentLang === 'de' ? 'Beginn' : 'Onset')}</dt><dd>${escapeHTML(onset)}</dd>
                <dt>${escapeHTML(currentLang === 'de' ? 'Vererbung' : 'Inheritance')}</dt><dd>${escapeHTML(inheritance)}</dd>
                <dt>${escapeHTML(currentLang === 'de' ? 'Therapie' : 'Treatment')}</dt><dd>${escapeHTML(treatment)}</dd>
                <dt>${escapeHTML(currentLang === 'de' ? 'Komplexität' : 'Complexity')}</dt><dd class="compare-stars">${difficulty}</dd>
            </dl>
            <div class="compare-systems">${systems}</div>
            <button class="action-btn compare-open-disease" data-gene="${escapeHTML(gene)}">
                <span class="material-symbols-outlined">open_in_new</span>
                <span>${escapeHTML(currentLang === 'de' ? 'Im Dashboard öffnen' : 'Open in dashboard')}</span>
            </button>
        </article>`;
    }).join('');
    DOM.compareGrid.innerHTML = cols;
    DOM.compareGrid.querySelectorAll('.compare-remove').forEach(b => b.addEventListener('click', () => { toggleCompare(b.getAttribute('data-gene')); renderCompare(); }));
    DOM.compareGrid.querySelectorAll('.compare-open-disease').forEach(b => b.addEventListener('click', () => {
        const g = b.getAttribute('data-gene');
        DOM.geneSearch.value = g;
        closeCompare();
        setMode('patient');
        performAnalysis(g);
    }));
    if (DOM.compareCount) DOM.compareCount.textContent = compareSet.length;
}

// ==========================================================================
// Reading-level toggle (Simple ↔ Detailed)
// ==========================================================================
let readingLevel = localStorage.getItem('aura_reading') || 'detailed';
function setReadingLevel(level) {
    readingLevel = level === 'simple' ? 'simple' : 'detailed';
    localStorage.setItem('aura_reading', readingLevel);
    if (DOM.readingToggleSimple) DOM.readingToggleSimple.classList.toggle('active', readingLevel === 'simple');
    if (DOM.readingToggleDetailed) DOM.readingToggleDetailed.classList.toggle('active', readingLevel === 'detailed');
    document.body.setAttribute('data-reading', readingLevel);
}

// ==========================================================================
// Theme toggle (dark/light)
// ==========================================================================
let currentTheme = localStorage.getItem('aura_theme') || 'dark';
function setTheme(theme) {
    currentTheme = theme === 'light' ? 'light' : 'dark';
    localStorage.setItem('aura_theme', currentTheme);
    document.body.setAttribute('data-theme', currentTheme);
    if (DOM.themeToggle) {
        const icon = DOM.themeToggle.querySelector('span.material-symbols-outlined');
        if (icon) icon.textContent = currentTheme === 'light' ? 'light_mode' : 'dark_mode';
    }
}

// ==========================================================================
// 3D Viewer Screenshot download
// ==========================================================================
function downloadStructureImage() {
    const v = getViewerInstance();
    if (!v || !v.canvas) {
        toast('3D viewer not ready', 'error');
        return;
    }
    try {
        const canvas = document.querySelector('pdbe-molstar canvas');
        if (!canvas) { toast('Canvas not found', 'error'); return; }
        const dataURL = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        a.download = `AuraGene-${(currentGeneData?.geneSymbol || 'structure')}-${currentPdbId}.png`;
        a.click();
        toast(currentLang === 'de' ? 'Strukturbild gespeichert.' : 'Structure image saved.', 'success');
    } catch (e) {
        toast(t('toast.error'), 'error');
    }
}

// ==========================================================================
// Favorites + History (persist via localStorage)
// ==========================================================================
let favorites = [];
let viewHistory = [];

function loadPersistedState() {
    try { favorites = JSON.parse(localStorage.getItem('aura_favorites') || '[]'); } catch { favorites = []; }
    try { viewHistory = JSON.parse(localStorage.getItem('aura_history') || '[]'); } catch { viewHistory = []; }
}
function persistFavorites() { localStorage.setItem('aura_favorites', JSON.stringify(favorites)); }
function persistHistory() { localStorage.setItem('aura_history', JSON.stringify(viewHistory.slice(0, 20))); }

function isFavorite(gene) { return favorites.includes(gene); }
function toggleFavorite(gene) {
    if (!gene) return;
    if (isFavorite(gene)) {
        favorites = favorites.filter(g => g !== gene);
        toast(t('favorites.remove'), 'info');
    } else {
        favorites.push(gene);
        toast(t('favorites.add'), 'success');
    }
    persistFavorites();
    renderFavoritesPanel();
    if (DOM.browseGrid && DOM.browseGrid.children.length > 0) renderBrowse();
}

function pushHistory(gene) {
    if (!gene) return;
    viewHistory = [gene, ...viewHistory.filter(g => g !== gene)].slice(0, 20);
    persistHistory();
    renderHistoryPanel();
}

function renderFavoritesPanel() {
    if (!DOM.favoritesList) return;
    if (favorites.length === 0) {
        DOM.favoritesList.innerHTML = `<p class="empty-line">${escapeHTML(t('favorites.empty'))}</p>`;
        return;
    }
    DOM.favoritesList.innerHTML = favorites.map(g => {
        const cat = diseaseCatalog.find(d => d.gene === g);
        const name = cat ? (currentLang === 'de' ? cat.nameDE : cat.name) : g;
        return `<button class="mini-pill fav-pill" data-gene="${escapeHTML(g)}">
            <span class="material-symbols-outlined">star</span>
            <strong>${escapeHTML(g)}</strong>
            <span>${escapeHTML(name)}</span>
        </button>`;
    }).join('');
    DOM.favoritesList.querySelectorAll('.fav-pill').forEach(p => {
        p.addEventListener('click', () => {
            const g = p.getAttribute('data-gene');
            DOM.geneSearch.value = g;
            performAnalysis(g);
        });
    });
}
function renderHistoryPanel() {
    if (!DOM.historyList) return;
    if (viewHistory.length === 0) {
        DOM.historyList.innerHTML = `<p class="empty-line">${escapeHTML(t('history.empty'))}</p>`;
        return;
    }
    DOM.historyList.innerHTML = viewHistory.slice(0, 8).map(g => {
        const cat = diseaseCatalog.find(d => d.gene === g);
        const name = cat ? (currentLang === 'de' ? cat.nameDE : cat.name) : g;
        return `<button class="mini-pill hist-pill" data-gene="${escapeHTML(g)}">
            <span class="material-symbols-outlined">history</span>
            <strong>${escapeHTML(g)}</strong>
            <span>${escapeHTML(name)}</span>
        </button>`;
    }).join('');
    DOM.historyList.querySelectorAll('.hist-pill').forEach(p => {
        p.addEventListener('click', () => {
            const g = p.getAttribute('data-gene');
            DOM.geneSearch.value = g;
            performAnalysis(g);
        });
    });
}

// ==========================================================================
// Toast notifications (non-blocking — replaces alert())
// ==========================================================================
function toast(message, level = 'info', durationMs = 3500) {
    if (!DOM.toastContainer) {
        // Fallback for early-init calls
        console.log('[toast]', message);
        return;
    }
    const el = document.createElement('div');
    el.className = `toast toast-${level}`;
    el.innerHTML = `
        <span class="material-symbols-outlined">${level === 'success' ? 'check_circle' : level === 'error' ? 'error' : 'info'}</span>
        <span class="toast-text">${escapeHTML(message)}</span>
    `;
    DOM.toastContainer.appendChild(el);
    requestAnimationFrame(() => el.classList.add('visible'));
    setTimeout(() => {
        el.classList.remove('visible');
        setTimeout(() => el.remove(), 300);
    }, durationMs);
}

// ==========================================================================
// Glossary Modal (educational, fully bilingual, searchable)
// ==========================================================================
function openGlossary() {
    if (!DOM.glossaryModal) return;
    DOM.glossaryModal.classList.add('open');
    DOM.glossaryModal.setAttribute('aria-hidden', 'false');
    renderGlossary('');
    setTimeout(() => DOM.glossarySearch && DOM.glossarySearch.focus(), 100);
}
function closeGlossary() {
    if (!DOM.glossaryModal) return;
    DOM.glossaryModal.classList.remove('open');
    DOM.glossaryModal.setAttribute('aria-hidden', 'true');
}
function renderGlossary(filterText) {
    if (!DOM.glossaryList) return;
    const q = (filterText || '').trim().toLowerCase();
    const entries = Object.keys(medicalDictionary)
        .filter(term => {
            if (!q) return true;
            const explain = dictLookup(term) || '';
            return term.toLowerCase().includes(q) || explain.toLowerCase().includes(q);
        })
        .sort();
    if (entries.length === 0) {
        DOM.glossaryList.innerHTML = `<div class="empty-state"><span class="material-symbols-outlined">search_off</span><p>${escapeHTML(t('glossary.empty'))}</p></div>`;
        return;
    }
    DOM.glossaryList.innerHTML = entries.map(term => {
        const explain = dictLookup(term);
        return `<div class="glossary-entry">
            <strong class="glossary-term">${escapeHTML(term)}</strong>
            <span class="glossary-def">${escapeHTML(explain || '')}</span>
        </div>`;
    }).join('');
}

// ==========================================================================
// URL hash deep-linking (#gene=CFTR or #variant=rs113993960)
// ==========================================================================
function parseHashAndLoad() {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return false;
    const params = new URLSearchParams(hash);
    const gene = params.get('gene');
    const variant = params.get('variant');
    const mode = params.get('mode');
    const lang = params.get('lang');
    if (lang && i18n[lang]) setLang(lang);
    if (mode === 'patient' || mode === 'expert') setMode(mode);
    const query = gene || variant;
    if (query) {
        DOM.geneSearch.value = query;
        performAnalysis(query);
        toast(t('toast.deepLink'), 'success');
        return true;
    }
    return false;
}
function updateHashFor(query) {
    if (!query) return;
    const params = new URLSearchParams();
    if (/^rs\d+$/i.test(query)) params.set('variant', query);
    else params.set('gene', query);
    if (currentMode !== 'expert') params.set('mode', currentMode);
    if (currentLang !== 'en') params.set('lang', currentLang);
    history.replaceState(null, '', '#' + params.toString());
}
function copyShareLink() {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => toast(t('toast.copied'), 'success'));
    } else {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); toast(t('toast.copied'), 'success'); }
        catch { toast(t('toast.error'), 'error'); }
        document.body.removeChild(ta);
    }
}

// ==========================================================================
// Keyboard shortcuts
// ==========================================================================
function bindKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
        // Don't capture while typing in inputs/textareas
        const tag = (e.target.tagName || '').toLowerCase();
        const inField = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
        // Allow Esc inside fields (close panels)
        if (e.key === 'Escape') {
            if (DOM.browseModal && DOM.browseModal.classList.contains('open')) { closeBrowse(); return; }
            if (DOM.glossaryModal && DOM.glossaryModal.classList.contains('open')) { closeGlossary(); return; }
            if (DOM.shortcutsModal && DOM.shortcutsModal.classList.contains('open')) { closeShortcuts(); return; }
            if (assistantOpen) { closeAssistant(); return; }
            hideAutocomplete();
            return;
        }
        if (inField) return;
        if (e.key === '/') { e.preventDefault(); DOM.geneSearch && DOM.geneSearch.focus(); }
        else if (e.key === 'b' || e.key === 'B') openBrowse();
        else if (e.key === 'a' || e.key === 'A') assistantOpen ? closeAssistant() : openAssistant();
        else if (e.key === 'm' || e.key === 'M') setMode(currentMode === 'expert' ? 'patient' : 'expert');
        else if (e.key === 'g' || e.key === 'G') openGlossary();
        else if (e.key === '?') openShortcuts();
    });
}

// ==========================================================================
// Shortcuts modal
// ==========================================================================
function openShortcuts() {
    if (!DOM.shortcutsModal) return;
    DOM.shortcutsModal.classList.add('open');
    DOM.shortcutsModal.setAttribute('aria-hidden', 'false');
}
function closeShortcuts() {
    if (!DOM.shortcutsModal) return;
    DOM.shortcutsModal.classList.remove('open');
    DOM.shortcutsModal.setAttribute('aria-hidden', 'true');
}

// ==========================================================================
// Onboarding hint (first-visit only)
// ==========================================================================
function maybeShowOnboarding() {
    if (localStorage.getItem('aura_onboarded') === '1') return;
    if (!DOM.onboarding) return;
    setTimeout(() => DOM.onboarding.classList.add('visible'), 1500);
}
function dismissOnboarding() {
    if (DOM.onboarding) DOM.onboarding.classList.remove('visible');
    localStorage.setItem('aura_onboarded', '1');
}

// ==========================================================================
// Init
// ==========================================================================
function initApp() {
    cacheDOM();
    setMode(currentMode);
    applyI18n();
    populateBrowseFilters();
    renderStatsPanel();

    // Lazy-load jsPDF in the background so first PDF click is instant
    loadJsPDF().catch(e => console.warn('jsPDF lazy-load failed:', e));

    // Language picker
    document.querySelectorAll('.lang-option').forEach(opt => {
        opt.classList.toggle('active', opt.getAttribute('data-lang') === currentLang);
        opt.addEventListener('click', () => setLang(opt.getAttribute('data-lang')));
    });
    if (DOM.langMore) {
        DOM.langMore.addEventListener('click', () => {
            loadGoogleTranslate();
            DOM.langMore.classList.add('loaded');
            // Reveal widget host
            if (DOM.google_translate_element) DOM.google_translate_element.classList.add('visible');
        });
    }

    // Browse modal
    if (DOM.browseOpenBtn) DOM.browseOpenBtn.addEventListener('click', openBrowse);
    if (DOM.browseClose) DOM.browseClose.addEventListener('click', closeBrowse);
    if (DOM.browseModal) {
        DOM.browseModal.addEventListener('click', (e) => {
            if (e.target === DOM.browseModal) closeBrowse();
        });
    }
    const wireFilter = (el, key) => {
        if (!el) return;
        el.addEventListener('change', () => { browseState[key] = el.value; renderBrowse(); });
    };
    wireFilter(DOM.browseFilterSystem, 'system');
    wireFilter(DOM.browseFilterTreatment, 'treatment');
    wireFilter(DOM.browseFilterOnset, 'onset');
    wireFilter(DOM.browseFilterInherit, 'inherit');
    wireFilter(DOM.browseSort, 'sort');
    if (DOM.browseSearch) {
        const bDebTimer = { t: null };
        const debBrowseSearch = debounce((v) => { browseState.search = v; renderBrowse(); }, 150, bDebTimer);
        DOM.browseSearch.addEventListener('input', (e) => debBrowseSearch(e.target.value));
    }
    if (DOM.browseClear) {
        DOM.browseClear.addEventListener('click', () => {
            browseState = { search: '', system: '', treatment: '', onset: '', inherit: '', sort: 'featured' };
            if (DOM.browseSearch) DOM.browseSearch.value = '';
            if (DOM.browseFilterSystem) DOM.browseFilterSystem.value = '';
            if (DOM.browseFilterTreatment) DOM.browseFilterTreatment.value = '';
            if (DOM.browseFilterOnset) DOM.browseFilterOnset.value = '';
            if (DOM.browseFilterInherit) DOM.browseFilterInherit.value = '';
            if (DOM.browseSort) DOM.browseSort.value = 'featured';
            renderBrowse();
        });
    }
    // Escape closes the browse modal
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && DOM.browseModal && DOM.browseModal.classList.contains('open')) {
            closeBrowse();
        }
    });

    // Search
    DOM.searchBtn.addEventListener('click', () => performAnalysis(DOM.geneSearch.value));
    DOM.geneSearch.addEventListener('keydown', (e) => {
        const open = !DOM.autocompleteSuggestions.classList.contains('hidden');
        if (e.key === 'ArrowDown' && open) { e.preventDefault(); navigateSuggestions('down'); }
        else if (e.key === 'ArrowUp' && open) { e.preventDefault(); navigateSuggestions('up'); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && !selectHighlightedSuggestion()) performAnalysis(DOM.geneSearch.value);
            else if (!open) performAnalysis(DOM.geneSearch.value);
        } else if (e.key === 'Escape') hideAutocomplete();
    });
    const debTimer = { t: null };
    const debouncedSearch = debounce((v) => fetchGeneSuggestions(v), 200, debTimer);
    DOM.geneSearch.addEventListener('input', (e) => debouncedSearch(e.target.value));
    document.addEventListener('click', (e) => {
        if (!DOM.geneSearch.contains(e.target) && !DOM.autocompleteSuggestions.contains(e.target)) hideAutocomplete();
    });

    // Quick chips
    DOM.quickChips.forEach(chip => {
        chip.addEventListener('click', () => {
            DOM.quickChips.forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            const g = chip.getAttribute('data-gene');
            DOM.geneSearch.value = g;
            clearHighlight();
            performAnalysis(g);
        });
    });

    // 3D
    initViewerControls();

    // HUD
    if (DOM.hudClearBtn) DOM.hudClearBtn.addEventListener('click', (e) => { e.stopPropagation(); clearHighlight(); });

    // Mode segmented buttons — both segments switch mode based on their data-mode attribute
    document.querySelectorAll('.mode-segment').forEach(seg => {
        seg.addEventListener('click', () => setMode(seg.getAttribute('data-mode')));
    });

    // Patient-mode tab switching
    document.querySelectorAll('.patient-tab').forEach(btn => {
        btn.addEventListener('click', () => activatePatientTab(btn.getAttribute('data-tab')));
    });

    // Patient-summary "read aloud"
    if (DOM.patientSpeakBtn) {
        DOM.patientSpeakBtn.addEventListener('click', () => {
            if (!currentGeneData?.patientStory) return;
            const txt = `${currentGeneData.patientStory.short}. ${currentGeneData.patientStory.metaphor}`;
            speakText(txt);
        });
    }

    // PDF export
    if (DOM.pdfExportBtn) DOM.pdfExportBtn.addEventListener('click', generateDoctorDossier);

    // Symptom search
    if (DOM.symptomSearchInput) {
        const symDebTimer = { t: null };
        const debouncedSym = debounce((v) => searchHPO(v), 300, symDebTimer);
        DOM.symptomSearchInput.addEventListener('input', (e) => debouncedSym(e.target.value));
    }

    // Assistant
    if (DOM.assistantFab) DOM.assistantFab.addEventListener('click', () => assistantOpen ? closeAssistant() : openAssistant());
    if (DOM.assistantClose) DOM.assistantClose.addEventListener('click', closeAssistant);
    if (DOM.assistantSend) DOM.assistantSend.addEventListener('click', () => {
        const v = DOM.assistantInput.value;
        DOM.assistantInput.value = '';
        processAssistantMessage(v);
    });
    if (DOM.assistantInput) DOM.assistantInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const v = DOM.assistantInput.value;
            DOM.assistantInput.value = '';
            processAssistantMessage(v);
        }
    });
    if (DOM.assistantMic) DOM.assistantMic.addEventListener('click', () => voiceListening ? stopVoiceListening() : startVoiceListening());
    if (DOM.assistantSuggestions) {
        DOM.assistantSuggestions.querySelectorAll('.suggestion-pill').forEach(btn => {
            btn.addEventListener('click', () => {
                const prompt = btn.getAttribute(`data-prompt-${currentLang}`) || btn.getAttribute('data-prompt-en') || btn.getAttribute('data-prompt-de') || btn.getAttribute('data-prompt') || btn.textContent;
                processAssistantMessage(prompt);
            });
        });
    }
    showAssistantWelcome();

    // v3.2 Wiring
    loadPersistedState();
    renderFavoritesPanel();
    renderHistoryPanel();
    bindKeyboardShortcuts();

    if (DOM.glossaryOpenBtn) DOM.glossaryOpenBtn.addEventListener('click', openGlossary);
    if (DOM.glossaryClose) DOM.glossaryClose.addEventListener('click', closeGlossary);
    if (DOM.glossaryModal) {
        DOM.glossaryModal.addEventListener('click', (e) => {
            if (e.target === DOM.glossaryModal) closeGlossary();
        });
    }
    if (DOM.glossarySearch) {
        const gTimer = { t: null };
        const debGlossary = debounce((v) => renderGlossary(v), 120, gTimer);
        DOM.glossarySearch.addEventListener('input', (e) => debGlossary(e.target.value));
    }

    if (DOM.shortcutsOpenBtn) DOM.shortcutsOpenBtn.addEventListener('click', openShortcuts);
    if (DOM.shortcutsClose) DOM.shortcutsClose.addEventListener('click', closeShortcuts);
    if (DOM.shortcutsModal) {
        DOM.shortcutsModal.addEventListener('click', (e) => {
            if (e.target === DOM.shortcutsModal) closeShortcuts();
        });
    }

    if (DOM.shareLinkBtn) DOM.shareLinkBtn.addEventListener('click', copyShareLink);

    if (DOM.historyClearBtn) DOM.historyClearBtn.addEventListener('click', () => {
        viewHistory = [];
        persistHistory();
        renderHistoryPanel();
    });

    if (DOM.sidePanelToggle && DOM.sidePanel) {
        DOM.sidePanelToggle.addEventListener('click', () => {
            DOM.sidePanel.classList.toggle('open');
        });
    }

    if (DOM.browseFavoritesToggle) {
        DOM.browseFavoritesToggle.addEventListener('click', () => {
            browseState.favoritesOnly = !browseState.favoritesOnly;
            DOM.browseFavoritesToggle.classList.toggle('active', browseState.favoritesOnly);
            renderBrowse();
        });
    }

    if (DOM.onboardingDismiss) DOM.onboardingDismiss.addEventListener('click', dismissOnboarding);
    maybeShowOnboarding();

    // v3.3: Compare Mode
    if (DOM.compareOpenBtn) DOM.compareOpenBtn.addEventListener('click', openCompare);
    if (DOM.compareClose) DOM.compareClose.addEventListener('click', closeCompare);
    if (DOM.compareModal) {
        DOM.compareModal.addEventListener('click', (e) => { if (e.target === DOM.compareModal) closeCompare(); });
    }

    // v3.3: Reading-level toggle
    setReadingLevel(readingLevel);
    if (DOM.readingToggleSimple) DOM.readingToggleSimple.addEventListener('click', () => setReadingLevel('simple'));
    if (DOM.readingToggleDetailed) DOM.readingToggleDetailed.addEventListener('click', () => setReadingLevel('detailed'));

    // v3.3: Theme toggle
    setTheme(currentTheme);
    if (DOM.themeToggle) DOM.themeToggle.addEventListener('click', () => setTheme(currentTheme === 'dark' ? 'light' : 'dark'));

    // v3.3: 3D screenshot
    if (DOM.btnScreenshot) DOM.btnScreenshot.addEventListener('click', downloadStructureImage);

    // v3.6 Modul 1: Search-bar voice mic
    if (DOM.searchMicBtn) {
        DOM.searchMicBtn.addEventListener('click', () => {
            searchListening ? stopSearchListening() : startSearchListening();
        });
    }

    // v3.6 Modul 2: Ollama settings
    if (DOM.ollamaSettingsToggle) DOM.ollamaSettingsToggle.addEventListener('click', openOllamaSettings);
    document.querySelectorAll('.ollama-close-btn').forEach(btn => btn.addEventListener('click', closeOllamaSettings));
    const ollModal = document.getElementById('ollama-settings-modal');
    if (ollModal) ollModal.addEventListener('click', (e) => { if (e.target === ollModal) closeOllamaSettings(); });
    if (DOM.ollamaEnabledToggle) {
        DOM.ollamaEnabledToggle.checked = ollamaConfig.enabled;
        DOM.ollamaEnabledToggle.addEventListener('change', () => {
            ollamaConfig.enabled = DOM.ollamaEnabledToggle.checked;
            saveOllamaConfig();
            toast(ollamaConfig.enabled
                ? (currentLang === 'de' ? 'Lokale KI aktiviert' : 'Local AI enabled')
                : (currentLang === 'de' ? 'Lokale KI deaktiviert' : 'Local AI disabled'),
                'success');
        });
    }
    if (DOM.ollamaUrlInput) {
        DOM.ollamaUrlInput.value = ollamaConfig.url;
        DOM.ollamaUrlInput.addEventListener('change', () => {
            ollamaConfig.url = DOM.ollamaUrlInput.value.trim() || 'http://localhost:11434';
            saveOllamaConfig();
        });
    }
    if (DOM.ollamaTestBtn) DOM.ollamaTestBtn.addEventListener('click', testOllamaConnection);
    setOllamaStatus('off', currentLang === 'de' ? 'Noch nicht getestet' : 'Not tested');

    // v3.6 Modul 3: STL Export
    if (DOM.stlExportBtn) DOM.stlExportBtn.addEventListener('click', openSTLModal);
    if (DOM.stlOptionsClose) DOM.stlOptionsClose.addEventListener('click', closeSTLModal);
    const stlModal = document.getElementById('stl-options-modal');
    if (stlModal) stlModal.addEventListener('click', (e) => { if (e.target === stlModal) closeSTLModal(); });
    if (DOM.stlRadiusInput) {
        const out = document.getElementById('stl-radius-output');
        DOM.stlRadiusInput.addEventListener('input', () => {
            stlOptions.atomRadius = parseFloat(DOM.stlRadiusInput.value);
            if (out) out.textContent = DOM.stlRadiusInput.value;
        });
    }
    if (DOM.stlBondThicknessInput) {
        const out = document.getElementById('stl-bond-thickness-output');
        DOM.stlBondThicknessInput.addEventListener('input', () => {
            stlOptions.bondThickness = parseFloat(DOM.stlBondThicknessInput.value);
            if (out) out.textContent = DOM.stlBondThicknessInput.value;
        });
    }
    if (DOM.stlIncludeSticks) {
        DOM.stlIncludeSticks.addEventListener('change', () => {
            stlOptions.includeSticks = DOM.stlIncludeSticks.checked;
        });
    }
    if (DOM.stlGenerateBtn) DOM.stlGenerateBtn.addEventListener('click', generateSTL);

    // v3.6 Modul 4: Healthy ↔ Diseased Compare View
    if (DOM.btnCompareView) DOM.btnCompareView.addEventListener('click', toggleCompareView);
    document.querySelectorAll('.compare-seg').forEach(seg => {
        seg.addEventListener('click', () => setCompareView(seg.getAttribute('data-cmp')));
    });
    if (DOM.compareAutoflipBtn) DOM.compareAutoflipBtn.addEventListener('click', toggleAutoFlip);

    // v3.8: Story-Modus
    if (DOM.storyModeBtn) DOM.storyModeBtn.addEventListener('click', openStory);
    if (DOM.storyPrevBtn) DOM.storyPrevBtn.addEventListener('click', storyPrev);
    if (DOM.storyNextBtn) DOM.storyNextBtn.addEventListener('click', storyNext);
    if (DOM.storyPlaypauseBtn) DOM.storyPlaypauseBtn.addEventListener('click', toggleStoryAutoPlay);
    if (DOM.storyNarrateBtn) {
        DOM.storyNarrateBtn.addEventListener('click', toggleStoryNarrate);
        DOM.storyNarrateBtn.classList.toggle('active', storyState.narrate);
    }
    if (DOM.storyCloseBtn) DOM.storyCloseBtn.addEventListener('click', closeStory);
    // Keyboard shortcuts inside story
    document.addEventListener('keydown', (e) => {
        if (!DOM.storyOverlay || DOM.storyOverlay.classList.contains('hidden')) return;
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        if (e.key === 'ArrowRight') { e.preventDefault(); storyNext(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); storyPrev(); }
        else if (e.key === ' ') { e.preventDefault(); toggleStoryAutoPlay(); }
        else if (e.key === 'v' || e.key === 'V') { e.preventDefault(); toggleStoryNarrate(); }
        else if (e.key === 'Escape') { e.preventDefault(); closeStory(); }
    });

    // v3.9: Refresh buttons (live-data freshness)
    document.querySelectorAll('.refresh-btn[data-refresh]').forEach(btn => {
        btn.addEventListener('click', () => refreshSection(btn.getAttribute('data-refresh')));
    });

    // v3.9: Contact modal
    const openContact = () => {
        const m = document.getElementById('contact-modal');
        if (m) { m.classList.add('open'); m.setAttribute('aria-hidden', 'false'); }
    };
    const closeContact = () => {
        const m = document.getElementById('contact-modal');
        if (m) { m.classList.remove('open'); m.setAttribute('aria-hidden', 'true'); }
    };
    const contactBtn = document.getElementById('contact-open-btn');
    if (contactBtn) contactBtn.addEventListener('click', openContact);
    const cpContactBtn = document.getElementById('cross-promo-contact-btn');
    if (cpContactBtn) cpContactBtn.addEventListener('click', openContact);
    const contactClose = document.getElementById('contact-close');
    if (contactClose) contactClose.addEventListener('click', closeContact);
    const contactModal = document.getElementById('contact-modal');
    if (contactModal) contactModal.addEventListener('click', (e) => { if (e.target === contactModal) closeContact(); });
    document.querySelectorAll('.contact-cta[data-subject]').forEach(a => {
        a.addEventListener('click', (e) => {
            e.preventDefault();
            const subj = a.getAttribute('data-subject');
            const gene = currentGeneData?.geneSymbol || '';
            const disease = currentGeneData?.diseaseName || '';
            const tpl = {
                bug: {
                    subject: `[AuraGene] Datenfehler bei ${gene || 'Unbekannt'}`,
                    body: `Hallo,\n\nIch habe einen Datenfehler entdeckt:\n\nGen: ${gene}\nKrankheit: ${disease}\nURL: ${window.location.href}\n\nBeschreibung des Fehlers:\n\n\n— Vielen Dank!`
                },
                feature: {
                    subject: '[AuraGene] Feature-Wunsch',
                    body: `Hallo,\n\nIch hätte folgenden Feature-Wunsch:\n\n\n\n(Aktuelles Gen / Seite: ${gene || ''} — ${window.location.href})`
                },
                general: {
                    subject: '[AuraGene] Hallo',
                    body: `Hallo,\n\n`
                }
            };
            const t = tpl[subj] || tpl.general;
            const url = `mailto:info@ka77.de?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
            window.location.href = url;
        });
    });

    // v3.7: Tools dropdown
    if (DOM.actionToolsToggle && DOM.actionToolsPopover) {
        DOM.actionToolsToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = DOM.actionToolsPopover.classList.toggle('hidden');
            DOM.actionToolsToggle.setAttribute('aria-expanded', !open);
            DOM.actionToolsToggle.classList.toggle('open', !open);
        });
        // Close any tool-item click also closes the popover
        DOM.actionToolsPopover.querySelectorAll('.action-tool-item').forEach(item => {
            item.addEventListener('click', () => {
                setTimeout(() => {
                    DOM.actionToolsPopover.classList.add('hidden');
                    DOM.actionToolsToggle.setAttribute('aria-expanded', 'false');
                    DOM.actionToolsToggle.classList.remove('open');
                }, 50);
            });
        });
        // Close when clicking outside
        document.addEventListener('click', (e) => {
            if (DOM.actionToolsPopover.classList.contains('hidden')) return;
            if (DOM.actionToolsToggle.contains(e.target) || DOM.actionToolsPopover.contains(e.target)) return;
            DOM.actionToolsPopover.classList.add('hidden');
            DOM.actionToolsToggle.setAttribute('aria-expanded', 'false');
            DOM.actionToolsToggle.classList.remove('open');
        });
    }

    // v3.3 / v3.6: Immersive 3D-Viewer mode (custom — NOT browser fullscreen)
    if (DOM.btnFullscreen) DOM.btnFullscreen.addEventListener('click', toggleImmersiveMode);

    // URL deep-link or default
    if (!parseHashAndLoad()) {
        performAnalysis("CFTR");
    }
    // Listen for hash changes (user navigates back/forward)
    window.addEventListener('hashchange', () => parseHashAndLoad());
}

window.addEventListener('DOMContentLoaded', initApp);
